// src/config/defaults.ts
var DEFAULT_VAD_CONFIG = {
  energy_threshold_db: -45,
  min_frames_above: 3,
  fft_size: 2048,
  smoothing: 0.8
};
var DEFAULT_CONFIG = {
  helix: {
    thread_count: 3,
    initial_seed_count: 150,
    distribution_method: "card_deal",
    content_injection_max_threads: 2
  },
  repetition: {
    initial_reps: 7,
    min_reps: 3,
    max_reps: 15,
    fibonacci_sequence: [1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89],
    core_sentence_count: 30,
    core_refresh_hours: 5,
    adaptive_reps_enabled: true
  },
  cycle: {
    pause_duration_ms: 3e3,
    min_pause_ms: 1e3,
    max_pause_ms: 1e4,
    transition_gap_ms: 500,
    pause_adapts_to_phrase_length: true
  },
  spike: {
    rolling_window_size: 10,
    threshold_percent: 150,
    response_strategy: "alternate",
    alternate_sequence: ["repeat", "breakdown"],
    cooldown_items: 3,
    use_stddev_detection: true,
    stddev_threshold: 2,
    pause_extension_enabled: true,
    pause_extension_factor: 0.3,
    pause_extension_duration: 3
  },
  lego_introduction: {
    eternal_phrase_count: 5,
    molecular_breakdown_enabled: true,
    debut_phrases_repeat: false
  },
  content_injection: {
    enabled: true,
    max_threads_for_injection: 2,
    subject_categories: ["medical", "hospitality", "legal", "business", "travel"]
  },
  offline: {
    auto_cache_minutes: 30,
    max_download_hours: 8,
    prefetch_item_count: 50
  },
  session: {
    target_duration_minutes: 15,
    warmup_items: 5,
    question_min_gap_minutes: 5
  },
  features: {
    triple_helix_enabled: true,
    adaptive_difficulty_enabled: true,
    spike_detection_enabled: true,
    in_flow_questions_enabled: true,
    encouragements_enabled: true,
    pace_modes_available: true,
    listening_mode_available: true,
    vad_enabled: false
    // Disabled by default - requires microphone permission
  },
  vad: DEFAULT_VAD_CONFIG
};

// src/config/resolver.ts
var ConfigResolver = class {
  systemDefaults;
  courseOverrides = {};
  learnerOverrides = {};
  constructor(systemDefaults = DEFAULT_CONFIG) {
    this.systemDefaults = systemDefaults;
  }
  /**
   * Set course or school level overrides
   */
  setCourseOverrides(overrides) {
    this.courseOverrides = overrides;
  }
  /**
   * Set learner-specific adaptive overrides
   */
  setLearnerOverrides(overrides) {
    this.learnerOverrides = overrides;
  }
  /**
   * Update a single learner parameter (for real-time adaptation)
   */
  updateLearnerParam(section, key, value) {
    if (!this.learnerOverrides[section]) {
      this.learnerOverrides[section] = {};
    }
    this.learnerOverrides[section][key] = value;
  }
  /**
   * Resolve the final configuration by merging all tiers
   * Learner > Course > System Defaults
   */
  resolve() {
    return this.deepMerge(
      this.systemDefaults,
      this.courseOverrides,
      this.learnerOverrides
    );
  }
  /**
   * Get a specific config section
   */
  get(section) {
    return this.resolve()[section];
  }
  /**
   * Get a specific parameter value
   */
  getParam(section, key) {
    return this.resolve()[section][key];
  }
  /**
   * Export current overrides for persistence
   */
  exportOverrides() {
    return {
      course: this.courseOverrides,
      learner: this.learnerOverrides
    };
  }
  /**
   * Import overrides from persistence
   */
  importOverrides(overrides) {
    if (overrides.course) {
      this.courseOverrides = overrides.course;
    }
    if (overrides.learner) {
      this.learnerOverrides = overrides.learner;
    }
  }
  /**
   * Reset learner overrides (start fresh)
   */
  resetLearnerOverrides() {
    this.learnerOverrides = {};
  }
  /**
   * Deep merge utility - later objects override earlier ones
   */
  deepMerge(base, ...overrides) {
    const result = JSON.parse(JSON.stringify(base));
    for (const override of overrides) {
      for (const key of Object.keys(override)) {
        const overrideValue = override[key];
        if (overrideValue !== void 0) {
          const resultAny = result;
          const baseSection = resultAny[key] ?? {};
          const definedParams = Object.fromEntries(
            Object.entries(overrideValue).filter(([, value]) => value !== void 0)
          );
          resultAny[key] = { ...baseSection, ...definedParams };
        }
      }
    }
    return result;
  }
};
function createConfigResolver(courseOverrides, learnerOverrides) {
  const resolver = new ConfigResolver();
  if (courseOverrides) {
    resolver.setCourseOverrides(courseOverrides);
  }
  if (learnerOverrides) {
    resolver.setLearnerOverrides(learnerOverrides);
  }
  return resolver;
}

// src/data/types.ts
function createDefaultLegoProgress(legoId, courseId, threadId) {
  return {
    lego_id: legoId,
    course_id: courseId,
    thread_id: threadId,
    fibonacci_position: 0,
    skip_number: 1,
    reps_completed: 0,
    is_retired: false,
    last_practiced_at: null,
    // ROUND tracking
    introduction_played: false,
    introduction_index: 0,
    introduction_complete: false,
    // Eternal selection
    eternal_urn: [],
    last_eternal_phrase_id: null
  };
}

// src/data/database-types.ts
var LANGUAGE_META = {
  eng: { name: "English", flag: "\u{1F1EC}\u{1F1E7}" },
  spa: { name: "Spanish", flag: "\u{1F1EA}\u{1F1F8}" },
  ita: { name: "Italian", flag: "\u{1F1EE}\u{1F1F9}" },
  fra: { name: "French", flag: "\u{1F1EB}\u{1F1F7}" },
  deu: { name: "German", flag: "\u{1F1E9}\u{1F1EA}" },
  por: { name: "Portuguese", flag: "\u{1F1F5}\u{1F1F9}" },
  cym: { name: "Welsh", flag: "\u{1F3F4}\u{E0067}\u{E0062}\u{E0077}\u{E006C}\u{E0073}\u{E007F}" },
  jpn: { name: "Japanese", flag: "\u{1F1EF}\u{1F1F5}" },
  zho: { name: "Chinese", flag: "\u{1F1E8}\u{1F1F3}" },
  kor: { name: "Korean", flag: "\u{1F1F0}\u{1F1F7}" },
  ara: { name: "Arabic", flag: "\u{1F1F8}\u{1F1E6}" },
  nld: { name: "Dutch", flag: "\u{1F1F3}\u{1F1F1}" },
  rus: { name: "Russian", flag: "\u{1F1F7}\u{1F1FA}" },
  pol: { name: "Polish", flag: "\u{1F1F5}\u{1F1F1}" },
  swe: { name: "Swedish", flag: "\u{1F1F8}\u{1F1EA}" },
  nor: { name: "Norwegian", flag: "\u{1F1F3}\u{1F1F4}" },
  dan: { name: "Danish", flag: "\u{1F1E9}\u{1F1F0}" },
  fin: { name: "Finnish", flag: "\u{1F1EB}\u{1F1EE}" },
  tur: { name: "Turkish", flag: "\u{1F1F9}\u{1F1F7}" },
  hin: { name: "Hindi", flag: "\u{1F1EE}\u{1F1F3}" }
};
function getLanguageMeta(code) {
  return LANGUAGE_META[code] || { name: code.toUpperCase(), flag: "\u{1F310}" };
}
var AUDIO_BASE_URL = "https://ssi-audio-stage.s3.eu-west-1.amazonaws.com/mastered";
function getAudioUrl(uuid) {
  return `${AUDIO_BASE_URL}/${uuid}.mp3`;
}
function createAudioRef(uuid, duration_ms) {
  if (!uuid) {
    return {
      id: "silence",
      url: "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=",
      duration_ms: duration_ms || 1e3
    };
  }
  return {
    id: uuid,
    url: getAudioUrl(uuid),
    duration_ms
  };
}

// src/engine/types.ts
var CyclePhase = /* @__PURE__ */ ((CyclePhase2) => {
  CyclePhase2["IDLE"] = "IDLE";
  CyclePhase2["PROMPT"] = "PROMPT";
  CyclePhase2["PAUSE"] = "PAUSE";
  CyclePhase2["VOICE_1"] = "VOICE_1";
  CyclePhase2["VOICE_2"] = "VOICE_2";
  CyclePhase2["TRANSITION"] = "TRANSITION";
  return CyclePhase2;
})(CyclePhase || {});
var TEXT_VISIBILITY = {
  ["IDLE" /* IDLE */]: { known: false, target: false },
  ["PROMPT" /* PROMPT */]: { known: true, target: false },
  ["PAUSE" /* PAUSE */]: { known: true, target: false },
  ["VOICE_1" /* VOICE_1 */]: { known: true, target: false },
  ["VOICE_2" /* VOICE_2 */]: { known: true, target: true },
  ["TRANSITION" /* TRANSITION */]: { known: false, target: false }
};

// src/learning/types.ts
var DEFAULT_TIMING_THRESHOLDS = {
  quick_response_ms: 2e3,
  slow_response_ms: 5e3,
  good_prosody_delta_ms: 500,
  poor_prosody_delta_ms: 1500
};
var DEFAULT_CONTINUOUS_ADAPTATION_CONFIG = {
  responsiveness: 0.3,
  minPauseMultiplier: 0.8,
  maxPauseMultiplier: 1.4,
  significantZScore: 1.5,
  latencyWeight: 0.7
};
var DEFAULT_CALIBRATION_CONFIG = {
  minItems: 20,
  maxItems: 50,
  minStdDev: 5,
  autoComplete: true
};

// src/learning/SpacedRepetitionQueue.ts
var SpacedRepetitionQueue = class {
  config;
  queue = /* @__PURE__ */ new Map();
  fibonacciSequence;
  constructor(config) {
    this.config = config;
    this.fibonacciSequence = config.fibonacci_sequence;
  }
  /**
   * Add a new LEGO to the queue (first introduction)
   */
  addNew(lego, threadId, courseId) {
    const progress = {
      lego_id: lego.id,
      course_id: courseId,
      thread_id: threadId,
      fibonacci_position: 0,
      // Start at position 0
      skip_number: 0,
      // Practice immediately
      reps_completed: 0,
      is_retired: false,
      last_practiced_at: null,
      // ROUND tracking
      introduction_played: false,
      introduction_index: 0,
      introduction_complete: false,
      // Eternal selection
      eternal_urn: [],
      last_eternal_phrase_id: null
    };
    this.queue.set(lego.id, {
      lego,
      progress,
      priority: this.calculatePriority(progress)
    });
    return progress;
  }
  /**
   * Update progress for a LEGO in the queue
   */
  updateProgress(legoId, progress) {
    const entry = this.queue.get(legoId);
    if (entry) {
      entry.progress = progress;
      entry.priority = this.calculatePriority(progress);
    }
  }
  /**
   * Load existing progress into the queue
   */
  loadProgress(lego, progress) {
    this.queue.set(lego.id, {
      lego,
      progress,
      priority: this.calculatePriority(progress)
    });
  }
  /**
   * Get the next LEGO to practice (lowest priority score)
   */
  getNext() {
    let best = null;
    let bestPriority = Infinity;
    for (const queued of this.queue.values()) {
      if (queued.progress.is_retired) continue;
      if (queued.progress.skip_number > 0) continue;
      if (queued.priority < bestPriority) {
        best = queued;
        bestPriority = queued.priority;
      }
    }
    return best;
  }
  /**
   * Get all LEGOs ready for practice (skip_number = 0)
   */
  getReady() {
    return Array.from(this.queue.values()).filter((q) => !q.progress.is_retired && q.progress.skip_number <= 0).sort((a, b) => a.priority - b.priority);
  }
  /**
   * Record a practice attempt for a LEGO
   */
  recordPractice(legoId, wasSuccessful, wasSpike) {
    const queued = this.queue.get(legoId);
    if (!queued) return null;
    const progress = queued.progress;
    progress.last_practiced_at = /* @__PURE__ */ new Date();
    if (wasSpike) {
      progress.fibonacci_position = Math.max(0, progress.fibonacci_position - 1);
    } else if (wasSuccessful) {
      progress.reps_completed++;
      if (progress.reps_completed >= this.config.initial_reps) {
        progress.fibonacci_position = Math.min(
          progress.fibonacci_position + 1,
          this.fibonacciSequence.length - 1
        );
      }
      if (progress.fibonacci_position >= this.fibonacciSequence.length - 1) {
        progress.is_retired = true;
      }
    }
    progress.skip_number = this.fibonacciSequence[progress.fibonacci_position] || 0;
    queued.priority = this.calculatePriority(progress);
    return progress;
  }
  /**
   * Decrement skip numbers for all LEGOs (called after each practice)
   */
  decrementSkipNumbers() {
    for (const queued of this.queue.values()) {
      if (queued.progress.skip_number > 0) {
        queued.progress.skip_number--;
        queued.priority = this.calculatePriority(queued.progress);
      }
    }
  }
  /**
   * Get progress for a specific LEGO
   */
  getProgress(legoId) {
    return this.queue.get(legoId)?.progress || null;
  }
  /**
   * Get all progress (for persistence)
   */
  getAllProgress() {
    return Array.from(this.queue.values()).map((q) => q.progress);
  }
  /**
   * Get queue statistics
   */
  getStats() {
    const all = Array.from(this.queue.values());
    const active = all.filter((q) => !q.progress.is_retired);
    const ready = all.filter((q) => !q.progress.is_retired && q.progress.skip_number <= 0);
    const avgPosition = active.length > 0 ? active.reduce((sum, q) => sum + q.progress.fibonacci_position, 0) / active.length : 0;
    return {
      total: all.length,
      active: active.length,
      retired: all.length - active.length,
      ready: ready.length,
      avgPosition
    };
  }
  /**
   * Clear the queue
   */
  clear() {
    this.queue.clear();
  }
  /**
   * Update configuration
   */
  updateConfig(config) {
    this.config = { ...this.config, ...config };
    if (config.fibonacci_sequence) {
      this.fibonacciSequence = config.fibonacci_sequence;
    }
    for (const queued of this.queue.values()) {
      queued.priority = this.calculatePriority(queued.progress);
    }
  }
  // ============================================
  // PRIVATE METHODS
  // ============================================
  /**
   * Calculate priority score for a LEGO
   * Lower score = higher priority (practice sooner)
   */
  calculatePriority(progress) {
    if (progress.is_retired) return Infinity;
    let priority = progress.skip_number;
    if (progress.reps_completed < this.config.initial_reps) {
      priority -= 10;
    }
    priority += progress.fibonacci_position * 0.5;
    priority += Math.random() * 0.1;
    return priority;
  }
};
function createSpacedRepetitionQueue(config) {
  return new SpacedRepetitionQueue(config);
}

// src/learning/PhraseSelector.ts
var DEFAULT_CONFIG2 = {
  eternalSelectionMode: "random_urn",
  minEternalsBeforeRepeat: 3
};
function selectDebutPhrase(basket, progress) {
  if (progress.introduction_index === 0 && basket.debut) {
    return basket.debut;
  }
  const debutIndex = progress.introduction_index - 1;
  if (debutIndex < basket.debut_phrases.length) {
    return basket.debut_phrases[debutIndex];
  }
  return null;
}
function initializeEternalUrn(basket, _progress) {
  const phraseIds = basket.eternal_phrases.map((p) => p.id);
  const shuffled = [...phraseIds];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
function selectEternalPhrase(basket, progress, config = DEFAULT_CONFIG2) {
  if (basket.eternal_phrases.length === 0) {
    return { phrase: null, updatedUrn: [] };
  }
  switch (config.eternalSelectionMode) {
    case "random_urn":
      return selectFromUrn(basket, progress);
    case "sequential":
      return selectSequential(basket, progress);
    case "max_distance":
      return selectMaxDistance(basket, progress);
    default:
      return selectFromUrn(basket, progress);
  }
}
function selectFromUrn(basket, progress) {
  let urn = progress.eternal_urn;
  if (!urn || urn.length === 0) {
    urn = initializeEternalUrn(basket);
  }
  const phraseId = urn[0];
  const updatedUrn = urn.slice(1);
  const phrase = basket.eternal_phrases.find((p) => p.id === phraseId) || null;
  return { phrase, updatedUrn };
}
function selectSequential(basket, progress) {
  let urn = progress.eternal_urn;
  if (!urn || urn.length === 0) {
    urn = basket.eternal_phrases.map((p) => p.id);
  }
  const phraseId = urn[0];
  const updatedUrn = urn.slice(1);
  const phrase = basket.eternal_phrases.find((p) => p.id === phraseId) || null;
  return { phrase, updatedUrn };
}
function selectMaxDistance(basket, progress) {
  const lastUsed = progress.last_eternal_phrase_id;
  if (!lastUsed || basket.eternal_phrases.length <= 1) {
    const phrase2 = basket.eternal_phrases[0] || null;
    return { phrase: phrase2, updatedUrn: progress.eternal_urn };
  }
  const lastIndex = basket.eternal_phrases.findIndex((p) => p.id === lastUsed);
  const halfwayIndex = (lastIndex + Math.floor(basket.eternal_phrases.length / 2)) % basket.eternal_phrases.length;
  const phrase = basket.eternal_phrases[halfwayIndex];
  return { phrase, updatedUrn: progress.eternal_urn };
}
function getDebutPhraseCount(basket) {
  return basket.debut_phrases.length;
}

// src/learning/RoundEngine.ts
var MAX_BUILD_PHRASES = 7;
var MAX_SPACED_REP_PHRASES = 12;
var CONSOLIDATE_COUNT = 2;
var DEFAULT_CONFIG3 = {
  spacedRepInterleaveCount: MAX_SPACED_REP_PHRASES,
  consolidationCount: CONSOLIDATE_COUNT,
  maxBuildPhrases: MAX_BUILD_PHRASES,
  phraseSelector: {
    eternalSelectionMode: "random_urn",
    minEternalsBeforeRepeat: 3
  }
};
function createRoundState(legoId, config = DEFAULT_CONFIG3) {
  return {
    lego_id: legoId,
    current_phase: "intro_audio",
    phase_index: 0,
    spaced_rep_target: config.spacedRepInterleaveCount,
    spaced_rep_completed: 0,
    consolidation_remaining: config.consolidationCount
  };
}
function getNextRoundItem(lego, seed, basket, progress, roundState, config = DEFAULT_CONFIG3) {
  const threadId = progress.thread_id;
  switch (roundState.current_phase) {
    case "intro_audio":
      return handleIntroAudio(lego, seed, basket, progress, roundState, threadId, config);
    case "components":
      return handleComponents(lego, seed, basket, progress, roundState, threadId, config);
    case "debut_lego":
      return handleDebutLego(lego, seed, basket, progress, roundState, threadId, config);
    case "debut_phrases":
      return handleDebutPhrases(lego, seed, basket, progress, roundState, threadId, config);
    case "spaced_rep":
      return handleSpacedRep(lego, seed, basket, progress, roundState, threadId, config);
    case "consolidation":
      return handleConsolidation(lego, seed, basket, progress, roundState, threadId, config);
    default:
      return createCompleteResult(progress, roundState);
  }
}
function handleIntroAudio(lego, seed, basket, progress, roundState, threadId, config) {
  if (basket.introduction_audio && !progress.introduction_played) {
    const introPhrase = {
      id: `${lego.id}_intro`,
      phraseType: "debut",
      // Technically it's an intro, but this works
      phrase: lego.lego,
      audioRefs: lego.audioRefs,
      // We'll use introduction_audio separately
      wordCount: 0,
      containsLegos: [lego.id]
    };
    return {
      item: {
        lego,
        phrase: introPhrase,
        seed,
        thread_id: threadId,
        mode: "introduction"
      },
      updatedProgress: {
        ...progress,
        introduction_played: true
      },
      updatedRoundState: advancePhase(roundState),
      roundComplete: false,
      needsSpacedRepItem: false,
      isIntroductionAudio: true
    };
  }
  return getNextRoundItem(
    lego,
    seed,
    basket,
    { ...progress, introduction_played: true },
    advancePhase(roundState),
    config
  );
}
function handleComponents(lego, seed, basket, progress, roundState, _threadId, config) {
  return getNextRoundItem(
    lego,
    seed,
    basket,
    progress,
    advancePhase(roundState),
    config
  );
}
function handleDebutLego(lego, seed, basket, progress, roundState, threadId, config) {
  if (basket.debut) {
    return {
      item: {
        lego,
        phrase: basket.debut,
        seed,
        thread_id: threadId,
        mode: "introduction"
      },
      updatedProgress: {
        ...progress,
        introduction_index: 1
        // Move past debut
      },
      updatedRoundState: advancePhase(roundState),
      roundComplete: false,
      needsSpacedRepItem: false,
      isIntroductionAudio: false
    };
  }
  return getNextRoundItem(
    lego,
    seed,
    basket,
    progress,
    advancePhase(roundState),
    config
  );
}
function handleDebutPhrases(lego, seed, basket, progress, roundState, threadId, config = DEFAULT_CONFIG3) {
  const buildPhraseCount = progress.introduction_index - 1;
  if (buildPhraseCount >= config.maxBuildPhrases) {
    return getNextRoundItem(
      lego,
      seed,
      basket,
      progress,
      advancePhase(roundState),
      config
    );
  }
  const phrase = selectDebutPhrase(basket, progress);
  if (phrase) {
    return {
      item: {
        lego,
        phrase,
        seed,
        thread_id: threadId,
        mode: "introduction"
      },
      updatedProgress: {
        ...progress,
        introduction_index: progress.introduction_index + 1
      },
      updatedRoundState: roundState,
      roundComplete: false,
      needsSpacedRepItem: false,
      isIntroductionAudio: false
    };
  }
  return getNextRoundItem(
    lego,
    seed,
    basket,
    progress,
    advancePhase(roundState),
    config
  );
}
function handleSpacedRep(lego, seed, basket, progress, roundState, _threadId, config) {
  if (roundState.spaced_rep_completed < roundState.spaced_rep_target) {
    return {
      item: null,
      // TripleHelix will provide the item
      updatedProgress: progress,
      updatedRoundState: {
        ...roundState,
        spaced_rep_completed: roundState.spaced_rep_completed + 1
      },
      roundComplete: false,
      needsSpacedRepItem: true,
      isIntroductionAudio: false
    };
  }
  return getNextRoundItem(
    lego,
    seed,
    basket,
    progress,
    advancePhase(roundState),
    config
  );
}
function handleConsolidation(lego, seed, basket, progress, roundState, threadId, config) {
  if (roundState.consolidation_remaining > 0 && basket.eternal_phrases.length > 0) {
    const { phrase, updatedUrn } = selectEternalPhrase(basket, progress, config.phraseSelector);
    if (phrase) {
      return {
        item: {
          lego,
          phrase,
          seed,
          thread_id: threadId,
          mode: "practice"
        },
        updatedProgress: {
          ...progress,
          eternal_urn: updatedUrn,
          last_eternal_phrase_id: phrase.id
        },
        updatedRoundState: {
          ...roundState,
          consolidation_remaining: roundState.consolidation_remaining - 1
        },
        roundComplete: false,
        needsSpacedRepItem: false,
        isIntroductionAudio: false
      };
    }
  }
  return createCompleteResult(progress, roundState);
}
function createCompleteResult(progress, roundState) {
  return {
    item: null,
    updatedProgress: {
      ...progress,
      introduction_complete: true
    },
    updatedRoundState: roundState,
    roundComplete: true,
    needsSpacedRepItem: false,
    isIntroductionAudio: false
  };
}
function advancePhase(roundState, _basket, _lego) {
  const phaseOrder = [
    "intro_audio",
    "components",
    "debut_lego",
    "debut_phrases",
    "spaced_rep",
    "consolidation"
  ];
  const currentIndex = phaseOrder.indexOf(roundState.current_phase);
  let nextPhase = phaseOrder[currentIndex + 1];
  if (nextPhase === "components") {
    nextPhase = "debut_lego";
  }
  return {
    ...roundState,
    current_phase: nextPhase || "consolidation",
    phase_index: 0
    // Reset for new phase
  };
}
function needsRound(progress) {
  return !progress.introduction_complete;
}

// src/learning/TripleHelixEngine.ts
var TripleHelixEngine = class {
  helixConfig;
  threads = /* @__PURE__ */ new Map();
  activeThread = 1;
  courseId;
  allSeeds = [];
  // ROUND-based learning state
  baskets = /* @__PURE__ */ new Map();
  roundStates = /* @__PURE__ */ new Map();
  legoProgressMap = /* @__PURE__ */ new Map();
  roundConfig = {
    // REVIEW: 3 spaced rep items per ROUND (provides good interleaving without
    // overwhelming the learner; can be configured higher up to MAX_SPACED_REP_PHRASES=12)
    spacedRepInterleaveCount: 3,
    consolidationCount: 2,
    // CONSOLIDATE: 2 phrases
    maxBuildPhrases: 7,
    // BUILD: up to 7 phrases
    phraseSelector: {
      eternalSelectionMode: "random_urn",
      minEternalsBeforeRepeat: 3
    }
  };
  // Currently active round (if any)
  activeRoundLegoId = null;
  constructor(helixConfig, repetitionConfig, courseId) {
    this.helixConfig = helixConfig;
    this.courseId = courseId;
    for (let i = 1; i <= helixConfig.thread_count; i++) {
      this.threads.set(i, {
        queue: new SpacedRepetitionQueue(repetitionConfig),
        seeds: [],
        currentSeedIndex: 0,
        currentLegoIndex: 0,
        introducedSeeds: /* @__PURE__ */ new Set()
      });
    }
  }
  /**
   * Register a ClassifiedBasket for a LEGO (for ROUND-based phrase selection)
   */
  registerBasket(legoId, basket) {
    this.baskets.set(legoId, basket);
  }
  /**
   * Register multiple baskets at once
   */
  registerBaskets(baskets) {
    for (const basket of baskets) {
      this.baskets.set(basket.lego_id, basket);
    }
  }
  /**
   * Get the currently active round (if any)
   */
  getActiveRound() {
    if (this.activeRoundLegoId) {
      const roundState = this.roundStates.get(this.activeRoundLegoId);
      if (roundState) {
        return { legoId: this.activeRoundLegoId, roundState };
      }
    }
    return null;
  }
  /**
   * Load seeds and distribute them across threads (card-dealing)
   */
  loadSeeds(seeds) {
    this.allSeeds = seeds;
    const threadCount = this.helixConfig.thread_count;
    const seedCount = Math.min(seeds.length, this.helixConfig.initial_seed_count);
    for (let i = 0; i < seedCount; i++) {
      const threadId = i % threadCount + 1;
      const thread = this.threads.get(threadId);
      if (thread) {
        thread.seeds.push(seeds[i]);
      }
    }
  }
  /**
   * Load existing state (from persistence)
   */
  loadState(helixState, seedProgress, legoProgress) {
    this.activeThread = helixState.active_thread;
    for (const sp of seedProgress) {
      const thread = this.threads.get(sp.thread_id);
      if (thread && sp.is_introduced) {
        thread.introducedSeeds.add(sp.seed_id);
      }
    }
    for (const lp of legoProgress) {
      const thread = this.threads.get(lp.thread_id);
      if (thread) {
        const lego = this.findLego(lp.lego_id);
        if (lego) {
          thread.queue.loadProgress(lego, lp);
        }
      }
    }
    for (const [threadId, state] of Object.entries(helixState.threads)) {
      const thread = this.threads.get(parseInt(threadId));
      if (thread && state.currentSeedId) {
        const seedIndex = thread.seeds.findIndex((s) => s.seed_id === state.currentSeedId);
        if (seedIndex >= 0) {
          thread.currentSeedIndex = seedIndex;
          thread.currentLegoIndex = state.currentLegoIndex;
        }
      }
    }
  }
  /**
   * Get the next learning item to practice
   *
   * Enhanced with ROUND-based learning:
   * - If there's an active Round, continue it
   * - If Round needs spaced rep, get it from another thread
   * - Otherwise, normal thread rotation
   */
  getNextItem() {
    if (this.activeRoundLegoId) {
      const result = this.continueActiveRound();
      if (result) return result;
    }
    let item = this.getItemFromThread(this.activeThread);
    if (!item) {
      for (let i = 1; i <= this.helixConfig.thread_count; i++) {
        const threadId = (this.activeThread + i - 1) % this.helixConfig.thread_count + 1;
        item = this.getItemFromThread(threadId);
        if (item) {
          this.activeThread = threadId;
          break;
        }
      }
    }
    return item;
  }
  /**
   * Continue the active Round, handling all phases including spaced rep
   */
  continueActiveRound() {
    const legoId = this.activeRoundLegoId;
    if (!legoId) return null;
    const basket = this.baskets.get(legoId);
    const roundState = this.roundStates.get(legoId);
    const progress = this.legoProgressMap.get(legoId);
    if (!basket || !roundState || !progress) {
      this.activeRoundLegoId = null;
      return null;
    }
    const lego = this.findLego(legoId);
    const seed = this.findSeedForLego(legoId);
    if (!lego || !seed) {
      this.activeRoundLegoId = null;
      return null;
    }
    const result = getNextRoundItem(
      lego,
      seed,
      basket,
      progress,
      roundState,
      this.roundConfig
    );
    this.legoProgressMap.set(legoId, result.updatedProgress);
    this.roundStates.set(legoId, result.updatedRoundState);
    if (result.roundComplete) {
      this.activeRoundLegoId = null;
      const thread = this.findThreadForLego(legoId);
      if (thread) {
        thread.queue.updateProgress(legoId, result.updatedProgress);
      }
      return null;
    }
    if (result.needsSpacedRepItem) {
      const spacedRepItem = this.getSpacedRepItemForRound(progress.thread_id);
      if (spacedRepItem) {
        return spacedRepItem;
      }
      return this.continueActiveRound();
    }
    return result.item;
  }
  /**
   * Get a spaced rep item from a thread OTHER than the active Round's thread
   */
  getSpacedRepItemForRound(excludeThreadId) {
    for (const [threadId, thread] of this.threads) {
      if (threadId === excludeThreadId) continue;
      const readyLego = thread.queue.getNext();
      if (readyLego) {
        const seed = this.findSeedForLego(readyLego.lego.id);
        if (seed) {
          return this.createLearningItemWithBasket(
            readyLego.lego,
            seed,
            threadId,
            "review"
          );
        }
      }
    }
    return null;
  }
  /**
   * Record practice result for a LEGO
   */
  recordPractice(legoId, threadId, wasSuccessful, wasSpike) {
    const thread = this.threads.get(threadId);
    if (!thread) return null;
    const progress = thread.queue.recordPractice(legoId, wasSuccessful, wasSpike);
    thread.queue.decrementSkipNumbers();
    this.rotateThread();
    return progress;
  }
  /**
   * Mark a SEED as fully introduced
   */
  markSeedIntroduced(seedId, threadId) {
    const thread = this.threads.get(threadId);
    if (thread) {
      thread.introducedSeeds.add(seedId);
    }
  }
  /**
   * Get current helix state (for persistence)
   */
  getHelixState() {
    const threads = {};
    for (const [threadId, thread] of this.threads) {
      const currentSeed = thread.seeds[thread.currentSeedIndex];
      threads[threadId] = {
        seedOrder: thread.seeds.map((s) => s.seed_id),
        currentSeedId: currentSeed?.seed_id || null,
        currentLegoIndex: thread.currentLegoIndex
      };
    }
    return {
      active_thread: this.activeThread,
      threads,
      injected_content: {}
    };
  }
  /**
   * Get all LEGO progress (for persistence)
   */
  getAllLegoProgress() {
    const progress = [];
    for (const thread of this.threads.values()) {
      progress.push(...thread.queue.getAllProgress());
    }
    return progress;
  }
  /**
   * Get all SEED progress (for persistence)
   */
  getAllSeedProgress() {
    const progress = [];
    for (const [threadId, thread] of this.threads) {
      for (const seed of thread.seeds) {
        progress.push({
          seed_id: seed.seed_id,
          course_id: this.courseId,
          thread_id: threadId,
          is_introduced: thread.introducedSeeds.has(seed.seed_id),
          introduced_at: thread.introducedSeeds.has(seed.seed_id) ? /* @__PURE__ */ new Date() : null
        });
      }
    }
    return progress;
  }
  /**
   * Get statistics across all threads
   */
  getStats() {
    const threadStats = [];
    let totalIntroduced = 0;
    let totalSeeds = 0;
    for (const [threadId, thread] of this.threads) {
      threadStats.push({
        threadId,
        stats: thread.queue.getStats()
      });
      totalSeeds += thread.seeds.length;
      totalIntroduced += thread.introducedSeeds.size;
    }
    return {
      threads: threadStats,
      activeThread: this.activeThread,
      totalSeeds,
      introducedSeeds: totalIntroduced
    };
  }
  // ============================================
  // PRIVATE METHODS
  // ============================================
  getItemFromThread(threadId) {
    const thread = this.threads.get(threadId);
    if (!thread) return null;
    const readyLego = thread.queue.getNext();
    if (readyLego) {
      const progress = this.legoProgressMap.get(readyLego.lego.id);
      if (progress && needsRound(progress)) {
        return this.startRoundForLego(readyLego.lego.id, threadId);
      }
      const seed = this.findSeedForLego(readyLego.lego.id);
      if (seed) {
        return this.createLearningItemWithBasket(
          readyLego.lego,
          seed,
          threadId,
          "review"
        );
      }
    }
    return this.getNextNewItem(thread, threadId);
  }
  getNextNewItem(thread, threadId) {
    if (thread.currentSeedIndex >= thread.seeds.length) {
      return null;
    }
    const seed = thread.seeds[thread.currentSeedIndex];
    const legos = seed.legos.filter((l) => l.new);
    if (thread.currentLegoIndex >= legos.length) {
      this.markSeedIntroduced(seed.seed_id, threadId);
      thread.currentSeedIndex++;
      thread.currentLegoIndex = 0;
      return this.getNextNewItem(thread, threadId);
    }
    const lego = legos[thread.currentLegoIndex];
    if (!thread.queue.getProgress(lego.id)) {
      thread.queue.addNew(lego, threadId, this.courseId);
    }
    if (!this.legoProgressMap.has(lego.id)) {
      this.legoProgressMap.set(
        lego.id,
        createDefaultLegoProgress(lego.id, this.courseId, threadId)
      );
    }
    thread.currentLegoIndex++;
    const basket = this.baskets.get(lego.id);
    if (basket) {
      return this.startRoundForLego(lego.id, threadId);
    }
    return this.createLearningItem(lego, seed, threadId, "introduction");
  }
  /**
   * Start a Round for a LEGO (or continue an existing one)
   */
  startRoundForLego(legoId, _threadId) {
    if (!this.roundStates.has(legoId)) {
      this.roundStates.set(legoId, createRoundState(legoId, this.roundConfig));
    }
    this.activeRoundLegoId = legoId;
    return this.continueActiveRound();
  }
  /**
   * Create a learning item using basket for phrase variety
   */
  createLearningItemWithBasket(lego, seed, threadId, mode) {
    const basket = this.baskets.get(lego.id);
    const progress = this.legoProgressMap.get(lego.id);
    if (basket && progress && basket.eternal_phrases.length > 0) {
      const { phrase, updatedUrn } = selectEternalPhrase(
        basket,
        progress,
        this.roundConfig.phraseSelector
      );
      if (phrase) {
        this.legoProgressMap.set(lego.id, {
          ...progress,
          eternal_urn: updatedUrn,
          last_eternal_phrase_id: phrase.id
        });
        return {
          lego,
          phrase,
          seed,
          thread_id: threadId,
          mode
        };
      }
    }
    return this.createLearningItem(lego, seed, threadId, mode);
  }
  createLearningItem(lego, seed, threadId, mode) {
    const phrase = {
      id: `${lego.id}_phrase`,
      phraseType: mode === "introduction" ? "debut" : "eternal",
      phrase: lego.lego,
      audioRefs: lego.audioRefs,
      wordCount: lego.lego.target.split(/\s+/).length,
      containsLegos: [lego.id]
    };
    return {
      lego,
      phrase,
      seed,
      thread_id: threadId,
      mode
    };
  }
  rotateThread() {
    this.activeThread = this.activeThread % this.helixConfig.thread_count + 1;
  }
  findLego(legoId) {
    for (const seed of this.allSeeds) {
      const lego = seed.legos.find((l) => l.id === legoId);
      if (lego) return lego;
    }
    return null;
  }
  findSeedForLego(legoId) {
    for (const seed of this.allSeeds) {
      if (seed.legos.some((l) => l.id === legoId)) {
        return seed;
      }
    }
    return null;
  }
  findThreadForLego(legoId) {
    for (const thread of this.threads.values()) {
      if (thread.queue.getProgress(legoId)) {
        return thread;
      }
    }
    return null;
  }
};
function createTripleHelixEngine(helixConfig, repetitionConfig, courseId) {
  return new TripleHelixEngine(helixConfig, repetitionConfig, courseId);
}

// src/learning/MetricsTracker.ts
var MetricsTracker = class {
  config;
  rollingWindow = [];
  lengthDeltaHistory = [];
  currentSession = null;
  listeners = [];
  constructor(config) {
    this.config = config;
  }
  /**
   * Start a new session
   */
  startSession(sessionId) {
    if (this.currentSession) {
      this.endSession();
    }
    this.currentSession = {
      session_id: sessionId,
      started_at: /* @__PURE__ */ new Date(),
      ended_at: null,
      items_practiced: 0,
      spikes_detected: 0,
      final_rolling_average: 0,
      metrics: [],
      spikes: []
    };
    this.rollingWindow = [];
    this.lengthDeltaHistory = [];
    this.emit({ type: "session_started", session_id: sessionId });
  }
  /**
   * End the current session
   */
  endSession() {
    if (!this.currentSession) return null;
    this.currentSession.ended_at = /* @__PURE__ */ new Date();
    this.currentSession.final_rolling_average = this.getRollingAverage();
    const session = { ...this.currentSession };
    this.emit({ type: "session_ended", session });
    this.currentSession = null;
    return session;
  }
  /**
   * Record a response metric
   *
   * @param legoId - The LEGO that was practiced
   * @param responseLatencyMs - Legacy latency measurement (pause duration)
   * @param phraseLength - Length of target phrase in characters
   * @param threadId - Which triple helix thread
   * @param mode - Learning mode (component, debut, practice, etc.)
   * @param timing - Optional speech timing data from VAD
   * @param responseLengthMs - Optional: learner response length (legacy, use timing instead)
   * @param modelLengthMs - Optional: model audio length (legacy, use timing instead)
   */
  recordResponse(legoId, responseLatencyMs, phraseLength2, threadId, mode, timing, responseLengthMs, modelLengthMs) {
    const normalizedLatency = this.normalizeLatency(responseLatencyMs, phraseLength2);
    const metric = {
      id: `metric-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      lego_id: legoId,
      timestamp: /* @__PURE__ */ new Date(),
      response_latency_ms: responseLatencyMs,
      phrase_length: phraseLength2,
      normalized_latency: normalizedLatency,
      thread_id: threadId,
      triggered_spike: false,
      // Will be updated by SpikeDetector
      mode,
      // Speech timing data (from VAD)
      speech_detected: timing?.speech_detected,
      true_latency_ms: timing?.response_latency_ms,
      learner_duration_ms: timing?.learner_duration_ms,
      duration_delta_ms: timing?.duration_delta_ms,
      started_during_prompt: timing?.started_during_prompt,
      still_speaking_at_voice1: timing?.still_speaking_at_voice1
    };
    let lengthDelta = null;
    if (timing?.duration_delta_ms !== void 0 && timing.duration_delta_ms !== null) {
      lengthDelta = timing.duration_delta_ms;
    } else if (responseLengthMs !== void 0 && modelLengthMs !== void 0) {
      lengthDelta = responseLengthMs - modelLengthMs;
    }
    this.rollingWindow.push(metric);
    if (this.rollingWindow.length > this.config.spike.rolling_window_size) {
      this.rollingWindow.shift();
    }
    this.lengthDeltaHistory.push({
      timestamp: /* @__PURE__ */ new Date(),
      length_delta: lengthDelta
    });
    if (this.lengthDeltaHistory.length > this.config.spike.rolling_window_size) {
      this.lengthDeltaHistory.shift();
    }
    if (this.currentSession) {
      this.currentSession.metrics.push(metric);
      this.currentSession.items_practiced++;
    }
    this.emit({ type: "item_completed", metric });
    return metric;
  }
  /**
   * Record a spike event
   */
  recordSpike(spike) {
    if (this.currentSession) {
      this.currentSession.spikes.push(spike);
      this.currentSession.spikes_detected++;
    }
    if (this.rollingWindow.length > 0) {
      this.rollingWindow[this.rollingWindow.length - 1].triggered_spike = true;
    }
    this.emit({ type: "spike_detected", spike });
  }
  /**
   * Get the current rolling average (normalized latency)
   */
  getRollingAverage() {
    if (this.rollingWindow.length === 0) return 0;
    const sum = this.rollingWindow.reduce((acc, m) => acc + m.normalized_latency, 0);
    return sum / this.rollingWindow.length;
  }
  /**
   * Get the rolling standard deviation (normalized latency)
   * Formula: stddev = sqrt(sum((x - mean)^2) / n)
   */
  getRollingStdDev() {
    if (this.rollingWindow.length === 0) return 0;
    const mean2 = this.getRollingAverage();
    const sumSquaredDiffs = this.rollingWindow.reduce((acc, m) => {
      const diff = m.normalized_latency - mean2;
      return acc + diff * diff;
    }, 0);
    return Math.sqrt(sumSquaredDiffs / this.rollingWindow.length);
  }
  /**
   * Get the rolling average of length_delta
   */
  getRollingAvgLengthDelta() {
    const validDeltas = this.lengthDeltaHistory.filter((d) => d.length_delta !== null);
    if (validDeltas.length === 0) return 0;
    const sum = validDeltas.reduce((acc, d) => acc + d.length_delta, 0);
    return sum / validDeltas.length;
  }
  /**
   * Get the rolling standard deviation of length_delta
   * Formula: stddev = sqrt(sum((x - mean)^2) / n)
   */
  getRollingStdDevLengthDelta() {
    const validDeltas = this.lengthDeltaHistory.filter((d) => d.length_delta !== null);
    if (validDeltas.length === 0) return 0;
    const mean2 = this.getRollingAvgLengthDelta();
    const sumSquaredDiffs = validDeltas.reduce((acc, d) => {
      const diff = d.length_delta - mean2;
      return acc + diff * diff;
    }, 0);
    return Math.sqrt(sumSquaredDiffs / validDeltas.length);
  }
  /**
   * Get the current rolling window
   */
  getRollingWindow() {
    return [...this.rollingWindow];
  }
  /**
   * Check if we have enough data for spike detection
   * Need at least half the window to avoid false positives
   */
  hasEnoughData() {
    return this.rollingWindow.length >= Math.ceil(this.config.spike.rolling_window_size / 2);
  }
  /**
   * Get the current session (if any)
   */
  getCurrentSession() {
    return this.currentSession ? { ...this.currentSession } : null;
  }
  /**
   * Add a listener for metrics events
   */
  addListener(listener) {
    this.listeners.push(listener);
  }
  /**
   * Remove a listener
   */
  removeListener(listener) {
    const index = this.listeners.indexOf(listener);
    if (index !== -1) {
      this.listeners.splice(index, 1);
    }
  }
  /**
   * Update configuration
   */
  updateConfig(config) {
    if (config.spike) {
      this.config.spike = { ...this.config.spike, ...config.spike };
      const newSize = this.config.spike.rolling_window_size;
      while (this.rollingWindow.length > newSize) {
        this.rollingWindow.shift();
      }
      while (this.lengthDeltaHistory.length > newSize) {
        this.lengthDeltaHistory.shift();
      }
    }
  }
  /**
   * Normalize latency by phrase length
   * This allows fair comparison between short and long phrases
   *
   * Formula: latency_per_character = latency_ms / phrase_length
   * Minimum phrase length of 5 to avoid division issues
   */
  normalizeLatency(latencyMs, phraseLength2) {
    const effectiveLength = Math.max(phraseLength2, 5);
    return latencyMs / effectiveLength;
  }
  /**
   * Emit an event to all listeners
   */
  emit(event) {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch (error) {
        console.error("MetricsTracker listener error:", error);
      }
    }
  }
};
function createMetricsTracker(config) {
  return new MetricsTracker(config);
}

// src/learning/SpikeDetector.ts
var SpikeDetector = class {
  config;
  metricsTracker;
  adaptationState;
  constructor(config, metricsTracker) {
    this.config = config;
    this.metricsTracker = metricsTracker;
    this.adaptationState = {
      alternate_index: 0,
      items_since_spike: Infinity,
      // Start with no cooldown
      next_response: config.spike.alternate_sequence[0]
    };
  }
  /**
   * Analyze a response and determine if it's a spike
   *
   * Uses differential-based discontinuity detection per APML spec:
   * 1. Calculate latency_differential = current_latency - rolling_average
   * 2. Detect discontinuity when: abs(differential) > (stddev_threshold * rolling_stddev)
   * 3. Calculate magnitude = abs(differential) / stddev (how many sigmas)
   * 4. Classify severity: mild (<2.5σ), moderate (≥2.5σ), severe (>4.0σ)
   *
   * Falls back to threshold_percent approach if use_stddev_detection is false
   */
  detectSpike(normalizedLatency) {
    const rollingAverage = this.metricsTracker.getRollingAverage();
    const rollingStdDev = this.metricsTracker.getRollingStdDev();
    const differential = normalizedLatency - rollingAverage;
    if (!this.metricsTracker.hasEnoughData()) {
      return {
        is_spike: false,
        latency: normalizedLatency,
        rolling_average: rollingAverage,
        threshold: 0,
        ratio: 0,
        differential,
        stddev: rollingStdDev,
        magnitude: 0,
        severity: "none"
      };
    }
    let isSpike = false;
    let magnitude = 0;
    let severity = "none";
    if (this.config.spike.use_stddev_detection && rollingStdDev > 0) {
      magnitude = Math.abs(differential) / rollingStdDev;
      const threshold2 = this.config.spike.stddev_threshold * rollingStdDev;
      isSpike = differential > threshold2;
      if (isSpike) {
        if (magnitude >= 4) {
          severity = "severe";
        } else if (magnitude >= 2.5) {
          severity = "moderate";
        } else {
          severity = "mild";
        }
      }
    } else {
      const threshold2 = rollingAverage * (this.config.spike.threshold_percent / 100);
      isSpike = normalizedLatency > threshold2;
      if (rollingStdDev > 0) {
        magnitude = Math.abs(differential) / rollingStdDev;
        if (magnitude >= 4) {
          severity = "severe";
        } else if (magnitude >= 2.5) {
          severity = "moderate";
        } else if (isSpike) {
          severity = "mild";
        }
      } else {
        const ratio2 = rollingAverage > 0 ? normalizedLatency / rollingAverage : 0;
        if (ratio2 > 2.5) {
          severity = "severe";
        } else if (ratio2 > 1.8) {
          severity = "moderate";
        } else if (isSpike) {
          severity = "mild";
        }
      }
    }
    const threshold = this.config.spike.use_stddev_detection && rollingStdDev > 0 ? this.config.spike.stddev_threshold * rollingStdDev : rollingAverage * (this.config.spike.threshold_percent / 100);
    const ratio = rollingAverage > 0 ? normalizedLatency / rollingAverage : 0;
    return {
      is_spike: isSpike,
      latency: normalizedLatency,
      rolling_average: rollingAverage,
      threshold,
      ratio,
      differential,
      stddev: rollingStdDev,
      magnitude,
      severity: isSpike ? severity : "none"
    };
  }
  /**
   * Determine what response to take for a spike
   */
  getSpikeResponse(detection, legoType) {
    if (!detection.is_spike) {
      return { action: "none", reason: "No spike detected", in_cooldown: false };
    }
    if (this.adaptationState.items_since_spike < this.config.spike.cooldown_items) {
      return {
        action: "none",
        reason: `In cooldown (${this.adaptationState.items_since_spike}/${this.config.spike.cooldown_items} items)`,
        in_cooldown: true
      };
    }
    let action;
    switch (this.config.spike.response_strategy) {
      case "repeat":
        action = "repeat";
        break;
      case "breakdown":
        action = legoType === "M" ? "breakdown" : "repeat";
        break;
      case "alternate":
        const nextResponse = this.adaptationState.next_response;
        if (nextResponse === "breakdown" && legoType === "A") {
          action = "repeat";
        } else {
          action = nextResponse;
        }
        break;
      default:
        action = "repeat";
    }
    return {
      action,
      reason: `Spike detected (ratio: ${detection.ratio.toFixed(2)}x average)`,
      in_cooldown: false
    };
  }
  /**
   * Process a complete response and return what to do
   */
  processResponse(legoId, normalizedLatency, legoType, threadId) {
    this.adaptationState.items_since_spike++;
    const detection = this.detectSpike(normalizedLatency);
    const response = this.getSpikeResponse(detection, legoType);
    let spike;
    if (response.action !== "none" && !response.in_cooldown) {
      spike = {
        id: `spike-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        lego_id: legoId,
        timestamp: /* @__PURE__ */ new Date(),
        latency: detection.latency,
        rolling_average: detection.rolling_average,
        spike_ratio: detection.ratio,
        response: response.action,
        thread_id: threadId
      };
      this.metricsTracker.recordSpike(spike);
      this.adaptationState.items_since_spike = 0;
      if (this.config.spike.response_strategy === "alternate") {
        this.advanceAlternateSequence();
      }
    }
    return { detection, response, spike };
  }
  /**
   * Get current adaptation state
   */
  getAdaptationState() {
    return { ...this.adaptationState };
  }
  /**
   * Reset adaptation state (e.g., at session start)
   */
  resetState() {
    this.adaptationState = {
      alternate_index: 0,
      items_since_spike: Infinity,
      next_response: this.config.spike.alternate_sequence[0]
    };
  }
  /**
   * Update configuration
   */
  updateConfig(config) {
    if (config.spike) {
      this.config.spike = { ...this.config.spike, ...config.spike };
      if (config.spike.alternate_sequence) {
        this.adaptationState.alternate_index = 0;
        this.adaptationState.next_response = this.config.spike.alternate_sequence[0];
      }
    }
  }
  /**
   * Advance to next item in alternate sequence
   */
  advanceAlternateSequence() {
    const sequence = this.config.spike.alternate_sequence;
    this.adaptationState.alternate_index = (this.adaptationState.alternate_index + 1) % sequence.length;
    this.adaptationState.next_response = sequence[this.adaptationState.alternate_index];
  }
};
function createSpikeDetector(config, metricsTracker) {
  return new SpikeDetector(config, metricsTracker);
}

// src/learning/MasteryStateMachine.ts
var TYPICAL_SKIP = {
  acquisition: 1,
  consolidating: 3,
  confident: 8,
  mastered: 21
};
var DEFAULT_CONFIG4 = {
  advancement_threshold: 3,
  // Consecutive smooth to advance
  fast_track_threshold: 5
  // Consecutive fast to skip a state
};
var MasteryStateMachine = class {
  config;
  states = /* @__PURE__ */ new Map();
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG4, ...config };
  }
  // ============================================
  // PUBLIC API
  // ============================================
  /**
   * Get mastery state for a LEGO
   * Creates new state if doesn't exist
   */
  getState(legoId) {
    let state = this.states.get(legoId);
    if (!state) {
      state = this.createInitialState(legoId);
      this.states.set(legoId, state);
    }
    return state;
  }
  /**
   * Record a smooth response (no discontinuity detected)
   *
   * @param legoId - LEGO identifier
   * @param wasFast - Whether response was faster than learner's pattern
   * @returns Transition if state changed, null otherwise
   */
  recordSmooth(legoId, wasFast) {
    const state = this.getState(legoId);
    state.consecutive_smooth++;
    if (wasFast) {
      state.consecutive_fast++;
    } else {
      state.consecutive_fast = 0;
    }
    state.updated_at = /* @__PURE__ */ new Date();
    const transition = this.checkAndApplyTransition(state);
    return transition;
  }
  /**
   * Record a discontinuity (pattern break detected)
   *
   * @param legoId - LEGO identifier
   * @param severity - 'mild' | 'moderate' | 'severe'
   * @returns Transition if state changed, null otherwise
   */
  recordDiscontinuity(legoId, severity) {
    const state = this.getState(legoId);
    const previousState = state.current_state;
    state.discontinuity_count++;
    state.last_discontinuity_at = /* @__PURE__ */ new Date();
    state.updated_at = /* @__PURE__ */ new Date();
    let transition = null;
    switch (severity) {
      case "mild":
        break;
      case "moderate":
        state.consecutive_smooth = 0;
        state.consecutive_fast = 0;
        transition = {
          from_state: previousState,
          to_state: state.current_state,
          reason: "hold",
          timestamp: state.updated_at
        };
        break;
      case "severe":
        const newState = this.regressState(state.current_state);
        if (newState !== state.current_state) {
          state.current_state = newState;
          transition = {
            from_state: previousState,
            to_state: newState,
            reason: "regression",
            timestamp: state.updated_at
          };
        }
        state.consecutive_smooth = 0;
        state.consecutive_fast = 0;
        break;
    }
    return transition;
  }
  /**
   * Get the typical skip value for a LEGO based on its current mastery state
   *
   * @param legoId - LEGO identifier
   * @returns Number of items to skip before practicing again
   */
  getTypicalSkip(legoId) {
    const state = this.getState(legoId);
    return TYPICAL_SKIP[state.current_state];
  }
  /**
   * Get all mastery states (for persistence)
   */
  getAllStates() {
    return Array.from(this.states.values());
  }
  /**
   * Load existing states (from persistence)
   */
  loadStates(states) {
    this.states.clear();
    for (const state of states) {
      const loadedState = {
        ...state,
        last_discontinuity_at: state.last_discontinuity_at ? new Date(state.last_discontinuity_at) : null,
        created_at: new Date(state.created_at),
        updated_at: new Date(state.updated_at)
      };
      this.states.set(state.lego_id, loadedState);
    }
  }
  /**
   * Update configuration
   */
  updateConfig(config) {
    this.config = { ...this.config, ...config };
  }
  /**
   * Get current configuration
   */
  getConfig() {
    return { ...this.config };
  }
  /**
   * Clear all states
   */
  clear() {
    this.states.clear();
  }
  /**
   * Reset state for a specific LEGO
   */
  resetState(legoId) {
    this.states.delete(legoId);
  }
  /**
   * Get all LEGOs in a specific mastery state
   */
  getLegosByState(state) {
    const result = [];
    this.states.forEach((legoState, legoId) => {
      if (legoState.current_state === state) {
        result.push(legoId);
      }
    });
    return result;
  }
  /**
   * Get statistics across all LEGOs
   */
  getStats() {
    const all = Array.from(this.states.values());
    const byState = {
      acquisition: 0,
      consolidating: 0,
      confident: 0,
      mastered: 0
    };
    let totalSmooth = 0;
    let totalDiscontinuities = 0;
    for (const state of all) {
      byState[state.current_state]++;
      totalSmooth += state.consecutive_smooth;
      totalDiscontinuities += state.discontinuity_count;
    }
    return {
      total: all.length,
      by_state: byState,
      avg_consecutive_smooth: all.length > 0 ? totalSmooth / all.length : 0,
      avg_discontinuity_count: all.length > 0 ? totalDiscontinuities / all.length : 0
    };
  }
  // ============================================
  // PRIVATE METHODS
  // ============================================
  /**
   * Create initial state for a new LEGO
   */
  createInitialState(legoId) {
    const now = /* @__PURE__ */ new Date();
    return {
      lego_id: legoId,
      current_state: "acquisition",
      consecutive_smooth: 0,
      consecutive_fast: 0,
      discontinuity_count: 0,
      last_discontinuity_at: null,
      created_at: now,
      updated_at: now
    };
  }
  /**
   * Check if state should transition and apply if needed
   */
  checkAndApplyTransition(state) {
    const previousState = state.current_state;
    if (state.consecutive_fast >= this.config.fast_track_threshold) {
      const newState = this.advanceState(state.current_state, 2);
      if (newState !== state.current_state) {
        state.current_state = newState;
        state.consecutive_smooth = 0;
        state.consecutive_fast = 0;
        return {
          from_state: previousState,
          to_state: newState,
          reason: "fast_track",
          timestamp: state.updated_at
        };
      }
    }
    if (state.consecutive_smooth >= this.config.advancement_threshold) {
      const newState = this.advanceState(state.current_state, 1);
      if (newState !== state.current_state) {
        state.current_state = newState;
        state.consecutive_smooth = 0;
        state.consecutive_fast = 0;
        return {
          from_state: previousState,
          to_state: newState,
          reason: "advancement",
          timestamp: state.updated_at
        };
      }
    }
    return null;
  }
  /**
   * Advance mastery state by N steps
   */
  advanceState(current, steps = 1) {
    const stateOrder = [
      "acquisition",
      "consolidating",
      "confident",
      "mastered"
    ];
    const currentIndex = stateOrder.indexOf(current);
    const newIndex = Math.min(currentIndex + steps, stateOrder.length - 1);
    return stateOrder[newIndex];
  }
  /**
   * Regress mastery state by one step
   */
  regressState(current) {
    const stateOrder = [
      "acquisition",
      "consolidating",
      "confident",
      "mastered"
    ];
    const currentIndex = stateOrder.indexOf(current);
    const newIndex = Math.max(currentIndex - 1, 0);
    return stateOrder[newIndex];
  }
};
function createMasteryStateMachine(config = {}) {
  return new MasteryStateMachine(config);
}

// src/learning/curvature.ts
var DEFAULT_WINDOW = 7;
var DEFAULT_MIN_SAMPLES = 5;
function computeCurvature(values, options = {}) {
  const window2 = Math.max(3, options.window ?? DEFAULT_WINDOW);
  const minSamples = Math.max(3, options.minSamples ?? DEFAULT_MIN_SAMPLES);
  const n = values.length;
  if (n < minSamples) return null;
  const xs = options.x;
  if (xs && xs.length !== n) {
    throw new Error(
      `curvature: x length (${xs.length}) must equal values length (${n})`
    );
  }
  const start = Math.max(0, n - window2);
  const count = n - start;
  if (count < minSamples) return null;
  const xLast = xs ? xs[n - 1] : n - 1;
  let s0 = 0, s1 = 0, s2 = 0, s3 = 0, s4 = 0;
  let t0 = 0, t1 = 0, t2 = 0;
  for (let i = start; i < n; i++) {
    const u = (xs ? xs[i] : i) - xLast;
    const y = values[i];
    const u2 = u * u;
    s0 += 1;
    s1 += u;
    s2 += u2;
    s3 += u2 * u;
    s4 += u2 * u2;
    t0 += y;
    t1 += y * u;
    t2 += y * u2;
  }
  const c = solve3(
    [
      [s0, s1, s2],
      [s1, s2, s3],
      [s2, s3, s4]
    ],
    [t0, t1, t2]
  );
  if (!c) return null;
  return {
    level: c[0],
    velocity: c[1],
    acceleration: 2 * c[2],
    samples: count
  };
}
function curvatureSeries(values, options = {}) {
  const xs = options.x;
  if (xs && xs.length !== values.length) {
    throw new Error(
      `curvature: x length (${xs.length}) must equal values length (${values.length})`
    );
  }
  const out = [];
  for (let end = 1; end <= values.length; end++) {
    out.push(
      computeCurvature(values.slice(0, end), {
        ...options,
        x: xs ? xs.slice(0, end) : void 0
      })
    );
  }
  return out;
}
function accelerationAlarm(values, options = {}) {
  const noiseWindow = Math.max(2, options.noiseWindow ?? 10);
  const threshold = options.threshold ?? 2;
  const series = curvatureSeries(values, options);
  const recent = [];
  return series.map((pt, index) => {
    if (!pt) return { index, acceleration: null, z: null, flagged: false };
    const accel = pt.acceleration;
    let z = null;
    let flagged = false;
    if (recent.length >= 2) {
      const mean2 = recent.reduce((a, b) => a + b, 0) / recent.length;
      const variance = recent.reduce((a, b) => a + (b - mean2) * (b - mean2), 0) / recent.length;
      const std = Math.sqrt(variance);
      z = std > 1e-9 ? (accel - mean2) / std : accel - mean2;
      flagged = Math.abs(z) >= threshold;
    }
    recent.push(accel);
    if (recent.length > noiseWindow) recent.shift();
    return { index, acceleration: accel, z, flagged };
  });
}
function solve3(M, b) {
  const a = [
    [M[0][0], M[0][1], M[0][2], b[0]],
    [M[1][0], M[1][1], M[1][2], b[1]],
    [M[2][0], M[2][1], M[2][2], b[2]]
  ];
  for (let col = 0; col < 3; col++) {
    let pivot = col;
    for (let r = col + 1; r < 3; r++) {
      if (Math.abs(a[r][col]) > Math.abs(a[pivot][col])) pivot = r;
    }
    if (Math.abs(a[pivot][col]) < 1e-12) return null;
    if (pivot !== col) {
      const tmp = a[col];
      a[col] = a[pivot];
      a[pivot] = tmp;
    }
    for (let r = col + 1; r < 3; r++) {
      const factor = a[r][col] / a[col][col];
      for (let k = col; k < 4; k++) a[r][k] -= factor * a[col][k];
    }
  }
  const c = [0, 0, 0];
  for (let row = 2; row >= 0; row--) {
    let sum = a[row][3];
    for (let k = row + 1; k < 3; k++) sum -= a[row][k] * c[k];
    c[row] = sum / a[row][row];
  }
  return [c[0], c[1], c[2]];
}

// src/learning/localDifficulty.ts
function assessLocalDifficulty(series, options = {}) {
  const { unitId, unitKind, values, x } = series;
  const opts = { ...options, x };
  const curvature = computeCurvature(values, opts);
  const alarms = accelerationAlarm(values, opts);
  const last = alarms.length > 0 ? alarms[alarms.length - 1] : void 0;
  const accelerationZ = last?.z ?? null;
  const flagged = last?.flagged ?? false;
  const samples = curvature?.samples ?? 0;
  let state;
  if (!curvature || !last || last.acceleration === null) {
    state = "warming_up";
  } else if (flagged && curvature.acceleration > 0) {
    state = "struggling";
  } else if (flagged && curvature.acceleration < 0) {
    state = "easing";
  } else {
    state = "steady";
  }
  return { unitId, unitKind, curvature, accelerationZ, flagged, samples, state };
}
function rankLocalDifficulty(seriesList, options = {}) {
  const assessed = seriesList.map((s) => assessLocalDifficulty(s, options));
  const rank = {
    struggling: 0,
    easing: 1,
    steady: 2,
    warming_up: 3
  };
  return assessed.sort((a, b) => {
    if (rank[a.state] !== rank[b.state]) return rank[a.state] - rank[b.state];
    const za = a.accelerationZ ?? 0;
    const zb = b.accelerationZ ?? 0;
    return Math.abs(zb) - Math.abs(za);
  });
}

// src/learning/centrality.ts
function tokenizeTarget(text) {
  return text.normalize("NFC").toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
}
function containsTokenSeq(haystack, needle) {
  if (needle.length === 0 || needle.length > haystack.length) return false;
  outer: for (let i = 0; i + needle.length <= haystack.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return true;
  }
  return false;
}
function computeLegoCentrality(legos, phrases) {
  const docs = [];
  for (const p of phrases) {
    docs.push({ ownerOrdinal: p.ownerOrdinal, tokens: tokenizeTarget(p.target) });
  }
  for (const l of legos) {
    docs.push({ ownerOrdinal: l.ordinal, tokens: tokenizeTarget(l.target) });
  }
  const postings = /* @__PURE__ */ new Map();
  for (let d = 0; d < docs.length; d++) {
    const seen = /* @__PURE__ */ new Set();
    for (const t of docs[d].tokens) {
      if (seen.has(t)) continue;
      seen.add(t);
      let list = postings.get(t);
      if (!list) {
        list = [];
        postings.set(t, list);
      }
      list.push(d);
    }
  }
  const results = legos.map((lego) => {
    const needle = tokenizeTarget(lego.target);
    let forwardReuse = 0;
    if (needle.length > 0) {
      let candidates;
      for (const t of needle) {
        const list = postings.get(t);
        if (!list) {
          candidates = [];
          break;
        }
        if (!candidates || list.length < candidates.length) candidates = list;
      }
      for (const d of candidates ?? []) {
        const doc = docs[d];
        if (doc.ownerOrdinal <= lego.ordinal) continue;
        if (needle.length === 1 || containsTokenSeq(doc.tokens, needle)) forwardReuse++;
      }
    }
    return { legoId: lego.legoId, ordinal: lego.ordinal, forwardReuse, percentile: 0 };
  });
  const sorted = [...results].sort((a, b) => a.forwardReuse - b.forwardReuse);
  const n = sorted.length;
  const percentileByCount = /* @__PURE__ */ new Map();
  for (let i = 0; i < n; i++) {
    const c = sorted[i].forwardReuse;
    if (!percentileByCount.has(c)) {
      percentileByCount.set(c, n > 1 ? i / (n - 1) : 1);
    }
  }
  for (const r of results) {
    r.percentile = percentileByCount.get(r.forwardReuse) ?? 0;
  }
  return new Map(results.map((r) => [r.legoId, r]));
}

// src/learning/evidence.ts
var EVIDENCE_SERIES_RING_CAP = 20;
var EvidenceAggregatorImpl = class {
  series = /* @__PURE__ */ new Map();
  record(e) {
    const ring = this.series.get(e.unitId) ?? [];
    const last = ring[ring.length - 1];
    if (last !== void 0 && e.cycleId !== void 0 && last.cycleId === e.cycleId) {
      const newWeightSum = last.weightSum + e.weight;
      last.value = newWeightSum > 0 ? (last.value * last.weightSum + e.value * e.weight) / newWeightSum : last.value;
      last.weightSum = newWeightSum;
    } else {
      ring.push({ value: e.value, cycleId: e.cycleId, weightSum: e.weight });
      while (ring.length > EVIDENCE_SERIES_RING_CAP) {
        ring.shift();
      }
    }
    this.series.set(e.unitId, ring);
  }
  getSeries(unitId) {
    const ring = this.series.get(unitId) ?? [];
    return { values: ring.map((r) => r.value), x: ring.map((_, i) => i) };
  }
  readyUnits(minSamples) {
    const ready = [];
    for (const [unitId, ring] of this.series) {
      if (ring.length >= minSamples) ready.push(unitId);
    }
    return ready;
  }
  snapshot() {
    const out = /* @__PURE__ */ new Map();
    for (const [unitId, ring] of this.series) {
      out.set(unitId, { values: ring.map((r) => r.value), x: ring.map((_, i) => i) });
    }
    return out;
  }
  /** `x` on the input is ignored (and may be absent/stale ms-based data from before the cycle-index fix) — always recomputed as ring position. */
  hydrate(data) {
    for (const [unitId, { values }] of data) {
      const ring = values.map((value) => ({
        value,
        cycleId: void 0,
        weightSum: 1
      }));
      this.series.set(unitId, ring.slice(-EVIDENCE_SERIES_RING_CAP));
    }
  }
};
function createEvidenceAggregator() {
  return new EvidenceAggregatorImpl();
}

// src/learning/ratePolicy.ts
var DEFAULT_RATE_POLICY_BOUNDS = {
  buildCount: { floor: 3, ceiling: 7, scripted: 7, step: 1 },
  consolidateCount: { floor: 1, ceiling: 4, scripted: 2, step: 1 },
  spacedRepCap: { floor: 6, ceiling: 12, scripted: 12, step: 1 },
  pauseMultiplier: { floor: 0.7, ceiling: 1.4, scripted: 1, step: 0.05 },
  breatherMinRoundGap: 3
};
var DEFAULT_RATE_POLICY_CONFIG = {
  bounds: DEFAULT_RATE_POLICY_BOUNDS,
  criticalFrontloadFraction: 0.15,
  criticalCentralityFraction: 0.15,
  neighbourhoodWindow: 3,
  hysteresisReads: 2,
  decayRounds: 5,
  manualOverrideDeadZoneMultiplier: 2
};
function mergeBounds(partial) {
  if (!partial) return DEFAULT_RATE_POLICY_BOUNDS;
  return {
    buildCount: { ...DEFAULT_RATE_POLICY_BOUNDS.buildCount, ...partial.buildCount },
    consolidateCount: { ...DEFAULT_RATE_POLICY_BOUNDS.consolidateCount, ...partial.consolidateCount },
    spacedRepCap: { ...DEFAULT_RATE_POLICY_BOUNDS.spacedRepCap, ...partial.spacedRepCap },
    pauseMultiplier: { ...DEFAULT_RATE_POLICY_BOUNDS.pauseMultiplier, ...partial.pauseMultiplier },
    breatherMinRoundGap: partial.breatherMinRoundGap ?? DEFAULT_RATE_POLICY_BOUNDS.breatherMinRoundGap
  };
}
function clamp(value, floor, ceiling) {
  return Math.min(ceiling, Math.max(floor, value));
}
var RatePolicyEngine = class _RatePolicyEngine {
  config;
  units = /* @__PURE__ */ new Map();
  /**
   * Units deferred by the budget decision (non-critical, confirmed
   * struggling, not the round's own LEGO) and not yet signalled as
   * return-ready — the population the neighbourhood return trigger watches.
   */
  deferredUnits = /* @__PURE__ */ new Set();
  /** Round-level lever offsets from scripted defaults, in step units (fractional while decaying). */
  buildOffset = 0;
  consolidateOffset = 0;
  spacedRepOffset = 0;
  roundsSinceBreather = Infinity;
  // never gated on session start
  constructor(config = {}) {
    this.config = {
      bounds: mergeBounds(config.bounds),
      criticalFrontloadFraction: config.criticalFrontloadFraction ?? DEFAULT_RATE_POLICY_CONFIG.criticalFrontloadFraction,
      criticalCentralityFraction: config.criticalCentralityFraction ?? DEFAULT_RATE_POLICY_CONFIG.criticalCentralityFraction,
      neighbourhoodWindow: config.neighbourhoodWindow ?? DEFAULT_RATE_POLICY_CONFIG.neighbourhoodWindow,
      hysteresisReads: config.hysteresisReads ?? DEFAULT_RATE_POLICY_CONFIG.hysteresisReads,
      decayRounds: Math.max(1, config.decayRounds ?? DEFAULT_RATE_POLICY_CONFIG.decayRounds),
      manualOverrideDeadZoneMultiplier: config.manualOverrideDeadZoneMultiplier ?? DEFAULT_RATE_POLICY_CONFIG.manualOverrideDeadZoneMultiplier
    };
  }
  unitState(unitId) {
    let s = this.units.get(unitId);
    if (!s) {
      s = { consecutiveStruggling: 0, consecutiveEasing: 0, pauseNudge: 0 };
      this.units.set(unitId, s);
    }
    return s;
  }
  isCritical(unitId, input) {
    const centrality = input.unitCentralityPercentile?.[unitId];
    if (centrality !== void 0) {
      return centrality >= 1 - this.config.criticalCentralityFraction;
    }
    const ordinal = input.unitOrdinals[unitId];
    if (ordinal === void 0) return false;
    const cutoff = Math.ceil(input.courseLegoCount * this.config.criticalFrontloadFraction);
    return ordinal <= cutoff;
  }
  /** Decay one lever offset a `step`-fraction of the way back to 0, never overshooting past 0. */
  static decayOffset(offset, step, decayRounds) {
    if (offset === 0) return 0;
    const decayAmount = step / decayRounds;
    if (offset > 0) return Math.max(0, offset - decayAmount);
    return Math.min(0, offset + decayAmount);
  }
  planRound(input) {
    const { bounds } = this.config;
    const requiredReads = input.manualOverrideActive ? this.config.hysteresisReads * this.config.manualOverrideDeadZoneMultiplier : this.config.hysteresisReads;
    let ownLegoConfirmedStruggling = false;
    let otherNonCriticalConfirmedStruggling = false;
    for (const read of input.difficulty) {
      const u = this.unitState(read.unitId);
      if (read.state === "struggling") {
        u.consecutiveStruggling++;
        u.consecutiveEasing = 0;
      } else if (read.state === "easing") {
        u.consecutiveEasing++;
        u.consecutiveStruggling = 0;
      } else {
        u.consecutiveStruggling = 0;
        u.consecutiveEasing = 0;
      }
      let unitMovedStruggling = false;
      let unitMovedEasing = false;
      if (u.consecutiveStruggling >= requiredReads) {
        u.pauseNudge = u.pauseNudge + bounds.pauseMultiplier.step;
        u.consecutiveStruggling = 0;
        unitMovedStruggling = true;
      } else if (u.consecutiveEasing >= requiredReads) {
        u.pauseNudge = u.pauseNudge - bounds.pauseMultiplier.step;
        u.consecutiveEasing = 0;
        unitMovedEasing = true;
      } else if (read.state === "steady") {
        u.pauseNudge = _RatePolicyEngine.decayOffset(u.pauseNudge, bounds.pauseMultiplier.step, this.config.decayRounds);
      }
      const maxTravel = bounds.pauseMultiplier.ceiling - bounds.pauseMultiplier.floor;
      u.pauseNudge = clamp(u.pauseNudge, -maxTravel, maxTravel);
      if (unitMovedStruggling || unitMovedEasing) {
        const isCritical = this.isCritical(read.unitId, input);
        const isOwnLego = read.unitId === input.roundLegoId && read.unitKind === "lego";
        if (unitMovedStruggling && !isCritical) {
          if (isOwnLego) {
            ownLegoConfirmedStruggling = true;
          } else {
            otherNonCriticalConfirmedStruggling = true;
            this.deferredUnits.add(read.unitId);
          }
        }
      }
    }
    let buildMoved = false;
    let consolidateMoved = false;
    let spacedRepMoved = false;
    let wantBreather = false;
    if (ownLegoConfirmedStruggling) {
      this.consolidateOffset += bounds.consolidateCount.step;
      consolidateMoved = true;
      wantBreather = true;
    } else if (otherNonCriticalConfirmedStruggling) {
      this.buildOffset -= bounds.buildCount.step;
      this.spacedRepOffset -= bounds.spacedRepCap.step;
      this.consolidateOffset += bounds.consolidateCount.step;
      buildMoved = true;
      spacedRepMoved = true;
      consolidateMoved = true;
    }
    if (!buildMoved) {
      this.buildOffset = _RatePolicyEngine.decayOffset(this.buildOffset, bounds.buildCount.step, this.config.decayRounds);
    }
    if (!consolidateMoved) {
      this.consolidateOffset = _RatePolicyEngine.decayOffset(
        this.consolidateOffset,
        bounds.consolidateCount.step,
        this.config.decayRounds
      );
    }
    if (!spacedRepMoved) {
      this.spacedRepOffset = _RatePolicyEngine.decayOffset(
        this.spacedRepOffset,
        bounds.spacedRepCap.step,
        this.config.decayRounds
      );
    }
    this.buildOffset = clamp(this.buildOffset, bounds.buildCount.floor - bounds.buildCount.scripted, bounds.buildCount.ceiling - bounds.buildCount.scripted);
    this.consolidateOffset = clamp(
      this.consolidateOffset,
      bounds.consolidateCount.floor - bounds.consolidateCount.scripted,
      bounds.consolidateCount.ceiling - bounds.consolidateCount.scripted
    );
    this.spacedRepOffset = clamp(this.spacedRepOffset, bounds.spacedRepCap.floor - bounds.spacedRepCap.scripted, bounds.spacedRepCap.ceiling - bounds.spacedRepCap.scripted);
    const returnReady = [];
    if (this.deferredUnits.size > 0) {
      const stateByUnit = new Map(input.difficulty.map((d) => [d.unitId, d.state]));
      for (const deferredId of [...this.deferredUnits]) {
        if (stateByUnit.get(deferredId) === "easing") {
          this.deferredUnits.delete(deferredId);
          continue;
        }
        const deferredOrdinal = input.unitOrdinals[deferredId];
        if (deferredOrdinal === void 0) continue;
        let neighbourEasing = false;
        let neighbourStruggling = false;
        for (const [unitId, state] of stateByUnit) {
          if (unitId === deferredId) continue;
          const ordinal = input.unitOrdinals[unitId];
          if (ordinal === void 0) continue;
          if (Math.abs(ordinal - deferredOrdinal) > this.config.neighbourhoodWindow) continue;
          if (state === "easing") neighbourEasing = true;
          else if (state === "struggling") neighbourStruggling = true;
        }
        if (neighbourEasing && !neighbourStruggling) {
          returnReady.push(deferredId);
          this.deferredUnits.delete(deferredId);
        }
      }
    }
    this.roundsSinceBreather += 1;
    let insertBreather = false;
    if (wantBreather && this.roundsSinceBreather >= bounds.breatherMinRoundGap) {
      insertBreather = true;
      this.roundsSinceBreather = 0;
    }
    const buildCount = clamp(
      Math.round(bounds.buildCount.scripted + this.buildOffset),
      bounds.buildCount.floor,
      bounds.buildCount.ceiling
    );
    const consolidateCount = clamp(
      Math.round(bounds.consolidateCount.scripted + this.consolidateOffset),
      bounds.consolidateCount.floor,
      bounds.consolidateCount.ceiling
    );
    const spacedRepCap = clamp(
      Math.round(bounds.spacedRepCap.scripted + this.spacedRepOffset),
      bounds.spacedRepCap.floor,
      bounds.spacedRepCap.ceiling
    );
    const units = this.units;
    const pauseFloor = bounds.pauseMultiplier.floor;
    const pauseCeiling = bounds.pauseMultiplier.ceiling;
    const pauseMultiplier = (legoId) => {
      const base = input.basePauseMultiplier(legoId);
      const nudge = units.get(legoId)?.pauseNudge ?? 0;
      return clamp(base + nudge, pauseFloor, pauseCeiling);
    };
    return { buildCount, consolidateCount, spacedRepCap, insertBreather, returnReady, pauseMultiplier };
  }
};
function createRatePolicyEngine(config = {}) {
  return new RatePolicyEngine(config);
}

// src/learning/syntheticSeries.ts
var QUIET_NOISE = 0.2;
var BREAK_STEPS_UP = [0.5, 1.3, 2.4, 4];
var BREAK_STEPS_DOWN = [0.5, 1.4, 2.6, 4.2];
var BREAK_LEN = 4;
var MIN_LENGTH = 8;
var round2 = (v) => Math.round(v * 100) / 100;
function makeLatencySeries(kind, opts = {}) {
  const rng = opts.rng ?? Math.random;
  const noiseAmt = (scale) => (rng() * 2 - 1) * scale;
  const n = opts.length != null ? Math.max(MIN_LENGTH, Math.floor(opts.length)) : 12 + Math.floor(rng() * 5);
  const out = [];
  if (kind === "struggling") {
    const quiet = n - BREAK_LEN;
    const base = 7 + rng() * 5;
    for (let i = 0; i < quiet; i++) {
      out.push(round2(base + 0.02 * i + noiseAmt(QUIET_NOISE)));
    }
    const b0 = base + 0.02 * quiet;
    for (const s of BREAK_STEPS_UP) out.push(round2(b0 + s + noiseAmt(0.15)));
  } else if (kind === "easing") {
    const quiet = n - BREAK_LEN;
    const base = 14 + rng() * 5;
    for (let i = 0; i < quiet; i++) {
      out.push(round2(base - 0.02 * i + noiseAmt(QUIET_NOISE)));
    }
    const b0 = base - 0.02 * quiet;
    for (const s of BREAK_STEPS_DOWN) out.push(round2(b0 - s + noiseAmt(0.15)));
  } else {
    const base = 8 + rng() * 8;
    const slope = (rng() * 2 - 1) * 0.05;
    for (let i = 0; i < n; i++) {
      out.push(round2(base + slope * i + noiseAmt(0.25)));
    }
  }
  return out;
}

// src/learning/competenceBand.ts
var ENTRY_CONVERSATIONAL_HOURS = 30;
var B1_CONVERSATIONAL_HOURS = 100;
var LABELS = {
  warming_up: "Warming up",
  entry_conversational: "Entry-conversational (~30h anchor)",
  b1_conversational: "Conversational, ~B1 (~100h anchor)",
  beyond_anchor: "Beyond the 100h anchor"
};
function competenceBand(totalPracticeMinutes) {
  const minutes = Number.isFinite(totalPracticeMinutes) && totalPracticeMinutes > 0 ? totalPracticeMinutes : 0;
  const hoursOnTask = minutes / 60;
  let band;
  let progressToNext;
  if (hoursOnTask < ENTRY_CONVERSATIONAL_HOURS) {
    band = "warming_up";
    progressToNext = hoursOnTask / ENTRY_CONVERSATIONAL_HOURS;
  } else if (hoursOnTask < B1_CONVERSATIONAL_HOURS) {
    band = "entry_conversational";
    progressToNext = (hoursOnTask - ENTRY_CONVERSATIONAL_HOURS) / (B1_CONVERSATIONAL_HOURS - ENTRY_CONVERSATIONAL_HOURS);
  } else {
    band = hoursOnTask >= 2 * B1_CONVERSATIONAL_HOURS ? "beyond_anchor" : "b1_conversational";
    progressToNext = 1;
  }
  return {
    band,
    label: LABELS[band],
    hoursOnTask,
    progressToNext,
    basis: "time-on-task",
    confidence: "flat-anchor"
  };
}

// src/learning/resolveResumeAnchor.ts
function seedOfLegoId(legoId) {
  if (!legoId) return null;
  const m = /^S(\d{4})L\d{2}/.exec(legoId);
  return m ? Number(m[1]) : null;
}
function resolveResumeAnchor(cursorLegoId, ceilingLegoId, findIndex, findFirstLegoOfSeed) {
  if (cursorLegoId && findIndex(cursorLegoId) !== -1) {
    return { legoId: cursorLegoId, viaCeiling: false };
  }
  if (cursorLegoId && findFirstLegoOfSeed) {
    const seed = seedOfLegoId(cursorLegoId);
    if (seed !== null) {
      const seedAnchor = findFirstLegoOfSeed(seed);
      if (seedAnchor && findIndex(seedAnchor) !== -1) {
        return { legoId: seedAnchor, viaCeiling: false, viaSeed: true };
      }
    }
  }
  if (ceilingLegoId && findIndex(ceilingLegoId) !== -1) {
    return { legoId: ceilingLegoId, viaCeiling: true };
  }
  return { legoId: null, viaCeiling: false };
}
function beyondSliceLanding(cursorLegoId, last) {
  if (!cursorLegoId || !last?.legoId) return null;
  const cursorSeed = seedOfLegoId(cursorLegoId);
  const lastSeed = last.seed ?? seedOfLegoId(last.legoId);
  if (cursorSeed === null || lastSeed === null) return null;
  return cursorSeed > lastSeed ? last.legoId : null;
}

// src/learning/resolveResumeStart.ts
var ResumeResolutionError = class extends Error {
  reason;
  constructor(message, reason) {
    super(message);
    this.name = "ResumeResolutionError";
    this.reason = reason;
  }
};
async function resolveResumeStart(deps) {
  const { lastCompletedLegoId, ceilingLegoId } = deps;
  try {
    if (await deps.hasReachedInfinitePlay(lastCompletedLegoId)) {
      throw new Error("CourseEndNoNextLego");
    }
    const map = await deps.fetchRoundMap();
    let seedHit = null;
    const findIndex = (legoId) => seedHit?.legoId === legoId ? seedHit.index : map.rounds.findIndex((r) => r.legoId === legoId);
    const firstLegoOfSeed = (seed) => {
      const index = map.rounds.findIndex((r) => r.seed === seed);
      if (index === -1) return null;
      const legoId = map.rounds[index].legoId;
      seedHit = { legoId, index };
      return legoId;
    };
    const { legoId: anchor, viaCeiling, viaSeed } = resolveResumeAnchor(
      lastCompletedLegoId,
      ceilingLegoId,
      findIndex,
      firstLegoOfSeed
    );
    if (viaSeed) {
      deps.onSeedFallback?.(lastCompletedLegoId, anchor);
    }
    if (viaCeiling) {
      deps.onCeilingFallback?.(lastCompletedLegoId, anchor);
    }
    if (!anchor) {
      const beyond = beyondSliceLanding(lastCompletedLegoId, map.rounds[map.rounds.length - 1]);
      if (lastCompletedLegoId && beyond) {
        deps.onBeyondMap?.(lastCompletedLegoId, beyond);
        return beyond;
      }
      if (lastCompletedLegoId) {
        deps.onAnchorMissing?.(lastCompletedLegoId);
      }
      return null;
    }
    return anchor;
  } catch (err) {
    if (err?.message === "CourseEndNoNextLego") throw err;
    if (err instanceof ResumeResolutionError) throw err;
    throw new ResumeResolutionError(
      `Could not resolve resume position (cursor=${lastCompletedLegoId ?? "null"}, ceiling=${ceilingLegoId ?? "null"}): ${String(err?.message ?? err)}`,
      err
    );
  }
}

// src/persistence/cursorTelemetry.ts
var sink = null;
function setCursorTelemetrySink(next) {
  sink = next;
}
function reportCursorMove(event) {
  if (!sink) return;
  try {
    sink(event);
  } catch {
  }
}

// src/persistence/ProgressStore.ts
var ProgressStore = class {
  client;
  schema;
  /** Per-enrolment serialisation for the read-then-add in updateEnrollmentActivity. */
  enrollmentActivityChain = /* @__PURE__ */ new Map();
  constructor(config) {
    this.client = config.client;
    this.schema = config.schema ?? "public";
  }
  // ============================================
  // LEARNER MANAGEMENT
  // ============================================
  async getLearner(learnerId) {
    const { data, error } = await this.client.schema(this.schema).from("learners").select("*").eq("id", learnerId).single();
    if (error) {
      if (error.code === "PGRST116") return null;
      throw new Error(`Failed to get learner: ${error.message}`);
    }
    return this.mapToLearnerRecord(data);
  }
  async updateLearnerPreferences(learnerId, prefs) {
    const { error } = await this.client.schema(this.schema).from("learners").update({
      preferences: prefs,
      updated_at: (/* @__PURE__ */ new Date()).toISOString()
    }).eq("id", learnerId);
    if (error) {
      throw new Error(`Failed to update preferences: ${error.message}`);
    }
  }
  // ============================================
  // COURSE ENROLLMENT
  // ============================================
  async getEnrollment(learnerId, courseId) {
    const { data, error } = await this.client.schema(this.schema).from("course_enrollments").select("*").eq("learner_id", learnerId).eq("course_id", courseId).single();
    if (error) {
      if (error.code === "PGRST116") return null;
      throw new Error(`Failed to get enrollment: ${error.message}`);
    }
    return this.mapToEnrollmentRecord(data);
  }
  async createEnrollment(learnerId, courseId) {
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const initialHelixState = {
      active_thread: 1,
      threads: {
        1: { seedOrder: [], currentSeedId: null, currentLegoIndex: 0 },
        2: { seedOrder: [], currentSeedId: null, currentLegoIndex: 0 },
        3: { seedOrder: [], currentSeedId: null, currentLegoIndex: 0 }
      },
      injected_content: {}
    };
    const { data, error } = await this.client.schema(this.schema).from("course_enrollments").insert({
      learner_id: learnerId,
      course_id: courseId,
      enrolled_at: now,
      last_practiced_at: null,
      total_practice_minutes: 0,
      helix_state: initialHelixState,
      last_completed_lego_id: null,
      last_completed_round_index: null
    }).select().single();
    if (error) {
      throw new Error(`Failed to create enrollment: ${error.message}`);
    }
    return this.mapToEnrollmentRecord(data);
  }
  async updateHelixState(learnerId, courseId, state) {
    const { error } = await this.client.schema(this.schema).from("course_enrollments").update({
      helix_state: state,
      last_practiced_at: (/* @__PURE__ */ new Date()).toISOString()
    }).eq("learner_id", learnerId).eq("course_id", courseId);
    if (error) {
      throw new Error(`Failed to update helix state: ${error.message}`);
    }
  }
  async updateEnrollmentProgress(learnerId, courseId, legoId, roundIndex) {
    const { error } = await this.client.schema(this.schema).from("course_enrollments").update({
      last_completed_lego_id: legoId,
      last_completed_round_index: roundIndex,
      // Round just completed → next round starts at cycle 0. Resets
      // the mid-round cursor (see updateCurrentCycle / migration
      // 20260507_current_cycle_index.sql).
      current_cycle_index: 0,
      last_practiced_at: (/* @__PURE__ */ new Date()).toISOString()
    }).eq("learner_id", learnerId).eq("course_id", courseId).or(`last_completed_round_index.is.null,last_completed_round_index.lt.${roundIndex}`);
    if (error) {
      throw new Error(`Failed to update enrollment progress: ${error.message}`);
    }
  }
  /**
   * Mid-round cursor write — bumps `current_cycle_index` so an app
   * reload (PWA update, close+open) resumes from the cycle the learner
   * was on, not the start of the round. Fires on every cycle_completed.
   *
   * Bypasses the forward-only round guard because the round itself
   * isn't moving here; only the cycle within an in-progress round is.
   */
  async updateCurrentCycle(learnerId, courseId, cycleIndex) {
    const { error } = await this.client.schema(this.schema).from("course_enrollments").update({
      current_cycle_index: cycleIndex,
      last_practiced_at: (/* @__PURE__ */ new Date()).toISOString()
    }).eq("learner_id", learnerId).eq("course_id", courseId);
    if (error) {
      throw new Error(`Failed to update current cycle: ${error.message}`);
    }
  }
  /**
   * Explicit cursor write for intentional navigation events (belt-back,
   * jump-to-furthest, jump-to-belt). Bypasses the max-only guard in
   * updateEnrollmentProgress because the user has chosen this position —
   * "I am now at round X" — even if X is behind their high-water mark.
   *
   * The DB trigger still preserves the ceiling: a backward write here
   * doesn't lower highest_completed_round_index, and a forward one doesn't
   * lower the ceiling either (it ratchets only up).
   */
  /**
   * Switch playback mode. infplay_round_index is a LIFETIME counter —
   * once it crosses 0 it never goes back. Semantics:
   *   - First-ever 'infplay': counter initialised to 1
   *   - Re-entering 'infplay' after a 'main' detour: counter unchanged
   *     (carries over)
   *   - 'main': counter NOT reset — preserves "has this learner ever
   *     been in INF PLAY" so jumpToFurthest and the purple pill can
   *     keep showing the count after a back-belt-skip out.
   *
   * Optional `ratchetHighestTo`: when entering INF PLAY, also ratchet
   * highest_completed_lego_id + highest_completed_round_index to the
   * given (legoId, roundIndex). Per Tom 2026-05-20: belt-skipping
   * past content IS the legitimate way to enter INF PLAY, so the
   * data model has to accept that. Entering INF PLAY = "I've reached
   * the end of the new content I want to deal with" — highest should
   * reflect that even if the learner didn't literally play every belt.
   * Forward-only via .or filter; existing higher values never regress.
   */
  async setMode(learnerId, courseId, mode, ratchetHighestTo) {
    const { data: beforeRows, error } = await this.client.schema(this.schema).from("course_enrollments").update({
      current_mode: mode,
      last_practiced_at: (/* @__PURE__ */ new Date()).toISOString()
    }).eq("learner_id", learnerId).eq("course_id", courseId).select("last_completed_lego_id, last_completed_round_index");
    if (error) {
      throw new Error(`Failed to set mode: ${error.message}`);
    }
    const before = Array.isArray(beforeRows) ? beforeRows[0] : null;
    if (mode === "infplay") {
      const { error: initErr } = await this.client.schema(this.schema).from("course_enrollments").update({ infplay_round_index: 1 }).eq("learner_id", learnerId).eq("course_id", courseId).or("infplay_round_index.is.null,infplay_round_index.eq.0");
      if (initErr) {
        throw new Error(`Failed to initialise infplay counter: ${initErr.message}`);
      }
      if (ratchetHighestTo) {
        const { data: ratchetedRows, error: rErr } = await this.client.schema(this.schema).from("course_enrollments").update({
          last_completed_lego_id: ratchetHighestTo.legoId
        }).eq("learner_id", learnerId).eq("course_id", courseId).or(`last_completed_lego_id.is.null,last_completed_lego_id.lt.${ratchetHighestTo.legoId}`).select("last_completed_lego_id, last_completed_round_index");
        if (rErr) {
          throw new Error(`Failed to ratchet last_lego on infplay entry: ${rErr.message}`);
        }
        const moved = Array.isArray(ratchetedRows) && ratchetedRows.length > 0;
        reportCursorMove({
          kind: "infplay_ratchet",
          fromLegoId: before?.last_completed_lego_id ?? null,
          fromRoundIndex: before?.last_completed_round_index ?? null,
          toLegoId: moved ? ratchetHighestTo.legoId : before?.last_completed_lego_id ?? null,
          toRoundIndex: before?.last_completed_round_index ?? null,
          moved
        });
      }
    }
  }
  /**
   * Increment infplay_round_index by 1 — call once per round_complete
   * while in INF PLAY mode. PostgREST has no "increment" verb; we read
   * the current value and write current+1. Race-condition safe enough
   * for single-device play (the only place writing).
   */
  async bumpInfplayRound(learnerId, courseId) {
    const { data, error: readErr } = await this.client.schema(this.schema).from("course_enrollments").select("current_mode, infplay_round_index").eq("learner_id", learnerId).eq("course_id", courseId).single();
    if (readErr) {
      throw new Error(`Failed to read enrollment for bump: ${readErr.message}`);
    }
    if (!data || data.current_mode !== "infplay") return;
    const next = (data.infplay_round_index ?? 0) + 1;
    const { error } = await this.client.schema(this.schema).from("course_enrollments").update({ infplay_round_index: next }).eq("learner_id", learnerId).eq("course_id", courseId);
    if (error) {
      throw new Error(`Failed to bump infplay round: ${error.message}`);
    }
  }
  async setEnrollmentCursor(learnerId, courseId, legoId, roundIndex, opts) {
    const { error } = await this.client.schema(this.schema).from("course_enrollments").update({
      last_completed_lego_id: legoId,
      last_completed_round_index: roundIndex,
      // Backwards regressions (e.g. resume TTL belt-reset) imply the
      // learner is restarting the round; carry-over cycle indices
      // belong to a different round and would mislead the resume
      // logic. Always reset on cursor set.
      current_cycle_index: 0,
      last_practiced_at: (/* @__PURE__ */ new Date()).toISOString()
    }).eq("learner_id", learnerId).eq("course_id", courseId);
    if (error) {
      throw new Error(`Failed to set enrollment cursor: ${error.message}`);
    }
    reportCursorMove({
      kind: "explicit_nav",
      fromLegoId: opts?.from?.legoId ?? null,
      fromRoundIndex: opts?.from?.roundIndex ?? null,
      toLegoId: legoId,
      toRoundIndex: roundIndex,
      moved: true,
      ...opts?.reason ? { reason: opts.reason } : {}
    });
  }
  /**
   * Live-position write — the cursor IS where the playhead is right now
   * (the round currently playing + the cycle within it). Written on every
   * cycle during normal forward play. This is the "position, not completion"
   * model: last_completed_* names a position, not an achievement, so resume
   * lands here directly with no "+1".
   *
   * Sets all three of legoId / roundIndex / cycleIndex to the LIVE values —
   * crucially it does NOT reset current_cycle_index (unlike setEnrollmentCursor),
   * so a mid-round resume position is never wiped.
   *
   * Not forward-only: a live position behind the stored one lands (a learner
   * who went back from the course end must keep their new place). The one
   * guard left is the round-0-overwrite protection: a round 0 write only
   * lands over a null or round-0 row; deliberate returns to the start go
   * through setEnrollmentCursor, which has no guard.
   */
  async setLivePosition(learnerId, courseId, legoId, roundIndex, cycleIndex, opts) {
    const updateData = {
      last_completed_lego_id: legoId,
      last_completed_round_index: roundIndex,
      current_cycle_index: cycleIndex
    };
    if (opts?.touchPracticedAt !== false) {
      updateData.last_practiced_at = (/* @__PURE__ */ new Date()).toISOString();
    }
    let query = this.client.schema(this.schema).from("course_enrollments").update(updateData).eq("learner_id", learnerId).eq("course_id", courseId);
    if (roundIndex <= 0) {
      query = query.or(`last_completed_round_index.is.null,last_completed_round_index.lte.${roundIndex}`);
    }
    const { error } = await query;
    if (error) {
      throw new Error(`Failed to set live position: ${error.message}`);
    }
  }
  /**
   * Fold a session's activity into the enrolment: a forward-only seed
   * watermark and an ADDED practice-minutes delta.
   *
   * TWO THINGS THE SHAPE OF THIS METHOD GUARANTEES, both of them learned the
   * hard way (job #450, after a cross-family check on #446):
   *
   * 1. IT REPORTS FAILURE. It returns false when the row was not written —
   *    a read it could not trust, or an update that errored. It used to
   *    console.warn and return void, so the caller's `.catch()` rollback of
   *    its own minutes watermark was dead code and a failed write silently
   *    dropped that delta for good.
   *
   * 2. IT DOES NOT LOSE A CONCURRENT DELTA. The read-then-add below is not
   *    atomic, so two overlapping calls from the same client (a pause tap
   *    while a visibilitychange checkpoint is still in flight) would both
   *    read the same baseline and one delta would vanish. Calls for the same
   *    learner+course are queued onto a per-enrolment promise chain, so each
   *    reads what the previous one wrote. This covers ONE client only; two
   *    devices practising the same course at the same instant can still lose
   *    a delta, which needs a server-side atomic increment to close.
   */
  async updateEnrollmentActivity(learnerId, courseId, highestSeed, practiceMinutes) {
    const key = `${learnerId}:${courseId}`;
    const previous = this.enrollmentActivityChain.get(key) ?? Promise.resolve();
    const run = previous.catch(() => {
    }).then(() => this.writeEnrollmentActivity(learnerId, courseId, highestSeed, practiceMinutes));
    const tail = run.then(() => {
    }, () => {
    });
    this.enrollmentActivityChain.set(key, tail);
    try {
      return await run;
    } finally {
      if (this.enrollmentActivityChain.get(key) === tail) {
        this.enrollmentActivityChain.delete(key);
      }
    }
  }
  async writeEnrollmentActivity(learnerId, courseId, highestSeed, practiceMinutes) {
    const { data: enrollment, error: readErr } = await this.client.schema(this.schema).from("course_enrollments").select("highest_completed_seed, total_practice_minutes").eq("learner_id", learnerId).eq("course_id", courseId).single();
    if (readErr && readErr.code !== "PGRST116") {
      console.warn(`Skipping enrollment activity update (read failed): ${readErr.message}`);
      return false;
    }
    const currentHighest = enrollment?.highest_completed_seed || 0;
    const currentMinutes = enrollment?.total_practice_minutes || 0;
    const { error } = await this.client.schema(this.schema).from("course_enrollments").update({
      highest_completed_seed: Math.max(currentHighest, highestSeed),
      total_practice_minutes: currentMinutes + practiceMinutes,
      last_practiced_at: (/* @__PURE__ */ new Date()).toISOString()
    }).eq("learner_id", learnerId).eq("course_id", courseId);
    if (error) {
      console.warn(`Failed to update enrollment activity: ${error.message}`);
      return false;
    }
    return true;
  }
  // ============================================
  // LEGO PROGRESS
  // ============================================
  async getLegoProgress(learnerId, courseId) {
    const { data, error } = await this.client.schema(this.schema).from("lego_progress").select("*").eq("learner_id", learnerId).eq("course_id", courseId);
    if (error) {
      throw new Error(`Failed to get LEGO progress: ${error.message}`);
    }
    return (data ?? []).map(this.mapToLegoProgressRecord);
  }
  async getLegoProgressById(learnerId, legoId, courseId) {
    const { data, error } = await this.client.schema(this.schema).from("lego_progress").select("*").eq("learner_id", learnerId).eq("lego_id", legoId).eq("course_id", courseId).single();
    if (error) {
      if (error.code === "PGRST116") return null;
      throw new Error(`Failed to get LEGO progress: ${error.message}`);
    }
    return this.mapToLegoProgressRecord(data);
  }
  async saveLegoProgress(progress) {
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const { data, error } = await this.client.schema(this.schema).from("lego_progress").insert({
      learner_id: progress.learner_id,
      lego_id: progress.lego_id,
      course_id: progress.course_id,
      thread_id: progress.thread_id,
      fibonacci_position: progress.fibonacci_position,
      skip_number: progress.skip_number,
      reps_completed: progress.reps_completed,
      is_retired: progress.is_retired,
      last_practiced_at: progress.last_practiced_at?.toISOString() ?? null,
      created_at: now,
      updated_at: now
    }).select().single();
    if (error) {
      throw new Error(`Failed to save LEGO progress: ${error.message}`);
    }
    return this.mapToLegoProgressRecord(data);
  }
  async updateLegoProgress(id, updates) {
    const updateData = {
      updated_at: (/* @__PURE__ */ new Date()).toISOString()
    };
    if (updates.fibonacci_position !== void 0) {
      updateData.fibonacci_position = updates.fibonacci_position;
    }
    if (updates.skip_number !== void 0) {
      updateData.skip_number = updates.skip_number;
    }
    if (updates.reps_completed !== void 0) {
      updateData.reps_completed = updates.reps_completed;
    }
    if (updates.is_retired !== void 0) {
      updateData.is_retired = updates.is_retired;
    }
    if (updates.last_practiced_at !== void 0) {
      updateData.last_practiced_at = updates.last_practiced_at?.toISOString() ?? null;
    }
    const { error } = await this.client.schema(this.schema).from("lego_progress").update(updateData).eq("id", id);
    if (error) {
      throw new Error(`Failed to update LEGO progress: ${error.message}`);
    }
  }
  async bulkUpdateLegoProgress(updates) {
    const promises = updates.map(
      ({ id, updates: u }) => this.updateLegoProgress(id, u)
    );
    await Promise.all(promises);
  }
  // ============================================
  // SEED PROGRESS
  // ============================================
  async getSeedProgress(learnerId, courseId) {
    const { data, error } = await this.client.schema(this.schema).from("seed_progress").select("*").eq("learner_id", learnerId).eq("course_id", courseId);
    if (error) {
      throw new Error(`Failed to get SEED progress: ${error.message}`);
    }
    return (data ?? []).map(this.mapToSeedProgressRecord);
  }
  async saveSeedProgress(progress) {
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const { data, error } = await this.client.schema(this.schema).from("seed_progress").insert({
      learner_id: progress.learner_id,
      seed_id: progress.seed_id,
      course_id: progress.course_id,
      thread_id: progress.thread_id,
      is_introduced: progress.is_introduced,
      introduced_at: progress.introduced_at?.toISOString() ?? null,
      created_at: now,
      updated_at: now
    }).select().single();
    if (error) {
      throw new Error(`Failed to save SEED progress: ${error.message}`);
    }
    return this.mapToSeedProgressRecord(data);
  }
  async updateSeedProgress(id, updates) {
    const updateData = {
      updated_at: (/* @__PURE__ */ new Date()).toISOString()
    };
    if (updates.is_introduced !== void 0) {
      updateData.is_introduced = updates.is_introduced;
    }
    if (updates.introduced_at !== void 0) {
      updateData.introduced_at = updates.introduced_at?.toISOString() ?? null;
    }
    const { error } = await this.client.schema(this.schema).from("seed_progress").update(updateData).eq("id", id);
    if (error) {
      throw new Error(`Failed to update SEED progress: ${error.message}`);
    }
  }
  // ============================================
  // LEARNER BASELINE (Calibration)
  // ============================================
  async getBaseline(learnerId, courseId) {
    const { data, error } = await this.client.schema(this.schema).from("learner_baselines").select("*").eq("learner_id", learnerId).eq("course_id", courseId).single();
    if (error) {
      if (error.code === "PGRST116") return null;
      throw new Error(`Failed to get learner baseline: ${error.message}`);
    }
    return this.mapToBaselineRecord(data);
  }
  async saveBaseline(learnerId, courseId, baseline) {
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const { data, error } = await this.client.schema(this.schema).from("learner_baselines").insert({
      learner_id: learnerId,
      course_id: courseId,
      calibrated_at: baseline.calibrated_at.toISOString(),
      calibration_items: baseline.calibration_items,
      latency_mean: baseline.latency.mean,
      latency_std_dev: baseline.latency.stdDev,
      duration_delta_mean: baseline.durationDelta.mean,
      duration_delta_std_dev: baseline.durationDelta.stdDev,
      had_timing_data: baseline.hadTimingData,
      metadata: baseline.metadata ?? null,
      created_at: now,
      updated_at: now
    }).select().single();
    if (error) {
      throw new Error(`Failed to save learner baseline: ${error.message}`);
    }
    return this.mapToBaselineRecord(data);
  }
  async updateBaseline(learnerId, courseId, baseline) {
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const { data, error } = await this.client.schema(this.schema).from("learner_baselines").update({
      calibrated_at: baseline.calibrated_at.toISOString(),
      calibration_items: baseline.calibration_items,
      latency_mean: baseline.latency.mean,
      latency_std_dev: baseline.latency.stdDev,
      duration_delta_mean: baseline.durationDelta.mean,
      duration_delta_std_dev: baseline.durationDelta.stdDev,
      had_timing_data: baseline.hadTimingData,
      metadata: baseline.metadata ?? null,
      updated_at: now
    }).eq("learner_id", learnerId).eq("course_id", courseId).select().single();
    if (error) {
      throw new Error(`Failed to update learner baseline: ${error.message}`);
    }
    return this.mapToBaselineRecord(data);
  }
  // ============================================
  // MAPPERS
  // ============================================
  mapToLearnerRecord(data) {
    return {
      id: data.id,
      user_id: data.user_id,
      display_name: data.display_name,
      created_at: new Date(data.created_at),
      updated_at: new Date(data.updated_at),
      preferences: data.preferences
    };
  }
  mapToEnrollmentRecord(data) {
    return mapEnrollmentRow(data);
  }
  mapToLegoProgressRecord(data) {
    return {
      id: data.id,
      learner_id: data.learner_id,
      lego_id: data.lego_id,
      course_id: data.course_id,
      thread_id: data.thread_id,
      fibonacci_position: data.fibonacci_position,
      skip_number: data.skip_number,
      reps_completed: data.reps_completed,
      is_retired: data.is_retired,
      last_practiced_at: data.last_practiced_at ? new Date(data.last_practiced_at) : null,
      // ROUND tracking
      introduction_played: data.introduction_played ?? false,
      introduction_index: data.introduction_index ?? 0,
      introduction_complete: data.introduction_complete ?? false,
      // Eternal selection
      eternal_urn: data.eternal_urn ?? [],
      last_eternal_phrase_id: data.last_eternal_phrase_id ?? null,
      created_at: new Date(data.created_at),
      updated_at: new Date(data.updated_at)
    };
  }
  mapToSeedProgressRecord(data) {
    return {
      id: data.id,
      learner_id: data.learner_id,
      seed_id: data.seed_id,
      course_id: data.course_id,
      thread_id: data.thread_id,
      is_introduced: data.is_introduced,
      introduced_at: data.introduced_at ? new Date(data.introduced_at) : null,
      created_at: new Date(data.created_at),
      updated_at: new Date(data.updated_at)
    };
  }
  mapToBaselineRecord(data) {
    return {
      id: data.id,
      learner_id: data.learner_id,
      course_id: data.course_id,
      calibrated_at: new Date(data.calibrated_at),
      calibration_items: data.calibration_items,
      latency: {
        mean: data.latency_mean,
        stdDev: data.latency_std_dev
      },
      durationDelta: {
        mean: data.duration_delta_mean,
        stdDev: data.duration_delta_std_dev
      },
      hadTimingData: data.had_timing_data,
      metadata: data.metadata,
      created_at: new Date(data.created_at),
      updated_at: new Date(data.updated_at)
    };
  }
};
function createProgressStore(config) {
  return new ProgressStore(config);
}
function mapEnrollmentRow(data) {
  return {
    id: data.id,
    learner_id: data.learner_id,
    course_id: data.course_id,
    enrolled_at: new Date(data.enrolled_at),
    last_practiced_at: data.last_practiced_at ? new Date(data.last_practiced_at) : null,
    total_practice_minutes: data.total_practice_minutes,
    helix_state: data.helix_state,
    last_completed_lego_id: data.last_completed_lego_id ?? null,
    last_completed_round_index: data.last_completed_round_index ?? null,
    current_cycle_index: data.current_cycle_index ?? null,
    highest_completed_round_index: data.highest_completed_round_index ?? null,
    highest_completed_lego_id: data.highest_completed_lego_id ?? null,
    current_mode: data.current_mode === "infplay" ? "infplay" : "main",
    infplay_round_index: data.infplay_round_index ?? 0
  };
}

// src/persistence/SessionStore.ts
var SessionStore = class {
  client;
  schema;
  constructor(config) {
    this.client = config.client;
    this.schema = config.schema ?? "public";
  }
  // ============================================
  // SESSION MANAGEMENT
  // ============================================
  async startSession(learnerId, courseId) {
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const { data, error } = await this.client.schema(this.schema).from("sessions").insert({
      learner_id: learnerId,
      course_id: courseId,
      started_at: now,
      ended_at: null,
      duration_seconds: 0,
      items_practiced: 0,
      spikes_detected: 0,
      final_rolling_average: 0
    }).select().single();
    if (error) {
      throw new Error(`Failed to start session: ${error.message}`);
    }
    return this.mapToSessionRecord(data);
  }
  /**
   * Close a session row.
   *
   * `playSeconds` is REQUIRED and is the only accepted source of
   * duration_seconds. Time counts as in-app time when the app is PLAYING —
   * including listening — and does not count when it is not (owner ruling,
   * 2026-08-19). The caller measures that directly by accumulating segments
   * between playback start and stop; this method must never re-derive it.
   *
   * This used to compute `ended_at - started_at` instead, which is wall-clock
   * and counts every minute the tab sat idle. It also OVERWROTE the correct
   * play-seconds value that checkpointSession had already written. That is
   * what produced sessions claiming 128 hours with items_practiced = 0 — a
   * timer that was never closed, not a learner who studied for five days.
   * There is deliberately no wall-clock fallback: the parameter is required
   * so the old behaviour cannot creep back in as a default.
   */
  async endSession(sessionId, metrics, playSeconds) {
    const endedAt = metrics.ended_at ?? /* @__PURE__ */ new Date();
    const durationSeconds = Math.max(0, Math.floor(playSeconds));
    const { data, error } = await this.client.schema(this.schema).from("sessions").update({
      ended_at: endedAt.toISOString(),
      duration_seconds: durationSeconds,
      items_practiced: metrics.items_practiced,
      spikes_detected: metrics.spikes_detected,
      final_rolling_average: metrics.final_rolling_average
    }).eq("id", sessionId).select().single();
    if (error) {
      throw new Error(`Failed to end session: ${error.message}`);
    }
    return this.mapToSessionRecord(data);
  }
  async checkpointSession(sessionId, itemsPracticed, durationSeconds, onFailure) {
    const { error } = await this.client.schema(this.schema).from("sessions").update({
      items_practiced: itemsPracticed,
      duration_seconds: durationSeconds,
      ended_at: (/* @__PURE__ */ new Date()).toISOString()
      // Always update ended_at so we have a timestamp even if tab closes
    }).eq("id", sessionId);
    if (error) {
      console.error("[SessionStore] checkpointSession FAILED:", {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        sessionId,
        itemsPracticed,
        durationSeconds
      });
      try {
        onFailure?.({ message: String(error.message ?? ""), code: error.code });
      } catch {
      }
    }
  }
  /**
   * Bump the per-learner-per-course-per-UTC-day speaking opportunities
   * counter. Called per cycle (oppsDelta=1) and periodically with
   * accumulated play time delta. Fire-and-forget — must never block
   * playback. Errors are logged with full Postgres detail for diagnosis.
   *
   * Replaces the old sessions.items_practiced / duration_seconds path,
   * which silently failed when sessionStore was null at init.
   */
  async bumpSpeakingOpportunities(learnerId, courseCode, oppsDelta, secondsDelta) {
    if (oppsDelta <= 0 && secondsDelta <= 0) return;
    const { error } = await this.client.rpc("bump_speaking_opportunities", {
      p_learner_id: learnerId,
      p_course_code: courseCode,
      p_opps_delta: oppsDelta,
      p_seconds_delta: secondsDelta
    });
    if (error) {
      console.error("[SessionStore] bumpSpeakingOpportunities FAILED:", {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        learnerId,
        courseCode,
        oppsDelta,
        secondsDelta
      });
    }
  }
  async getSession(sessionId) {
    const { data, error } = await this.client.schema(this.schema).from("sessions").select("*").eq("id", sessionId).single();
    if (error) {
      if (error.code === "PGRST116") return null;
      throw new Error(`Failed to get session: ${error.message}`);
    }
    return this.mapToSessionRecord(data);
  }
  async getRecentSessions(learnerId, limit = 10) {
    const { data, error } = await this.client.schema(this.schema).from("sessions").select("*").eq("learner_id", learnerId).order("started_at", { ascending: false }).limit(limit);
    if (error) {
      throw new Error(`Failed to get recent sessions: ${error.message}`);
    }
    return (data ?? []).map(this.mapToSessionRecord);
  }
  // ============================================
  // METRICS
  // ============================================
  async saveMetrics(sessionId, metrics) {
    if (metrics.length === 0) return;
    const session = await this.getSession(sessionId);
    if (!session) {
      throw new Error(`Session not found: ${sessionId}`);
    }
    const records = metrics.map((m) => ({
      id: m.id,
      session_id: sessionId,
      learner_id: session.learner_id,
      course_id: session.course_id,
      lego_id: m.lego_id,
      timestamp: m.timestamp.toISOString(),
      response_latency_ms: m.response_latency_ms,
      phrase_length: m.phrase_length,
      normalized_latency: m.normalized_latency,
      thread_id: m.thread_id,
      triggered_spike: m.triggered_spike,
      mode: m.mode
    }));
    const { error } = await this.client.schema(this.schema).from("response_metrics").insert(records);
    if (error) {
      throw new Error(`Failed to save metrics: ${error.message}`);
    }
  }
  async saveSpikes(sessionId, spikes) {
    if (spikes.length === 0) return;
    const session = await this.getSession(sessionId);
    if (!session) {
      throw new Error(`Session not found: ${sessionId}`);
    }
    const records = spikes.map((s) => ({
      id: s.id,
      session_id: sessionId,
      learner_id: session.learner_id,
      course_id: session.course_id,
      lego_id: s.lego_id,
      timestamp: s.timestamp.toISOString(),
      latency: s.latency,
      rolling_average: s.rolling_average,
      spike_ratio: s.spike_ratio,
      response: s.response,
      thread_id: s.thread_id
    }));
    const { error } = await this.client.schema(this.schema).from("spike_events").insert(records);
    if (error) {
      throw new Error(`Failed to save spikes: ${error.message}`);
    }
  }
  async getSessionMetrics(sessionId) {
    const { data, error } = await this.client.schema(this.schema).from("response_metrics").select("*").eq("session_id", sessionId).order("timestamp", { ascending: true });
    if (error) {
      throw new Error(`Failed to get session metrics: ${error.message}`);
    }
    return (data ?? []).map(this.mapToMetricRecord);
  }
  async getSessionSpikes(sessionId) {
    const { data, error } = await this.client.schema(this.schema).from("spike_events").select("*").eq("session_id", sessionId).order("timestamp", { ascending: true });
    if (error) {
      throw new Error(`Failed to get session spikes: ${error.message}`);
    }
    return (data ?? []).map(this.mapToSpikeRecord);
  }
  // ============================================
  // MAPPERS
  // ============================================
  mapToSessionRecord(data) {
    return {
      id: data.id,
      learner_id: data.learner_id,
      course_id: data.course_id,
      started_at: new Date(data.started_at),
      ended_at: data.ended_at ? new Date(data.ended_at) : null,
      duration_seconds: data.duration_seconds,
      items_practiced: data.items_practiced,
      spikes_detected: data.spikes_detected,
      final_rolling_average: data.final_rolling_average
    };
  }
  mapToMetricRecord(data) {
    return {
      db_id: data.db_id,
      id: data.id,
      session_id: data.session_id,
      learner_id: data.learner_id,
      course_id: data.course_id,
      lego_id: data.lego_id,
      timestamp: new Date(data.timestamp),
      response_latency_ms: data.response_latency_ms,
      phrase_length: data.phrase_length,
      normalized_latency: data.normalized_latency,
      thread_id: data.thread_id,
      triggered_spike: data.triggered_spike,
      mode: data.mode
    };
  }
  mapToSpikeRecord(data) {
    return {
      db_id: data.db_id,
      id: data.id,
      session_id: data.session_id,
      learner_id: data.learner_id,
      course_id: data.course_id,
      lego_id: data.lego_id,
      timestamp: new Date(data.timestamp),
      latency: data.latency,
      rolling_average: data.rolling_average,
      spike_ratio: data.spike_ratio,
      response: data.response,
      thread_id: data.thread_id
    };
  }
};
function createSessionStore(config) {
  return new SessionStore(config);
}

// src/persistence/SyncService.ts
var SyncService = class {
  client;
  schema;
  maxRetries;
  baseDelayMs;
  autoSyncIntervalMs;
  queue = [];
  isSyncing = false;
  autoSyncTimer = null;
  lastSyncAt = null;
  lastError = null;
  onCompleteCallbacks = [];
  onErrorCallbacks = [];
  syncErrorsLogged = /* @__PURE__ */ new Set();
  constructor(config) {
    this.client = config.client;
    this.schema = config.schema ?? "public";
    this.maxRetries = config.maxRetries ?? 3;
    this.baseDelayMs = config.baseDelayMs ?? 1e3;
    this.autoSyncIntervalMs = config.autoSyncIntervalMs ?? 3e4;
  }
  // ============================================
  // QUEUE MANAGEMENT
  // ============================================
  async queueOperation(entity, operation, entityId, payload) {
    const item = {
      id: `sync-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      entity,
      operation,
      entity_id: entityId,
      payload,
      created_at: /* @__PURE__ */ new Date(),
      attempts: 0,
      last_attempt_at: null,
      error: null
    };
    this.queue.push(item);
    await this.persistQueue();
  }
  async processQueue() {
    if (this.isSyncing) {
      return 0;
    }
    if (this.queue.length === 0) {
      return 0;
    }
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      return 0;
    }
    this.isSyncing = true;
    let processedCount = 0;
    try {
      const itemsToProcess = [...this.queue];
      const blockedRecords = /* @__PURE__ */ new Set();
      for (const item of itemsToProcess) {
        const recordKey = `${item.entity}:${item.entity_id}`;
        if (blockedRecords.has(recordKey)) {
          continue;
        }
        if (item.last_attempt_at) {
          const delay = this.baseDelayMs * Math.pow(2, item.attempts - 1);
          if (Date.now() - item.last_attempt_at.getTime() < delay) {
            blockedRecords.add(recordKey);
            continue;
          }
        }
        try {
          await this.processItem(item);
          this.removeFromQueue(item.id);
          processedCount++;
        } catch (error) {
          item.attempts++;
          item.last_attempt_at = /* @__PURE__ */ new Date();
          item.error = error instanceof Error ? error.message : String(error);
          if (item.attempts >= this.maxRetries) {
            const errorKey = `max-retries-${item.entity}`;
            if (!this.syncErrorsLogged.has(errorKey)) {
              this.syncErrorsLogged.add(errorKey);
              console.error(`[SyncService] Sync item ${item.id} (${item.entity}) exceeded max retries:`, error);
            }
            this.removeFromQueue(item.id);
          } else {
            blockedRecords.add(recordKey);
            const delay = this.baseDelayMs * Math.pow(2, item.attempts - 1);
            const warnKey = `retry-${item.entity}`;
            if (!this.syncErrorsLogged.has(warnKey)) {
              this.syncErrorsLogged.add(warnKey);
              console.warn(`[SyncService] Sync item ${item.id} (${item.entity}) failed, retry in ${delay}ms`);
            }
          }
        }
      }
      this.lastSyncAt = /* @__PURE__ */ new Date();
      this.lastError = null;
      await this.persistQueue();
      if (processedCount > 0) {
        this.onCompleteCallbacks.forEach((cb) => cb(processedCount));
      }
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : String(error);
      this.onErrorCallbacks.forEach(
        (cb) => cb(error instanceof Error ? error : new Error(String(error)))
      );
    } finally {
      this.isSyncing = false;
    }
    return processedCount;
  }
  async getQueueStatus() {
    return {
      pending_count: this.queue.length,
      last_sync_at: this.lastSyncAt,
      is_syncing: this.isSyncing,
      last_error: this.lastError
    };
  }
  async clearQueue() {
    this.queue = [];
    await this.persistQueue();
  }
  // ============================================
  // SYNC CONTROL
  // ============================================
  startAutoSync(intervalMs) {
    if (this.autoSyncTimer) {
      this.stopAutoSync();
    }
    const interval = intervalMs ?? this.autoSyncIntervalMs;
    this.autoSyncTimer = setInterval(() => {
      this.processQueue().catch((error) => {
        if (!this.syncErrorsLogged.has("auto-sync")) {
          this.syncErrorsLogged.add("auto-sync");
          console.error("[SyncService] Auto-sync error:", error);
        }
      });
    }, interval);
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.handleOnline);
    }
  }
  stopAutoSync() {
    if (this.autoSyncTimer) {
      clearInterval(this.autoSyncTimer);
      this.autoSyncTimer = null;
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.handleOnline);
    }
  }
  async forcSync() {
    await this.processQueue();
  }
  // ============================================
  // EVENTS
  // ============================================
  onSyncComplete(callback) {
    this.onCompleteCallbacks.push(callback);
  }
  onSyncError(callback) {
    this.onErrorCallbacks.push(callback);
  }
  // ============================================
  // INTERNAL METHODS
  // ============================================
  handleOnline = () => {
    this.processQueue().catch((error) => {
      if (!this.syncErrorsLogged.has("online-sync")) {
        this.syncErrorsLogged.add("online-sync");
        console.error("[SyncService] Online sync error:", error);
      }
    });
  };
  async processItem(item) {
    const tableName = this.getTableName(item.entity);
    switch (item.operation) {
      case "create": {
        const { error } = await this.client.schema(this.schema).from(tableName).insert(item.payload);
        if (error) {
          if (error.code === "23505") {
            return;
          }
          throw new Error(`Create failed: ${error.message}`);
        }
        break;
      }
      case "update": {
        const payload = item.payload;
        const { error } = await this.client.schema(this.schema).from(tableName).update(payload).eq("id", item.entity_id);
        if (error) {
          throw new Error(`Update failed: ${error.message}`);
        }
        break;
      }
      case "delete": {
        const { error } = await this.client.schema(this.schema).from(tableName).delete().eq("id", item.entity_id);
        if (error) {
          throw new Error(`Delete failed: ${error.message}`);
        }
        break;
      }
    }
  }
  getTableName(entity) {
    const tableMap = {
      lego_progress: "lego_progress",
      seed_progress: "seed_progress",
      session: "sessions",
      metric: "response_metrics",
      spike: "spike_events",
      enrollment: "course_enrollments",
      learner_baseline: "learner_baselines"
    };
    return tableMap[entity];
  }
  removeFromQueue(id) {
    this.queue = this.queue.filter((item) => item.id !== id);
  }
  async persistQueue() {
    if (typeof indexedDB === "undefined") return;
    let db;
    try {
      db = await this.openSyncDb();
      const tx = db.transaction("sync_queue", "readwrite");
      const store = tx.objectStore("sync_queue");
      await store.clear();
      for (const item of this.queue) {
        await store.add(item);
      }
    } catch (error) {
      if (!this.syncErrorsLogged.has("persist-queue")) {
        this.syncErrorsLogged.add("persist-queue");
        console.error("[SyncService] Failed to persist sync queue:", error);
      }
    } finally {
      db?.close();
    }
  }
  async loadQueue() {
    if (typeof indexedDB === "undefined") return;
    let db;
    try {
      db = await this.openSyncDb();
      const items = await this.getAllFromStore(db);
      this.queue = items.map((item) => ({
        ...item,
        created_at: new Date(item.created_at),
        last_attempt_at: item.last_attempt_at ? new Date(item.last_attempt_at) : null
      }));
    } catch (error) {
      if (!this.syncErrorsLogged.has("load-queue")) {
        this.syncErrorsLogged.add("load-queue");
        console.error("[SyncService] Failed to load sync queue:", error);
      }
    } finally {
      db?.close();
    }
  }
  getAllFromStore(db) {
    return new Promise((resolve, reject) => {
      const tx = db.transaction("sync_queue", "readonly");
      const store = tx.objectStore("sync_queue");
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  async openSyncDb() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open("ssi-sync-queue", 1);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains("sync_queue")) {
          db.createObjectStore("sync_queue", { keyPath: "id" });
        }
      };
    });
  }
};
function createSyncService(config) {
  return new SyncService(config);
}

// src/persistence/LegoMetricsStore.ts
var LegoMetricsStore = class {
  client;
  schema;
  constructor(config) {
    this.client = config.client;
    this.schema = config.schema ?? "public";
  }
  async loadAll(learnerId, courseCode) {
    const { data, error } = await this.client.schema(this.schema).from("learner_lego_metrics").select("*").eq("learner_id", learnerId).eq("course_code", courseCode);
    if (error) {
      throw new Error(`Failed to load lego metrics: ${error.message}`);
    }
    return (data ?? []).map((row) => ({
      learner_id: row.learner_id,
      lego_id: row.lego_id,
      course_code: row.course_code,
      mastery_state: row.mastery_state,
      consecutive_smooth: row.consecutive_smooth,
      consecutive_fast: row.consecutive_fast,
      n_samples: row.n_samples,
      // Defensive: these columns may be absent (pre-migration) — default safely.
      mean_latency_ms: row.mean_latency_ms ?? null,
      recent_latency_samples: Array.isArray(row.recent_latency_samples) ? row.recent_latency_samples : [],
      evidence_series: row.evidence_series && Array.isArray(row.evidence_series.values) ? row.evidence_series : void 0,
      last_seen_at: new Date(row.last_seen_at),
      updated_at: new Date(row.updated_at)
    }));
  }
  async upsertMany(rows) {
    if (rows.length === 0) return;
    const payload = rows.map((r) => ({
      learner_id: r.learner_id,
      lego_id: r.lego_id,
      course_code: r.course_code,
      mastery_state: r.mastery_state,
      consecutive_smooth: r.consecutive_smooth,
      consecutive_fast: r.consecutive_fast,
      n_samples: r.n_samples,
      last_seen_at: r.last_seen_at.toISOString()
    }));
    const { error } = await this.client.schema(this.schema).from("learner_lego_metrics").upsert(payload, { onConflict: "learner_id,lego_id" });
    if (error) {
      throw new Error(`Failed to upsert lego metrics: ${error.message}`);
    }
  }
  /**
   * Persist the per-(learner, lego) difficulty SERIES (B4). Call AFTER
   * `upsertMany` for the same legos so the row exists — this upsert touches only
   * the series columns and relies on the conflict→update path. Isolated from the
   * mastery write on purpose (see `LegoSeriesUpsert`).
   */
  async upsertSeries(rows) {
    if (rows.length === 0) return;
    const payload = rows.map((r) => ({
      learner_id: r.learner_id,
      lego_id: r.lego_id,
      course_code: r.course_code,
      recent_latency_samples: r.recent_latency_samples,
      mean_latency_ms: r.mean_latency_ms
    }));
    const { error } = await this.client.schema(this.schema).from("learner_lego_metrics").upsert(payload, { onConflict: "learner_id,lego_id" });
    if (error) {
      throw new Error(`Failed to upsert lego series: ${error.message}`);
    }
  }
  /**
   * Persist the per-(learner, lego) SHARED EVIDENCE AGGREGATOR series
   * (adaptation v2, WP-4). Call AFTER `upsertMany` for the same legos, same
   * reasoning as `upsertSeries` above. Isolated so a missing `evidence_series`
   * column never regresses mastery or B4-series persistence.
   */
  async upsertEvidenceSeries(rows) {
    if (rows.length === 0) return;
    const payload = rows.map((r) => ({
      learner_id: r.learner_id,
      lego_id: r.lego_id,
      course_code: r.course_code,
      evidence_series: r.evidence_series
    }));
    const { error } = await this.client.schema(this.schema).from("learner_lego_metrics").upsert(payload, { onConflict: "learner_id,lego_id" });
    if (error) {
      throw new Error(`Failed to upsert lego evidence series: ${error.message}`);
    }
  }
};

// src/persistence/PodStateStore.ts
var PodStateStore = class {
  client;
  schema;
  constructor(config) {
    this.client = config.client;
    this.schema = config.schema ?? "public";
  }
  /** Load every sentence exposure count for this learner + course.
   *  Returns a Map keyed by sentence_id. */
  async loadAll(learnerId, courseCode) {
    const { data, error } = await this.client.schema(this.schema).from("learner_pod_state").select("sentence_id, exposures").eq("learner_id", learnerId).eq("course_code", courseCode);
    if (error) {
      throw new Error(`Failed to load pod state: ${error.message}`);
    }
    const map = /* @__PURE__ */ new Map();
    for (const row of data ?? []) {
      if (typeof row.sentence_id === "string" && typeof row.exposures === "number") {
        map.set(row.sentence_id, row.exposures);
      }
    }
    return map;
  }
  /** Upsert a batch of (learner, course, sentence_id) → exposures rows. */
  async upsertMany(rows) {
    if (rows.length === 0) return;
    const { error } = await this.client.schema(this.schema).from("learner_pod_state").upsert(rows, { onConflict: "learner_id,course_code,sentence_id" });
    if (error) {
      throw new Error(`Failed to upsert pod state: ${error.message}`);
    }
  }
  /** Delete every row for this learner + course (course reset). */
  async deleteAll(learnerId, courseCode) {
    const { error } = await this.client.schema(this.schema).from("learner_pod_state").delete().eq("learner_id", learnerId).eq("course_code", courseCode);
    if (error) {
      throw new Error(`Failed to delete pod state: ${error.message}`);
    }
  }
};

// src/audio/types.ts
var DEFAULT_CONTINUOUS_VAD_CONFIG = {
  energy_threshold_db: -45,
  min_frames_above: 3,
  fft_size: 2048,
  smoothing: 0.8,
  min_speech_duration_ms: 100,
  speech_end_debounce_ms: 200,
  adaptive_floor_enabled: true,
  calibration_window_ms: 400,
  calibration_min_samples: 6,
  calibration_percentile: 0.25,
  adaptive_margin_db: 9,
  post_voice1_grace_ms: 1500,
  prompt_boundary_guard_ms: 250
};
function createEmptySpeechTimingResult(promptEndMs, voice1StartMs) {
  return {
    prompt_start_ms: 0,
    prompt_end_ms: promptEndMs,
    voice1_start_ms: voice1StartMs,
    speech_start_ms: null,
    speech_end_ms: null,
    response_latency_ms: null,
    learner_duration_ms: null,
    duration_delta_ms: null,
    started_during_prompt: false,
    still_speaking_at_voice1: false,
    speech_detected: false,
    peak_energy_db: -100,
    average_energy_db: -100
  };
}

// src/audio/envelope-extractor-v1.json
var envelope_extractor_v1_default = {
  version: 1,
  gridMs: 20,
  smoothingTaps: 3,
  smoothingWindowMs: 60,
  peakProminenceRatio: 0.25,
  peakMinSeparationMs: 120,
  minSampleCount: 10
};

// src/audio/envelopeMetadata.ts
var ENVELOPE_EXTRACTOR_CONSTANTS = envelope_extractor_v1_default;
var CONTOUR_MAX_POINTS = 128;
function compactContour(envelope, gridMs, maxPoints = CONTOUR_MAX_POINTS) {
  const n = envelope.length;
  const points = Math.min(n, maxPoints);
  const contourGridMs = n * gridMs / points;
  const max = Math.max(...envelope);
  const contour = new Array(points).fill(0);
  if (max <= 0) return { contour, contourGridMs };
  for (let p = 0; p < points; p++) {
    const start = Math.floor(p * n / points);
    const end = Math.max(start + 1, Math.floor((p + 1) * n / points));
    let sum = 0;
    for (let i = start; i < end; i++) sum += envelope[i];
    contour[p] = Math.round(sum / (end - start) / max * 100);
  }
  return { contour, contourGridMs };
}
var emptyMetadata = (durationMs, sampleCount) => ({
  durationMs,
  peakCount: 0,
  peakToMeanRatio: 0,
  meanPeakWidthMs: 0,
  sampleCount,
  weight: 0
});
function resampleToGrid(samples, gridMs) {
  if (samples.length === 0) return new Float64Array(0);
  const sorted = [...samples].sort((a, b) => a.t - b.t);
  const start = sorted[0].t;
  const end = sorted[sorted.length - 1].t;
  const numPoints = Math.max(1, Math.floor((end - start) / gridMs) + 1);
  const out = new Float64Array(numPoints);
  let lo = 0;
  for (let g = 0; g < numPoints; g++) {
    const t = start + g * gridMs;
    while (lo < sorted.length - 2 && sorted[lo + 1].t < t) lo++;
    const a = sorted[lo];
    const b = sorted[Math.min(lo + 1, sorted.length - 1)];
    const dB = a.t === b.t ? a.db : a.db + (b.db - a.db) * (t - a.t) / (b.t - a.t);
    out[g] = Math.pow(10, dB / 20);
  }
  return out;
}
function movingAverage(series, taps) {
  const half = Math.floor(taps / 2);
  const out = new Float64Array(series.length);
  for (let i = 0; i < series.length; i++) {
    let sum = 0;
    let count = 0;
    for (let k = -half; k <= half; k++) {
      const j = i + k;
      if (j >= 0 && j < series.length) {
        sum += series[j];
        count++;
      }
    }
    out[i] = sum / count;
  }
  return out;
}
function prominenceOf(series, i) {
  const v = series[i];
  let leftMin = v;
  for (let j = i - 1; j >= 0; j--) {
    if (series[j] > v) break;
    if (series[j] < leftMin) leftMin = series[j];
  }
  let rightMin = v;
  for (let j = i + 1; j < series.length; j++) {
    if (series[j] > v) break;
    if (series[j] < rightMin) rightMin = series[j];
  }
  return v - Math.max(leftMin, rightMin);
}
function widthAt(series, i, threshold) {
  let left = i;
  while (left > 0 && series[left - 1] >= threshold) left--;
  let right = i;
  while (right < series.length - 1 && series[right + 1] >= threshold) right++;
  return right - left;
}
function findLocalMaxima(series) {
  const maxima = [];
  for (let i = 0; i < series.length; i++) {
    const prev = i === 0 ? -Infinity : series[i - 1];
    const next = i === series.length - 1 ? -Infinity : series[i + 1];
    if (series[i] >= prev && series[i] >= next && (series[i] > prev || series[i] > next)) {
      maxima.push(i);
    }
  }
  return maxima;
}
function extractEnvelopeMetadata(samples, durationMs, constants = ENVELOPE_EXTRACTOR_CONSTANTS) {
  const { gridMs, smoothingTaps, peakProminenceRatio, peakMinSeparationMs, minSampleCount } = constants;
  const rawEnvelope = resampleToGrid(samples, gridMs);
  const sampleCount = rawEnvelope.length;
  if (sampleCount < minSampleCount) {
    return emptyMetadata(durationMs, sampleCount);
  }
  const envelope = movingAverage(rawEnvelope, smoothingTaps);
  const max = Math.max(...envelope);
  const mean2 = envelope.reduce((a, b) => a + b, 0) / envelope.length;
  const peakToMeanRatio = mean2 > 0 ? max / mean2 : 0;
  const prominenceThreshold = peakProminenceRatio * (max - mean2);
  const candidates = prominenceThreshold <= 0 ? [] : findLocalMaxima(envelope).map((i) => ({ i, prominence: prominenceOf(envelope, i) })).filter((c) => c.prominence >= prominenceThreshold).sort((a, b) => envelope[b.i] - envelope[a.i]);
  const minSeparationSteps = peakMinSeparationMs / gridMs;
  const accepted = [];
  for (const c of candidates) {
    if (accepted.every((a) => Math.abs(a.i - c.i) >= minSeparationSteps)) accepted.push(c);
  }
  accepted.sort((a, b) => a.i - b.i);
  const widths = accepted.map((c) => widthAt(envelope, c.i, envelope[c.i] - c.prominence / 2) * gridMs);
  const meanPeakWidthMs = widths.length ? widths.reduce((a, b) => a + b, 0) / widths.length : 0;
  const { contour, contourGridMs } = compactContour(envelope, gridMs);
  return {
    durationMs,
    peakCount: accepted.length,
    peakToMeanRatio,
    meanPeakWidthMs,
    sampleCount,
    weight: 1,
    contour,
    contourGridMs
  };
}

// src/audio/VoiceActivityDetector.ts
var VoiceActivityDetector = class {
  config;
  audioContext = null;
  analyser = null;
  mediaStream = null;
  sourceNode = null;
  // Monitoring state
  isMonitoring = false;
  monitoringStartTime = 0;
  rafId = null;
  // Accumulated metrics during monitoring
  energySamples = [];
  speechFrameCount = 0;
  consecutiveAboveThreshold = 0;
  peakEnergy = -Infinity;
  totalSpeechDuration = 0;
  lastSpeechStartTime = null;
  // Continuous monitoring state (for timing analysis)
  isContinuousMode = false;
  continuousConfig = DEFAULT_CONTINUOUS_VAD_CONFIG;
  continuousStartTime = 0;
  phaseTimestamps = /* @__PURE__ */ new Map();
  firstSpeechStartAbsolute = null;
  // Absolute time of first speech start
  lastSpeechEndAbsolute = null;
  // Absolute time of last speech end
  speechEndDebounceTimer = null;
  confirmedSpeechEnd = null;
  // Confirmed end (after debounce)
  // Timestamped energy samples for continuous mode ONLY (adaptation v2 WP-6).
  // rAF cadence is display-locked/throttle-prone, so envelope extraction needs
  // timestamps, not just values. Never leaves this class — stopContinuousMonitoring
  // consumes it into EnvelopeMetadata (5 numbers) and discards it immediately.
  continuousEnergyTimeline = [];
  // Playback-rejection state (continuous mode only, 2026-08-20).
  // See ContinuousVADConfig for why the gate has to be relative.
  calibrationSamples = [];
  calibrationClosed = false;
  /** max(absolute threshold, measured floor + margin); null until calibrated */
  adaptiveThresholdDb = null;
  /** Start of an above-floor burst not yet sustained long enough to count.
   *  Re-armable: a burst that dies before min_speech_duration_ms is discarded,
   *  so a transient can no longer own the whole cycle's measurement. */
  candidateSpeechStart = null;
  /** Has the pending candidate stayed up for min_speech_duration_ms? Once it
   *  has, it is held until the prompt-end boundary can judge it, rather than
   *  being dropped the moment its run ends. */
  candidateSustained = false;
  /** Absolute time the pending candidate's run fell back to the floor; null
   *  while it is still above. A prompt-window candidate is judged on WHERE
   *  its run ended relative to PROMPT_END, and PROMPT_END is usually marked
   *  after the run has already ended, so that moment has to be remembered. */
  candidateRunEnd = null;
  /** Bumped by every dispose(); initialize() captures it before awaiting
   *  getUserMedia, so a dispose() that lands while the permission prompt is
   *  still up can be seen after the await. Without it the stream resolves into
   *  an owner that is already gone: the tracks are never stopped, the mic stays
   *  open, and on Android that alone holds the device in communication mode and
   *  kills Bluetooth A2DP media (learner report, 2026-09-22). */
  disposeGeneration = 0;
  constructor(config = {}) {
    this.config = { ...DEFAULT_VAD_CONFIG, ...config };
  }
  /**
   * Initialize VAD with microphone access
   * MUST be called from a user gesture (click, touch, etc.)
   * @returns true if initialization successful, false otherwise
   */
  async initialize() {
    if (this.isInitialized()) return true;
    if (typeof window === "undefined") {
      console.warn("VoiceActivityDetector: Not in browser environment");
      return false;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      console.warn("VoiceActivityDetector: getUserMedia not supported");
      return false;
    }
    try {
      const generationAtStart = this.disposeGeneration;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        },
        video: false
      }).catch((error) => {
        console.warn("VoiceActivityDetector: Failed to initialize", error);
        return null;
      });
      if (!stream) return false;
      if (this.disposeGeneration !== generationAtStart) {
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }
      if (this.isInitialized()) {
        stream.getTracks().forEach((track) => track.stop());
        return true;
      }
      this.mediaStream = stream;
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContextClass();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = this.config.fft_size;
      this.analyser.smoothingTimeConstant = this.config.smoothing;
      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.sourceNode.connect(this.analyser);
      return true;
    } catch (error) {
      console.warn("VoiceActivityDetector: Failed to initialize", error);
      this.dispose();
      return false;
    }
  }
  /**
   * Check if VAD is initialized and ready
   */
  isInitialized() {
    return this.audioContext !== null && this.analyser !== null;
  }
  /**
   * Start monitoring for voice activity
   * Call this at the beginning of PAUSE phase
   */
  startMonitoring() {
    if (!this.isInitialized()) {
      console.warn("VoiceActivityDetector: Not initialized, call initialize() first");
      return;
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.speechEndDebounceTimer) {
      clearTimeout(this.speechEndDebounceTimer);
      this.speechEndDebounceTimer = null;
    }
    this.isContinuousMode = false;
    this.isMonitoring = true;
    this.monitoringStartTime = performance.now();
    this.energySamples = [];
    this.speechFrameCount = 0;
    this.consecutiveAboveThreshold = 0;
    this.peakEnergy = -Infinity;
    this.totalSpeechDuration = 0;
    this.lastSpeechStartTime = null;
    if (this.audioContext?.state === "suspended") {
      this.audioContext.resume().catch((error) => {
        console.warn("VoiceActivityDetector: Failed to resume audio context", error);
      });
    }
    this.analyzeLoop();
  }
  /**
   * Stop monitoring and return results
   * Call this at the end of PAUSE phase
   */
  stopMonitoring() {
    const endTime = performance.now();
    this.isMonitoring = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.lastSpeechStartTime !== null) {
      this.totalSpeechDuration += endTime - this.lastSpeechStartTime;
    }
    const duration = endTime - this.monitoringStartTime;
    const averageEnergy = this.calculateAverageEnergy();
    const activityRatio = duration > 0 ? this.totalSpeechDuration / duration : 0;
    return {
      speech_detected: this.speechFrameCount > 0,
      speech_duration_ms: this.totalSpeechDuration,
      peak_energy_db: this.peakEnergy === -Infinity ? -100 : this.peakEnergy,
      average_energy_db: averageEnergy,
      activity_ratio: Math.min(1, activityRatio),
      start_time: this.monitoringStartTime,
      end_time: endTime
    };
  }
  /**
   * Get current VAD status for UI feedback
   */
  getStatus() {
    if (!this.isMonitoring || !this.analyser) {
      return {
        is_speaking: false,
        current_energy_db: -100,
        is_active: this.isMonitoring
      };
    }
    const energy = this.getCurrentEnergy();
    const isSpeaking = energy > this.config.energy_threshold_db;
    return {
      is_speaking: isSpeaking,
      current_energy_db: energy,
      is_active: true
    };
  }
  /**
   * Get current energy level in dB
   */
  getCurrentEnergy() {
    if (!this.analyser) return -100;
    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(dataArray);
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const normalized = dataArray[i] / 255;
      sum += normalized * normalized;
    }
    const rms = Math.sqrt(sum / dataArray.length);
    const db = rms > 0 ? 20 * Math.log10(rms) : -100;
    return db;
  }
  /**
   * Update configuration
   */
  updateConfig(config) {
    this.config = { ...this.config, ...config };
    if (this.analyser) {
      if (config.fft_size !== void 0) {
        this.analyser.fftSize = config.fft_size;
      }
      if (config.smoothing !== void 0) {
        this.analyser.smoothingTimeConstant = config.smoothing;
      }
    }
  }
  /**
   * Clean up resources
   */
  dispose() {
    this.disposeGeneration += 1;
    this.isMonitoring = false;
    this.isContinuousMode = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.speechEndDebounceTimer) {
      clearTimeout(this.speechEndDebounceTimer);
      this.speechEndDebounceTimer = null;
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch((error) => {
        console.warn("VoiceActivityDetector: Failed to close audio context", error);
      });
      this.audioContext = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }
  /**
   * Internal analysis loop using requestAnimationFrame
   */
  analyzeLoop() {
    if (!this.isMonitoring || !this.analyser) return;
    const energy = this.getCurrentEnergy();
    const now = performance.now();
    this.energySamples.push(energy);
    if (energy > this.peakEnergy) {
      this.peakEnergy = energy;
    }
    const isAboveThreshold = energy > this.config.energy_threshold_db;
    if (isAboveThreshold) {
      this.consecutiveAboveThreshold++;
      if (this.consecutiveAboveThreshold >= this.config.min_frames_above) {
        if (this.lastSpeechStartTime === null) {
          this.lastSpeechStartTime = now;
        }
        this.speechFrameCount++;
      }
    } else {
      if (this.lastSpeechStartTime !== null) {
        this.totalSpeechDuration += now - this.lastSpeechStartTime;
        this.lastSpeechStartTime = null;
      }
      this.consecutiveAboveThreshold = 0;
    }
    this.rafId = requestAnimationFrame(() => this.analyzeLoop());
  }
  /**
   * Calculate average energy from samples
   */
  calculateAverageEnergy() {
    if (this.energySamples.length === 0) return -100;
    const sum = this.energySamples.reduce((a, b) => a + b, 0);
    return sum / this.energySamples.length;
  }
  // ============================================
  // CONTINUOUS MONITORING (for timing analysis)
  // ============================================
  /**
   * Start continuous monitoring from PROMPT phase.
   * Call this at the beginning of the PROMPT phase.
   *
   * @param config Optional continuous monitoring config
   */
  startContinuousMonitoring(config) {
    if (!this.isInitialized()) {
      console.warn("VoiceActivityDetector: Not initialized, call initialize() first");
      return;
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.isContinuousMode = true;
    this.continuousConfig = { ...DEFAULT_CONTINUOUS_VAD_CONFIG, ...this.config, ...config };
    this.continuousStartTime = performance.now();
    this.phaseTimestamps.clear();
    this.phaseTimestamps.set("PROMPT", 0);
    this.firstSpeechStartAbsolute = null;
    this.lastSpeechEndAbsolute = null;
    this.confirmedSpeechEnd = null;
    this.continuousEnergyTimeline = [];
    this.calibrationSamples = [];
    this.calibrationClosed = false;
    this.adaptiveThresholdDb = null;
    this.candidateSpeechStart = null;
    this.candidateSustained = false;
    this.candidateRunEnd = null;
    if (this.speechEndDebounceTimer) {
      clearTimeout(this.speechEndDebounceTimer);
      this.speechEndDebounceTimer = null;
    }
    this.isMonitoring = true;
    this.monitoringStartTime = this.continuousStartTime;
    this.energySamples = [];
    this.speechFrameCount = 0;
    this.consecutiveAboveThreshold = 0;
    this.peakEnergy = -Infinity;
    this.totalSpeechDuration = 0;
    this.lastSpeechStartTime = null;
    if (this.audioContext?.state === "suspended") {
      this.audioContext.resume().catch((error) => {
        console.warn("VoiceActivityDetector: Failed to resume audio context", error);
      });
    }
    this.continuousAnalyzeLoop();
  }
  /**
   * Mark a phase transition during continuous monitoring.
   * Use this to record when phases start/end for overlap detection.
   *
   * @param phase The phase that is starting or a marker like 'PROMPT_END'
   * @param timestamp Optional absolute timestamp (defaults to now)
   */
  markPhaseTransition(phase, timestamp) {
    if (!this.isContinuousMode) {
      console.warn("VoiceActivityDetector: Not in continuous mode");
      return;
    }
    const absoluteTime = timestamp ?? performance.now();
    const relativeTime = absoluteTime - this.continuousStartTime;
    this.phaseTimestamps.set(phase, relativeTime);
  }
  /**
   * Check if currently in continuous monitoring mode
   */
  isContinuousMonitoring() {
    return this.isContinuousMode;
  }
  /**
   * Stop continuous monitoring and return timing results.
   *
   * @param modelDurationMs Duration of the model (target) audio in ms
   * @returns Full timing result with latency, duration delta, and overlap flags
   */
  stopContinuousMonitoring(modelDurationMs) {
    if (!this.isContinuousMode) {
      console.warn("VoiceActivityDetector: Not in continuous mode");
      return createEmptySpeechTimingResult(0, 0);
    }
    const endTime = performance.now();
    this.isMonitoring = false;
    this.isContinuousMode = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.speechEndDebounceTimer) {
      clearTimeout(this.speechEndDebounceTimer);
      this.speechEndDebounceTimer = null;
    }
    const promptEndMs = this.phaseTimestamps.get("PROMPT_END") ?? 0;
    const voice1StartMs = this.phaseTimestamps.get("VOICE_1") ?? 0;
    this.tryResolveCandidate(endTime - this.continuousStartTime, true);
    if (this.lastSpeechStartTime !== null && this.confirmedSpeechEnd === null) {
      const cycleEndMs = endTime - this.continuousStartTime;
      const hasVoice1 = this.phaseTimestamps.has("VOICE_1");
      this.confirmedSpeechEnd = hasVoice1 ? Math.min(cycleEndMs, voice1StartMs + this.continuousConfig.post_voice1_grace_ms) : cycleEndMs;
    }
    const speechStartMs = this.firstSpeechStartAbsolute !== null ? this.firstSpeechStartAbsolute - this.continuousStartTime : null;
    const speechEndMs = speechStartMs === null ? null : this.confirmedSpeechEnd ?? (this.lastSpeechEndAbsolute !== null ? this.lastSpeechEndAbsolute - this.continuousStartTime : null);
    const responseLatencyMs = speechStartMs;
    const learnerDurationMs = speechStartMs !== null && speechEndMs !== null ? speechEndMs - speechStartMs : null;
    const durationDeltaMs = learnerDurationMs !== null ? learnerDurationMs - modelDurationMs : null;
    const startedDuringPrompt = speechStartMs !== null && speechStartMs < promptEndMs;
    const stillSpeakingAtVoice1 = speechEndMs !== null && speechEndMs > voice1StartMs;
    const averageEnergy = this.calculateAverageEnergy();
    let envelope;
    if (speechStartMs !== null && speechEndMs !== null && learnerDurationMs !== null) {
      const speechWindow = this.continuousEnergyTimeline.filter(
        (s) => s.t - this.continuousStartTime >= speechStartMs && s.t - this.continuousStartTime <= speechEndMs
      );
      envelope = extractEnvelopeMetadata(speechWindow, learnerDurationMs);
    }
    this.continuousEnergyTimeline = [];
    return {
      prompt_start_ms: 0,
      prompt_end_ms: promptEndMs,
      voice1_start_ms: voice1StartMs,
      speech_start_ms: speechStartMs,
      speech_end_ms: speechEndMs,
      response_latency_ms: responseLatencyMs,
      learner_duration_ms: learnerDurationMs,
      duration_delta_ms: durationDeltaMs,
      started_during_prompt: startedDuringPrompt,
      still_speaking_at_voice1: stillSpeakingAtVoice1,
      speech_detected: speechStartMs !== null,
      peak_energy_db: this.peakEnergy === -Infinity ? -100 : this.peakEnergy,
      average_energy_db: averageEnergy,
      envelope
    };
  }
  /**
   * The energy a sample must exceed to count as speech, right now.
   *
   * Until the calibration slice closes this is the configured absolute
   * threshold; afterwards it is the larger of that and (measured floor +
   * margin). The measured floor is a LOW QUANTILE of the calibration slice
   * (see `calibration_percentile`), not a mean, median or max: a learner
   * speaking during the slice adds energy on top of the playback and can only
   * push samples up, so the estimator has to survive upward contamination —
   * and the median does not survive enough of it.
   */
  currentSpeechThresholdDb() {
    const absolute = this.continuousConfig.energy_threshold_db;
    if (this.adaptiveThresholdDb === null) return absolute;
    return Math.max(absolute, this.adaptiveThresholdDb);
  }
  /**
   * Accumulate the calibration slice and close it once the window elapses (or
   * PROMPT_END arrives first, for a very short prompt). Returns true while
   * calibration is still open, during which no onset may be CONFIRMED — but
   * see `backdateOnsetThroughCalibration`, which recovers an onset that was
   * already under way when the slice closed.
   */
  updateCalibration(relativeNow, energy) {
    if (!this.continuousConfig.adaptive_floor_enabled) return false;
    if (this.calibrationClosed) return false;
    const promptEnd = this.phaseTimestamps.get("PROMPT_END");
    const windowElapsed = relativeNow >= this.continuousConfig.calibration_window_ms;
    const promptEnded = promptEnd !== void 0 && relativeNow >= promptEnd;
    if (!windowElapsed && !promptEnded) {
      this.calibrationSamples.push(energy);
      return true;
    }
    this.calibrationClosed = true;
    if (this.calibrationSamples.length >= this.continuousConfig.calibration_min_samples) {
      const sorted = [...this.calibrationSamples].sort((a, b) => a - b);
      const q = Math.min(Math.max(this.continuousConfig.calibration_percentile, 0), 1);
      const idx = Math.min(sorted.length - 1, Math.floor(q * sorted.length));
      this.adaptiveThresholdDb = sorted[idx] + this.continuousConfig.adaptive_margin_db;
      this.backdateOnsetThroughCalibration();
    }
    return false;
  }
  /**
   * Recover the onset of an utterance that was ALREADY IN PROGRESS when the
   * calibration slice closed.
   *
   * The slice cannot judge its own samples as they arrive — there is no floor
   * yet to judge them against. But the moment the floor exists, the slice can
   * be re-read: walk backwards from its last sample while the energy is above
   * the freshly measured floor, and the start of that run is when the learner
   * actually began. Without this, the most confident speaker there is — the
   * one who answers 200ms in because they already knew it — was credited at
   * the moment calibration happened to close, and that inflated latency was
   * reported as truth (measured: onset 200ms, reported 432ms).
   *
   * Only ever called with a measured floor in hand. Under the starved-
   * calibration fallback there is no floor, the absolute threshold governs,
   * and back-dating there would hand the whole cycle back to playback — which
   * is the original bug, so it is deliberately not done.
   *
   * The candidate seeded here is not yet an onset: it still has to clear the
   * sustain test and the prompt-boundary test like any other.
   */
  backdateOnsetThroughCalibration() {
    if (this.firstSpeechStartAbsolute !== null) return;
    if (this.candidateSpeechStart !== null) return;
    const threshold = this.currentSpeechThresholdDb();
    const timeline = this.continuousEnergyTimeline;
    if (timeline.length === 0) return;
    if (timeline[timeline.length - 1].db <= threshold) return;
    let i = timeline.length - 1;
    while (i > 0 && timeline[i - 1].db > threshold) i--;
    this.candidateSpeechStart = timeline[i].t;
    this.candidateSustained = false;
    this.candidateRunEnd = null;
  }
  /**
   * Resolve a sustained candidate whose onset lies inside the prompt window,
   * using the prompt-end boundary rather than level. See
   * `prompt_boundary_guard_ms` for why level alone cannot do this.
   *
   * @param relativeNow  now, relative to prompt start
   * @param stillAbove   is the run still above the floor at `relativeNow`?
   * @returns 'learner' to confirm, 'bleed' to discard and re-arm, 'pending'
   *          to keep waiting for the boundary to arrive.
   */
  resolvePromptWindowCandidate(relativeNow, stillAbove) {
    const promptEnd = this.phaseTimestamps.get("PROMPT_END");
    if (promptEnd === void 0) return "pending";
    const guard = this.continuousConfig.prompt_boundary_guard_ms;
    if (guard <= 0) return "learner";
    if (stillAbove) {
      return relativeNow >= promptEnd + guard ? "learner" : "pending";
    }
    if (relativeNow < promptEnd - guard) return "learner";
    return "bleed";
  }
  /**
   * Whether a NEW utterance may still begin. The learner is invited to speak
   * between PROMPT_END and VOICE_1; once the app starts playing target audio
   * an onset is far likelier to be that audio than a response, and the
   * response-latency measurement it would produce is meaningless anyway.
   * Speech already in progress is unaffected — which is what keeps
   * `still_speaking_at_voice1` measurable.
   */
  onsetWindowOpen(relativeNow) {
    const voice1 = this.phaseTimestamps.get("VOICE_1");
    return voice1 === void 0 || relativeNow < voice1;
  }
  /** Has the pending candidate stayed up long enough to be an utterance
   *  rather than a door, a tap or a click? */
  candidateIsSustained(now) {
    return this.candidateSpeechStart !== null && now - this.candidateSpeechStart >= this.continuousConfig.min_speech_duration_ms;
  }
  /**
   * Decide, with whatever is known right now, whether the pending sustained
   * candidate is the learner, the app's own prompt audio, or still
   * undecidable. Called every frame while a sustained candidate is pending,
   * and once more at stop.
   *
   * PROMPT_END is marked by the player when the prompt audio actually
   * finishes, which is normally LONG after the candidate began — so this
   * cannot be settled when the candidate is created. It is settled here, on
   * whichever frame the information finally exists.
   *
   * @param relativeNow now, relative to prompt start
   * @param atStop      final call: no more information is coming, so an
   *                    unmarked PROMPT_END means the boundary test is simply
   *                    unavailable and the candidate is taken at face value.
   */
  tryResolveCandidate(relativeNow, atStop = false) {
    if (this.firstSpeechStartAbsolute !== null) return;
    if (this.candidateSpeechStart === null || !this.candidateSustained) return;
    const promptEnd = this.phaseTimestamps.get("PROMPT_END");
    if (promptEnd === void 0) {
      if (atStop) this.confirmOnset();
      return;
    }
    const candidateRel = this.candidateSpeechStart - this.continuousStartTime;
    if (candidateRel >= promptEnd) {
      this.confirmOnset();
      return;
    }
    const stillAbove = this.candidateRunEnd === null;
    const at = stillAbove ? relativeNow : this.candidateRunEnd - this.continuousStartTime;
    const verdict = this.resolvePromptWindowCandidate(at, stillAbove);
    if (verdict === "learner") {
      this.confirmOnset();
    } else if (verdict === "bleed") {
      this.discardCandidate();
    }
  }
  /** Throw the pending candidate away and re-arm for a real response. */
  discardCandidate() {
    this.candidateSpeechStart = null;
    this.candidateSustained = false;
    this.candidateRunEnd = null;
  }
  /** Promote the pending candidate to THE onset for this cycle. */
  confirmOnset() {
    const onset = this.candidateSpeechStart;
    this.firstSpeechStartAbsolute = onset;
    const onsetRel = onset !== null ? onset - this.continuousStartTime : 0;
    if (this.lastSpeechEndAbsolute !== null && this.lastSpeechEndAbsolute <= (onset ?? 0)) {
      this.lastSpeechEndAbsolute = null;
    }
    if (this.confirmedSpeechEnd !== null && this.confirmedSpeechEnd <= onsetRel) {
      this.confirmedSpeechEnd = null;
    }
    if (this.lastSpeechEndAbsolute === null && this.speechEndDebounceTimer) {
      clearTimeout(this.speechEndDebounceTimer);
      this.speechEndDebounceTimer = null;
    }
  }
  /**
   * Internal analysis loop for continuous monitoring.
   * Similar to analyzeLoop but tracks first speech start and debounces speech end.
   */
  continuousAnalyzeLoop() {
    if (!this.isMonitoring || !this.analyser) return;
    const energy = this.getCurrentEnergy();
    const now = performance.now();
    const relativeNow = now - this.continuousStartTime;
    this.energySamples.push(energy);
    this.continuousEnergyTimeline.push({ t: now, db: energy });
    if (energy > this.peakEnergy) {
      this.peakEnergy = energy;
    }
    const calibrating = this.updateCalibration(relativeNow, energy);
    const isAboveThreshold = !calibrating && energy > this.currentSpeechThresholdDb();
    if (isAboveThreshold) {
      this.consecutiveAboveThreshold++;
      if (this.speechEndDebounceTimer) {
        clearTimeout(this.speechEndDebounceTimer);
        this.speechEndDebounceTimer = null;
      }
      if (this.consecutiveAboveThreshold >= this.continuousConfig.min_frames_above) {
        if (this.lastSpeechStartTime === null) {
          this.lastSpeechStartTime = now;
        }
        this.speechFrameCount++;
        if (this.firstSpeechStartAbsolute === null) {
          if (this.candidateSpeechStart === null) {
            if (this.onsetWindowOpen(relativeNow)) {
              this.candidateSpeechStart = this.lastSpeechStartTime;
              this.candidateSustained = false;
              this.candidateRunEnd = null;
            }
          } else if (this.candidateRunEnd === null && this.candidateIsSustained(now)) {
            this.candidateSustained = true;
          }
          this.tryResolveCandidate(relativeNow);
        }
      }
    } else {
      if (this.firstSpeechStartAbsolute === null && this.candidateSpeechStart !== null) {
        if (!this.candidateSustained) {
          this.discardCandidate();
        } else {
          if (this.candidateRunEnd === null) this.candidateRunEnd = now;
          this.tryResolveCandidate(relativeNow);
        }
      }
      if (this.lastSpeechStartTime !== null) {
        this.lastSpeechEndAbsolute = now;
        if (!this.speechEndDebounceTimer) {
          this.speechEndDebounceTimer = setTimeout(() => {
            if (this.lastSpeechEndAbsolute !== null) {
              this.confirmedSpeechEnd = this.lastSpeechEndAbsolute - this.continuousStartTime;
            }
            this.speechEndDebounceTimer = null;
          }, this.continuousConfig.speech_end_debounce_ms);
        }
        this.totalSpeechDuration += now - this.lastSpeechStartTime;
        this.lastSpeechStartTime = null;
      }
      this.consecutiveAboveThreshold = 0;
    }
    this.rafId = requestAnimationFrame(() => this.continuousAnalyzeLoop());
  }
};
function createVoiceActivityDetector(config) {
  return new VoiceActivityDetector(config);
}

// src/audio/SpeechTimingAnalyzer.ts
var SpeechTimingAnalyzer = class {
  vad;
  config;
  listeners = /* @__PURE__ */ new Set();
  isActive = false;
  lastResult = null;
  constructor(vad, config) {
    this.vad = vad;
    this.config = config ?? {};
  }
  /**
   * Start timing analysis for a new learning cycle.
   * Call this when PROMPT phase begins.
   */
  startCycle() {
    if (this.isActive) {
      console.warn("SpeechTimingAnalyzer: Cycle already active, stopping previous");
      this.reset();
    }
    if (!this.vad.isInitialized()) {
      console.warn("SpeechTimingAnalyzer: VAD not initialized");
      return;
    }
    this.isActive = true;
    this.lastResult = null;
    this.vad.startContinuousMonitoring(this.config);
    this.emit({ type: "cycle_started" });
  }
  /**
   * Record a phase transition.
   * Call this when phases change during the cycle.
   *
   * @param phase The phase that is starting, or 'PROMPT_END' when prompt audio ends
   * @param timestamp Optional timestamp (defaults to now)
   */
  onPhaseChange(phase, timestamp) {
    if (!this.isActive) {
      return;
    }
    this.vad.markPhaseTransition(phase, timestamp);
  }
  /**
   * End the timing analysis cycle and get results.
   *
   * @param modelDurationMs Duration of the target audio (for duration delta calculation)
   * @returns Full timing result with latency, overlap flags, etc.
   */
  endCycle(modelDurationMs) {
    if (!this.isActive) {
      console.warn("SpeechTimingAnalyzer: No active cycle");
      return createEmptySpeechTimingResult(0, 0);
    }
    const result = this.vad.stopContinuousMonitoring(modelDurationMs);
    this.isActive = false;
    this.lastResult = result;
    this.emit({ type: "cycle_ended", result });
    return result;
  }
  /**
   * Reset the analyzer (abort current cycle if active).
   */
  reset() {
    if (this.isActive && this.vad.isContinuousMonitoring()) {
      this.vad.stopContinuousMonitoring(0);
    }
    this.isActive = false;
    this.lastResult = null;
  }
  /**
   * Check if a timing cycle is currently active.
   */
  isAnalyzing() {
    return this.isActive;
  }
  /**
   * Get the last timing result (from previous cycle).
   */
  getLastResult() {
    return this.lastResult;
  }
  /**
   * Update configuration for future cycles.
   */
  updateConfig(config) {
    this.config = { ...this.config, ...config };
  }
  /**
   * Add an event listener.
   */
  addEventListener(listener) {
    this.listeners.add(listener);
  }
  /**
   * Remove an event listener.
   */
  removeEventListener(listener) {
    this.listeners.delete(listener);
  }
  /**
   * Emit an event to all listeners.
   */
  emit(event) {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch (error) {
        console.error("SpeechTimingAnalyzer: Listener error", error);
      }
    }
  }
};
function createSpeechTimingAnalyzer(vad, config) {
  return new SpeechTimingAnalyzer(vad, config);
}

// src/audio/PronunciationEngine.ts
var PROSODY_WEIGHTS = {
  // Tonal — envelope shape matters most (tone = energy contour)
  cmn: { duration: 0.2, peakCount: 0.3, envelope: 0.5 },
  zho: { duration: 0.2, peakCount: 0.3, envelope: 0.5 },
  yue: { duration: 0.2, peakCount: 0.3, envelope: 0.5 },
  tha: { duration: 0.25, peakCount: 0.3, envelope: 0.45 },
  vie: { duration: 0.25, peakCount: 0.3, envelope: 0.45 },
  // Pitch-accent
  jpn: { duration: 0.25, peakCount: 0.35, envelope: 0.4 },
  kor: { duration: 0.25, peakCount: 0.35, envelope: 0.4 },
  // Stress-timed — peak count and envelope both matter
  eng: { duration: 0.25, peakCount: 0.35, envelope: 0.4 },
  deu: { duration: 0.25, peakCount: 0.35, envelope: 0.4 },
  nld: { duration: 0.25, peakCount: 0.35, envelope: 0.4 },
  rus: { duration: 0.25, peakCount: 0.35, envelope: 0.4 },
  // Syllable-timed — duration and peak count matter most
  spa: { duration: 0.3, peakCount: 0.4, envelope: 0.3 },
  fra: { duration: 0.3, peakCount: 0.4, envelope: 0.3 },
  ita: { duration: 0.3, peakCount: 0.4, envelope: 0.3 },
  por: { duration: 0.3, peakCount: 0.4, envelope: 0.3 },
  // Celtic
  cym: { duration: 0.3, peakCount: 0.35, envelope: 0.35 },
  gle: { duration: 0.3, peakCount: 0.35, envelope: 0.35 },
  gla: { duration: 0.3, peakCount: 0.35, envelope: 0.35 },
  // Semitic
  ara: { duration: 0.3, peakCount: 0.35, envelope: 0.35 },
  heb: { duration: 0.3, peakCount: 0.35, envelope: 0.35 }
};
var DEFAULT_WEIGHTS = { duration: 0.25, peakCount: 0.35, envelope: 0.4 };
function getWeightsForLanguage(langCode) {
  return Object.prototype.hasOwnProperty.call(PROSODY_WEIGHTS, langCode) ? PROSODY_WEIGHTS[langCode] : DEFAULT_WEIGHTS;
}
var ANALYSIS_FPS = 50;
function extractPitchContour(audioBuffer) {
  const channelData = audioBuffer.numberOfChannels > 1 ? mixToMono(audioBuffer) : audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;
  const frameSize = Math.round(sampleRate / ANALYSIS_FPS);
  const times = [];
  const frequencies = [];
  const clarities = [];
  const energy = [];
  for (let offset = 0; offset + frameSize <= channelData.length; offset += frameSize) {
    let sumSq = 0;
    for (let i = offset; i < offset + frameSize; i++) {
      sumSq += channelData[i] * channelData[i];
    }
    const rms = Math.sqrt(sumSq / frameSize);
    times.push(offset / sampleRate);
    frequencies.push(0);
    clarities.push(rms > 0.01 ? 0.95 : 0.1);
    energy.push(rms);
  }
  return { times, frequencies, clarities, sampleRate, energy };
}
function mixToMono(buffer) {
  const length = buffer.length;
  const mono = new Float32Array(length);
  const numChannels = buffer.numberOfChannels;
  for (let ch = 0; ch < numChannels; ch++) {
    const channelData = buffer.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      mono[i] += channelData[i] / numChannels;
    }
  }
  return mono;
}
function trimSilence(energy) {
  if (energy.length === 0) return [0, 0];
  const peak = Math.max(...energy);
  const threshold = Math.max(peak * 0.25, 8e-3);
  let start = 0;
  while (start < energy.length && energy[start] < threshold) start++;
  let end = energy.length;
  while (end > start && energy[end - 1] < threshold) end--;
  if (start >= end) return [0, energy.length];
  return [start, end];
}
function countEnergyPeaks(energy) {
  if (energy.length < 3) return energy.length > 0 ? 1 : 0;
  const smoothed = smooth(energy, 7);
  const sorted = [...smoothed].sort((a, b) => a - b);
  const p70 = sorted[Math.floor(sorted.length * 0.7)] || 0;
  const threshold = Math.max(p70 * 0.6, sorted[Math.floor(sorted.length * 0.3)] || 0);
  let peaks = 0;
  for (let i = 1; i < smoothed.length - 1; i++) {
    if (smoothed[i] > smoothed[i - 1] && smoothed[i] > smoothed[i + 1] && smoothed[i] > threshold) {
      peaks++;
      i += 4;
    }
  }
  return Math.max(1, peaks);
}
function smooth(arr, windowSize) {
  const half = Math.floor(windowSize / 2);
  return arr.map((_, i) => {
    let sum = 0, count = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(arr.length - 1, i + half); j++) {
      sum += arr[j];
      count++;
    }
    return sum / count;
  });
}
function normalizeEnvelope(energy, bins = 20) {
  if (energy.length === 0) return new Array(bins).fill(1 / bins);
  const actualBins = Math.min(bins, energy.length);
  const binSize = energy.length / actualBins;
  const result = [];
  for (let b = 0; b < actualBins; b++) {
    const start = Math.floor(b * binSize);
    const end = Math.max(start + 1, Math.floor((b + 1) * binSize));
    let sum = 0;
    for (let i = start; i < Math.min(end, energy.length); i++) {
      sum += energy[i];
    }
    result.push(sum / (end - start));
  }
  while (result.length < bins) {
    result.push(result[result.length - 1] || 0);
  }
  const total = result.reduce((a, b) => a + b, 0);
  if (total > 0) {
    for (let i = 0; i < result.length; i++) result[i] /= total;
  }
  return result;
}
function envelopeSimilarity(a, b) {
  if (a.length !== b.length || a.length === 0) return 50;
  let dotProduct = 0, normA = 0, normB = 0;
  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  if (denom === 0) return 50;
  const cosSim = dotProduct / denom;
  return Math.max(0, Math.min(100, cosSim * 100));
}
function compareProsody(nativeContour, learnerContour, langCode) {
  const weights = getWeightsForLanguage(langCode);
  const [nStart, nEnd] = trimSilence(nativeContour.energy);
  const [lStart, lEnd] = trimSilence(learnerContour.energy);
  const nEnergy = nativeContour.energy.slice(nStart, nEnd);
  const lEnergy = learnerContour.energy.slice(lStart, lEnd);
  const nDuration = nEnergy.length / ANALYSIS_FPS;
  const lDuration = lEnergy.length / ANALYSIS_FPS;
  let durationScore = 50;
  if (nDuration > 0 && lDuration > 0) {
    const ratio = lDuration / nDuration;
    const deviation = Math.abs(1 - ratio);
    durationScore = Math.max(0, Math.min(100, 100 * Math.exp(-1.5 * deviation * deviation)));
  }
  const nPeaks = countEnergyPeaks(nEnergy);
  const lPeaks = countEnergyPeaks(lEnergy);
  let peakCountScore;
  if (nPeaks === lPeaks) {
    peakCountScore = 100;
  } else {
    const diff = Math.abs(nPeaks - lPeaks);
    peakCountScore = Math.max(0, Math.min(100, 100 * Math.exp(-0.3 * diff * diff)));
  }
  const nNorm = normalizeEnvelope(nEnergy);
  const lNorm = normalizeEnvelope(lEnergy);
  const envelopeScore = envelopeSimilarity(nNorm, lNorm);
  const overall = Math.round(
    weights.duration * durationScore + weights.peakCount * peakCountScore + weights.envelope * envelopeScore
  );
  return {
    score: {
      duration: Math.round(durationScore),
      peakCount: Math.round(peakCountScore),
      envelope: Math.round(envelopeScore),
      overall
    },
    nativeContour: nNorm,
    learnerContour: lNorm,
    alignmentPath: [],
    learnerDurationMs: lDuration * 1e3,
    nativeDurationMs: nDuration * 1e3
  };
}
var AudioRecorder = class {
  mediaRecorder = null;
  audioContext;
  constructor(audioContext) {
    this.audioContext = audioContext;
  }
  start(stream) {
    const chunks = [];
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
    const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : void 0);
    this.mediaRecorder = mediaRecorder;
    return new Promise((resolve, reject) => {
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        try {
          const blob = new Blob(chunks, { type: mediaRecorder.mimeType || "audio/webm" });
          const arrayBuffer = await blob.arrayBuffer();
          const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
          resolve(audioBuffer);
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      };
      mediaRecorder.onerror = (event) => {
        reject(new Error(`MediaRecorder error: ${event}`));
      };
      mediaRecorder.start(100);
    });
  }
  stop() {
    if (this.mediaRecorder?.state === "recording") {
      this.mediaRecorder.stop();
    }
  }
  isRecording() {
    return this.mediaRecorder?.state === "recording";
  }
};
var nativePitchCache = /* @__PURE__ */ new Map();
function getNativePitchContour(phraseId, audioBuffer) {
  const cached = nativePitchCache.get(phraseId);
  if (cached) return cached;
  const contour = extractPitchContour(audioBuffer);
  nativePitchCache.set(phraseId, contour);
  return contour;
}

// src/audio/vadSummary.ts
var EMPTY_MASTERY = () => ({ acquisition: 0, consolidating: 0, confident: 0, mastered: 0 });
function mean(values) {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}
function median(values) {
  if (values.length === 0) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}
function summariseVad(learnerIds, names, metricsByLearner, prosodyByLearner, prosodyAvailable = true) {
  const mastery = EMPTY_MASTERY();
  const learnerLatencies = [];
  const rows = [];
  let legoSeries = 0;
  let withData = 0;
  let withProsody = 0;
  let peakSum = 0, peakBase = 0;
  let avgSum = 0, avgBase = 0;
  let peakCountSum = 0, peakCountBase = 0;
  let promptStarts = 0, promptBase = 0;
  let overruns = 0, overrunBase = 0;
  let prosodyEvents = 0;
  for (const learnerId of learnerIds) {
    const metrics = metricsByLearner.get(learnerId) ?? [];
    const prosody = prosodyByLearner.get(learnerId) ?? null;
    if (metrics.length > 0) withData++;
    if (prosody && prosody.events > 0) withProsody++;
    if (metrics.length === 0 && !prosody) continue;
    legoSeries += metrics.length;
    for (const m of metrics) {
      if (m.mastery_state in mastery) mastery[m.mastery_state]++;
    }
    const latencies = metrics.map((m) => m.mean_latency_ms).filter((v) => typeof v === "number" && Number.isFinite(v));
    const learnerMean = mean(latencies);
    if (learnerMean !== null) learnerLatencies.push(learnerMean);
    if (prosody) {
      prosodyEvents += prosody.events;
      peakSum += prosody.peakEnergyDbSum;
      peakBase += prosody.peakEnergyDbBase;
      avgSum += prosody.averageEnergyDbSum;
      avgBase += prosody.averageEnergyDbBase;
      peakCountSum += prosody.peakCountSum;
      peakCountBase += prosody.peakCountBase;
      promptStarts += prosody.startedDuringPrompt;
      promptBase += prosody.startedDuringPromptBase;
      overruns += prosody.stillSpeakingAtVoice1;
      overrunBase += prosody.stillSpeakingAtVoice1Base;
    }
    const lastSeen = metrics.map((m) => m.last_seen_at).filter((v) => !!v).sort().pop() ?? null;
    rows.push({
      learnerId,
      name: names.get(learnerId) ?? "Unnamed learner",
      legos: metrics.length,
      mastered: metrics.filter((m) => m.mastery_state === "mastered").length,
      meanLatency: learnerMean,
      prosodyEvents: prosody?.events ?? 0,
      lastSeenAt: lastSeen
    });
  }
  rows.sort((a, b) => b.legos - a.legos || a.name.localeCompare(b.name));
  return {
    total: learnerIds.length,
    withData,
    withProsody,
    uptake: learnerIds.length > 0 ? withData / learnerIds.length : null,
    mastery,
    legoSeries,
    learnerLatencies,
    medianLatency: median(learnerLatencies),
    prosody: {
      events: prosodyEvents,
      learners: withProsody,
      meanPeakEnergyDb: peakBase > 0 ? peakSum / peakBase : null,
      meanAverageEnergyDb: avgBase > 0 ? avgSum / avgBase : null,
      meanPeakCount: peakCountBase > 0 ? peakCountSum / peakCountBase : null,
      startedDuringPromptRate: promptBase > 0 ? promptStarts / promptBase : null,
      startedDuringPromptBase: promptBase,
      stillSpeakingRate: overrunBase > 0 ? overruns / overrunBase : null,
      stillSpeakingBase: overrunBase,
      available: prosodyAvailable
    },
    learners: rows
  };
}

// src/pricing/constants.ts
var BIG_10_LANGUAGES = [
  "eng",
  // English
  "spa",
  // Spanish
  "fra",
  // French
  "deu",
  // German
  "ita",
  // Italian
  "por",
  // Portuguese
  "zho",
  // Chinese (Mandarin)
  "jpn",
  // Japanese
  "ara",
  // Arabic
  "kor"
  // Korean
];
function isBig10Language(langCode) {
  return BIG_10_LANGUAGES.includes(langCode);
}
function canAccessCourse(user, course) {
  if (course.course_type === "community") {
    return true;
  }
  const requiresPaid = isBig10Language(course.target_lang);
  if (!requiresPaid) {
    return true;
  }
  return user.subscription_tier === "paid";
}

// src/pricing/types.ts
var BELT_MAX_SEEDS = {
  white: 7,
  // Seeds 1-7
  yellow: 19,
  // Seeds 8-19
  orange: 39,
  // Seeds 20-39
  green: 79,
  // Seeds 40-79
  blue: 149,
  // Seeds 80-149
  purple: 279,
  // Seeds 150-279
  brown: 399,
  // Seeds 280-399
  black: Infinity
  // Seeds 400+
};
var PREMIUM_PREVIEW_BELT = "yellow";
var PREMIUM_PREVIEW_MAX_SEED = BELT_MAX_SEEDS[PREMIUM_PREVIEW_BELT];
var SCHOOL_PUPIL_FREE_BELT = "green";
var SCHOOL_PUPIL_FREE_MAX_SEED = BELT_MAX_SEEDS[SCHOOL_PUPIL_FREE_BELT];

// src/pricing/access.ts
function checkCourseAccess(course, subscription, entitlements, platformRole) {
  if (platformRole === "ssi_admin" || platformRole === "tester") {
    return {
      canAccess: true,
      canPreview: true,
      reason: "entitled",
      upgradeRequired: false
    };
  }
  if (course.is_community || course.pricing_tier === "community") {
    return {
      canAccess: true,
      canPreview: true,
      reason: "community",
      upgradeRequired: false
    };
  }
  if (course.pricing_tier === "free") {
    return {
      canAccess: true,
      canPreview: true,
      reason: "free",
      upgradeRequired: false
    };
  }
  const isSubscribed = subscription?.isActive && subscription?.tier === "paid";
  if (isSubscribed) {
    return {
      canAccess: true,
      canPreview: true,
      reason: "subscribed",
      upgradeRequired: false
    };
  }
  if (entitlements && entitlements.length > 0) {
    const now = /* @__PURE__ */ new Date();
    const hasAccess = entitlements.some((e) => {
      if (e.expiresAt && new Date(e.expiresAt) <= now) return false;
      if (e.accessType === "full") return true;
      if (e.accessType === "courses" && e.grantedCourses && course.course_code) {
        return e.grantedCourses.includes(course.course_code);
      }
      return false;
    });
    if (hasAccess) {
      return {
        canAccess: true,
        canPreview: true,
        reason: "entitled",
        upgradeRequired: false
      };
    }
  }
  if (subscription?.isPending) {
    return {
      canAccess: false,
      canPreview: true,
      previewBeltLimit: PREMIUM_PREVIEW_BELT,
      previewMaxSeed: PREMIUM_PREVIEW_MAX_SEED,
      reason: "pending",
      upgradeRequired: false
    };
  }
  return {
    canAccess: false,
    canPreview: true,
    previewBeltLimit: PREMIUM_PREVIEW_BELT,
    previewMaxSeed: PREMIUM_PREVIEW_MAX_SEED,
    reason: "preview_only",
    upgradeRequired: true
  };
}
function canAccessSeed(course, subscription, seedNumber, entitlements, platformRole) {
  const access = checkCourseAccess(course, subscription, entitlements, platformRole);
  if (access.canAccess) {
    return true;
  }
  if (access.canPreview && access.previewMaxSeed) {
    return seedNumber <= access.previewMaxSeed;
  }
  return false;
}
function inferPricingTier(targetLang, courseCode) {
  if (courseCode.startsWith("community_")) {
    return "community";
  }
  const BIG_10 = ["eng", "spa", "fra", "deu", "ita", "por", "zho", "jpn", "ara", "kor"];
  if (BIG_10.includes(targetLang)) {
    return "premium";
  }
  if (targetLang === "cym") {
    return "premium";
  }
  return "free";
}

// src/pricing/trial.ts
var TRIAL_DAYS_COMMERCIAL = 30;
var TRIAL_DAYS_HERITAGE = 365;
function targetLangFromCourseCode(courseCode) {
  if (!courseCode) return "";
  const beforeFor = courseCode.split("_for_")[0];
  return beforeFor.split("_")[0] || "";
}
function isCommercialCourse(course) {
  const target = (course.target_lang || targetLangFromCourseCode(course.course_code)).toLowerCase();
  return isBig10Language(target);
}
function trialDaysForCourse(course) {
  return isCommercialCourse(course) ? TRIAL_DAYS_COMMERCIAL : TRIAL_DAYS_HERITAGE;
}

// src/pricing/offlineCapability.ts
var OFFLINE_UNLOCKED_BY = "any_paid_plan";
var OFFLINE_TEACHER_ROLES = ["teacher", "tutor", "school_admin", "govt_admin"];
var FREE_OFFLINE_WINDOW_DAYS = 30;
var FREE_OFFLINE_WINDOW_MS = FREE_OFFLINE_WINDOW_DAYS * 24 * 60 * 60 * 1e3;
function holdsOfflineCapability(f, unlockedBy = OFFLINE_UNLOCKED_BY) {
  if (f.privileged) return true;
  switch (unlockedBy) {
    case "premium_plan_only":
      return f.premiumPlan;
    case "voice_plan_only":
      return f.voicePlan;
    case "any_paid_plan":
      return f.premiumPlan || f.voicePlan;
  }
}
function isOfflineTeacherRole(educationalRole) {
  return !!educationalRole && OFFLINE_TEACHER_ROLES.includes(educationalRole);
}
function isFreeTierCourse(c) {
  return !!c.is_community || c.pricing_tier === "free" || c.pricing_tier === "community";
}
function freeOfflineWindowEndsAt(firstOfflineUseMs, nowMs) {
  return (firstOfflineUseMs ?? nowMs) + FREE_OFFLINE_WINDOW_MS;
}

// src/pricing/indiaCatalog.ts
var INDIA_CURRENCY = "INR";
var INDIA_PLACEHOLDER_PREFIX = "PLACEHOLDER_";
function isPlaceholderPriceId(priceId) {
  return !priceId || priceId.startsWith(INDIA_PLACEHOLDER_PREFIX);
}
function offeredIndiaPriceId(priceId) {
  return priceId && !isPlaceholderPriceId(priceId) ? priceId : null;
}
var INDIA_COURSE_MONTHLY_PAISE = 29900;
var INDIA_COURSE_QUARTERLY_PAISE = 79900;
var INDIA_COURSE_ANNUAL_PAISE = 299900;
var INDIA_PREMIUM_MONTHLY_PAISE = 199900;
var INDIA_PREMIUM_ANNUAL_PAISE = 1999e3;
var INDIA_LEGACY_COURSE_ANNUAL_PAISE = 299e3;
var INDIA_LEGACY_PREMIUM_MONTHLY_PAISE = 19e4;
var INDIA_LEGACY_PREMIUM_ANNUAL_PAISE = 19e5;
var INDIA_FAMILY_MONTHLY_PAISE = 32e4;
var INDIA_FAMILY_ANNUAL_PAISE = 32e5;
var INDIA_COURSES = [
  { courseCode: "eng_for_ben", speakers: "Bengali", monthly: "pri_01m3pqdpqd689nh7dh6w8tf0ek", quarterly: "pri_01m5z3n7brmadsbn2qztw48x7q", annual: "pri_01m5z3n7mh7knz02wc3ny1by0b", legacyAnnual: "pri_01m3pqdpwbtkcd2rg6asc9jv5d" },
  { courseCode: "eng_for_guj", speakers: "Gujarati", monthly: "pri_01m3pqdq7x663ea0m0eyq7wxqa", quarterly: "pri_01m5z3n7x8d22bb6fcgygekksw", annual: "pri_01m5z3n8g76m3ct2cgv96cmjg0", legacyAnnual: "pri_01m3pqdqcn6qb3y113xz7x399a" },
  { courseCode: "eng_for_hin", speakers: "Hindi", monthly: "pri_01m3pqdqqtg3vy4yv438cdq9g4", quarterly: "pri_01m5z3n5b90qqwxax89q0rfx7c", annual: "pri_01m5z3n8xfw8t4se1s9vydfztd", legacyAnnual: "pri_01m3pqdqx0nxqp6n12h09842yb" },
  { courseCode: "eng_for_kan", speakers: "Kannada", monthly: "pri_01m3pqdr8sr9qhrsrts2v7tpw7", quarterly: "pri_01m5z3n96f1n57638j7xbc2ahd", annual: "pri_01m5z3n9f9q56ddap3ys8m3rcc", legacyAnnual: "pri_01m3pqdret8zzxqs1mgnrmb3f9" },
  { courseCode: "eng_for_mar", speakers: "Marathi", monthly: "pri_01m3pqdrt0g0pp3ag4h4ss796s", quarterly: "pri_01m5z3n9rypqah9z7cc877n4g2", annual: "pri_01m5z3na1hhn3brpcs8skxbwj7", legacyAnnual: "pri_01m3pqdryjc6qna207yrpe0hv4" },
  { courseCode: "eng_for_pan", speakers: "Punjabi", monthly: "pri_01m3pqdsa5hjf692k944hxg8x0", quarterly: "pri_01m5z3naaf245p2m21ty16p3jn", annual: "pri_01m5z3naknsgv3pw7nxr3jnre9", legacyAnnual: "pri_01m3pqdsev0cv90p1xyr4te46b" },
  { courseCode: "eng_for_sin", speakers: "Sinhala", monthly: "pri_01m3yfnv4q0p8yz58eyqn36frm", quarterly: "pri_01m5z3nawb0msx511r3e3tds7d", annual: "pri_01m5z3nb5fd3859pcgra0qkc7d", legacyAnnual: "pri_01m3yfnv9k50e2rnr59ddfstye" },
  { courseCode: "eng_for_tam", speakers: "Tamil", monthly: "pri_01m3pqdsse32sz5d9qw831saps", quarterly: "pri_01m5z3nbeax8yk9784qxtm5tqg", annual: "pri_01m5z3nbqd95h1mts1f7arbbfq", legacyAnnual: "pri_01m3pqdsy5m7bdd35xmsjwkns9" },
  { courseCode: "eng_for_tel", speakers: "Telugu", monthly: "pri_01m3pqdtaqw5t2ctjjxvxr0r0n", quarterly: "pri_01m5z3nbzzty6bqe7hj22m0wj0", annual: "pri_01m5z3nc917daqcg6g27y61tjc", legacyAnnual: "pri_01m3pqdtf5a6bk2a5k1bfb21mx" },
  { courseCode: "eng_for_urd", speakers: "Urdu", monthly: "pri_01m3pqdtsyzrk7b43fvdan1ewr", quarterly: "pri_01m5z3nchsz46qb5ff8sg46q5w", annual: "pri_01m5z3nctka6xryj04zcnakv9b", legacyAnnual: "pri_01m3pqdtyqe18xgz8n2mky6pgw" }
];
var INDIA_PREMIUM = { monthly: "pri_01m5z3nd3j2mq639rrhnww3npp", annual: "pri_01m5z3ndcmt9mkh8pfh9qetqg7" };
var INDIA_PREMIUM_LEGACY = { monthly: "pri_01m3pqn7f9keh40e67rc9zbys2", annual: "pri_01m3pqn7m0vkgr9w801rqbzb07" };
var INDIA_FAMILY = { monthly: "pri_01m3pqn89k47yssys4yrmpxqrg", annual: "pri_01m3pqn8era1ez55shwep5wtgq" };
var INDIA_PRICES = Object.freeze(
  Object.fromEntries(
    [
      ...INDIA_COURSES.flatMap((c) => [
        { priceId: c.monthly, kind: "course", period: "monthly", amountPaise: INDIA_COURSE_MONTHLY_PAISE, courseCode: c.courseCode },
        { priceId: c.quarterly, kind: "course", period: "quarterly", amountPaise: INDIA_COURSE_QUARTERLY_PAISE, courseCode: c.courseCode },
        { priceId: c.annual, kind: "course", period: "annual", amountPaise: INDIA_COURSE_ANNUAL_PAISE, courseCode: c.courseCode },
        { priceId: c.legacyAnnual, kind: "course", period: "annual", amountPaise: INDIA_LEGACY_COURSE_ANNUAL_PAISE, courseCode: c.courseCode, legacy: true }
      ]),
      { priceId: INDIA_PREMIUM.monthly, kind: "premium", period: "monthly", amountPaise: INDIA_PREMIUM_MONTHLY_PAISE },
      { priceId: INDIA_PREMIUM.annual, kind: "premium", period: "annual", amountPaise: INDIA_PREMIUM_ANNUAL_PAISE },
      { priceId: INDIA_PREMIUM_LEGACY.monthly, kind: "premium", period: "monthly", amountPaise: INDIA_LEGACY_PREMIUM_MONTHLY_PAISE, legacy: true },
      { priceId: INDIA_PREMIUM_LEGACY.annual, kind: "premium", period: "annual", amountPaise: INDIA_LEGACY_PREMIUM_ANNUAL_PAISE, legacy: true },
      { priceId: INDIA_FAMILY.monthly, kind: "family", period: "monthly", amountPaise: INDIA_FAMILY_MONTHLY_PAISE },
      { priceId: INDIA_FAMILY.annual, kind: "family", period: "annual", amountPaise: INDIA_FAMILY_ANNUAL_PAISE }
    ].filter((p) => !isPlaceholderPriceId(p.priceId)).map((p) => [p.priceId, p])
  )
);
function indiaPremiumChangeTarget(period) {
  return offeredIndiaPriceId(INDIA_PREMIUM[period]) ?? INDIA_PREMIUM_LEGACY[period];
}
var INDIA_COURSE_CODES = INDIA_COURSES.map((c) => c.courseCode);
function isIndiaCourse(courseCode) {
  return !!courseCode && INDIA_COURSE_CODES.includes(courseCode);
}
function formatRupees(paise) {
  return `\u20B9${Math.round(paise / 100).toLocaleString("en-IN")}`;
}

// src/pricing/storeCourseCatalog.ts
var INDIA_STORE_COURSES = INDIA_COURSES.map((c) => ({
  courseCode: c.courseCode,
  play: `course_${c.courseCode}`,
  ios: {
    monthly: `com.saysomethingin.app.course.${c.courseCode}.monthly`,
    annual: `com.saysomethingin.app.course.${c.courseCode}.annual`
  }
}));
var STORE_COURSE_BASE_PLANS = {
  monthly: "monthly",
  annual: "annual"
};
function courseOfStoreProduct(productId) {
  if (!productId) return null;
  const c = INDIA_STORE_COURSES.find((x) => x.play === productId || x.ios.monthly === productId || x.ios.annual === productId);
  return c ? c.courseCode : null;
}
function iosStoreCoursePeriod(productId) {
  if (!productId) return null;
  for (const c of INDIA_STORE_COURSES) {
    if (c.ios.monthly === productId) return "monthly";
    if (c.ios.annual === productId) return "annual";
  }
  return null;
}

// src/platform/platformStatus.ts
var PLATFORM_TRIAL_GRACE_MS = 24 * 60 * 60 * 1e3;
function isPlatformActive(status, expiresAt, createdAt) {
  if (status == null) return true;
  if (status === "active") return true;
  if (status === "trial") {
    if (expiresAt) return new Date(expiresAt).getTime() > Date.now();
    return isWithinTrialGrace(createdAt);
  }
  return false;
}
function isWithinTrialGrace(createdAt) {
  if (!createdAt) return false;
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < PLATFORM_TRIAL_GRACE_MS;
}
function isUnstampedTrialLapsed(status, expiresAt, createdAt) {
  return status === "trial" && !expiresAt && !isWithinTrialGrace(createdAt);
}

// src/courses/displayName.ts
var LANGUAGE_NAMES = {
  eng: "English",
  spa: "Spanish",
  fra: "French",
  deu: "German",
  ita: "Italian",
  por: "Portuguese",
  zho: "Chinese",
  jpn: "Japanese",
  ara: "Arabic",
  kor: "Korean",
  cym: "Welsh",
  gle: "Irish",
  glv: "Manx",
  cor: "Cornish",
  eus: "Basque",
  cat: "Catalan",
  hin: "Hindi",
  tam: "Tamil",
  nld: "Dutch",
  dan: "Danish",
  nor: "Norwegian",
  ell: "Greek",
  hrv: "Croatian",
  // The rest of the live catalogue (2026-10-16), so a course named from its
  // code reads "X for Y Speakers" for every course the app sells.
  afr: "Afrikaans",
  hye: "Armenian",
  ben: "Bengali",
  bul: "Bulgarian",
  ces: "Czech",
  est: "Estonian",
  glg: "Galician",
  guj: "Gujarati",
  heb: "Hebrew",
  hun: "Hungarian",
  isl: "Icelandic",
  kan: "Kannada",
  lav: "Latvian",
  lit: "Lithuanian",
  mar: "Marathi",
  nep: "Nepali",
  fas: "Persian",
  pol: "Polish",
  pan: "Punjabi",
  ron: "Romanian",
  rus: "Russian",
  srp: "Serbian",
  sin: "Sinhala",
  swa: "Swahili",
  swe: "Swedish",
  tel: "Telugu",
  tha: "Thai",
  tur: "Turkish",
  ukr: "Ukrainian",
  urd: "Urdu",
  // Regional variants the catalogue names as a whole language, spelt as the
  // course row spells them.
  deu_at: "Austrian German",
  por_br: "Brazilian Portuguese",
  spa_mx: "Mexican Spanish",
  ara_eg: "Egyptian Arabic",
  ara_lb: "Lebanese Arabic"
};
var LANGUAGE_VARIANT_NAMES = {
  cym_n: "North",
  cym_s: "South",
  nob: "Bokm\xE5l",
  nno: "Nynorsk"
};
function normaliseLangCode(code) {
  return code.trim().toLowerCase().replace(/-/g, "_");
}
function languageName(code) {
  const key = normaliseLangCode(code);
  if (Object.prototype.hasOwnProperty.call(LANGUAGE_NAMES, key)) return LANGUAGE_NAMES[key];
  const variant = Object.prototype.hasOwnProperty.call(LANGUAGE_VARIANT_NAMES, key) ? LANGUAGE_VARIANT_NAMES[key] : void 0;
  if (variant) {
    const base = key === "nob" || key === "nno" ? "nor" : key.slice(0, key.lastIndexOf("_"));
    return `${languageName(base)} (${variant})`;
  }
  return key.charAt(0).toUpperCase() + key.slice(1);
}
var LANG_SLOT = "[a-z]{2,3}(?:_[a-z]{1,3})?";
var COURSE_CODE = new RegExp(`^(${LANG_SLOT})_for_(${LANG_SLOT})$`, "i");
var BARE_LANG = new RegExp(`^${LANG_SLOT}$`, "i");
function splitCourseCode(raw) {
  const match = raw.match(COURSE_CODE);
  if (!match) return null;
  return { target: match[1], known: match[2] };
}
function courseDisplayName(code) {
  if (!code) return "";
  const raw = code.trim();
  const parts = splitCourseCode(raw);
  if (parts) {
    return `${languageName(parts.target)} for ${languageName(parts.known)} Speakers`;
  }
  if (BARE_LANG.test(raw)) return languageName(raw);
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}
function courseShortName(code) {
  if (!code) return "";
  const raw = code.trim();
  const parts = splitCourseCode(raw);
  if (parts) return languageName(parts.target);
  if (BARE_LANG.test(raw)) return languageName(raw);
  return courseDisplayName(raw);
}

// src/script/courseBundle.ts
function phrasesByLego(bundle) {
  const map = /* @__PURE__ */ new Map();
  for (const phrase of bundle.phrases) {
    const list = map.get(phrase.legoId);
    if (list) list.push(phrase);
    else map.set(phrase.legoId, [phrase]);
  }
  return map;
}
function phrasesByLegoAndRole(bundle) {
  const map = /* @__PURE__ */ new Map();
  for (const phrase of bundle.phrases) {
    if (!phrase.audio.known || !phrase.audio.target1 || !phrase.audio.target2) continue;
    let bucket = map.get(phrase.legoId);
    if (!bucket) {
      bucket = { build: [], use: [] };
      map.set(phrase.legoId, bucket);
    }
    bucket[phrase.role].push(phrase);
  }
  return map;
}
function legosById(bundle) {
  const map = /* @__PURE__ */ new Map();
  for (const lego of bundle.legos) {
    map.set(lego.legoId, lego);
  }
  return map;
}
function seedsBySeedId(bundle) {
  const map = /* @__PURE__ */ new Map();
  for (const seed of bundle.seeds) {
    map.set(seed.seedId, seed);
  }
  return map;
}

// src/script/computePauseDuration.ts
var DEFAULT_PAUSE_K = 2.8;
var DEFAULT_PAUSE_REACTION_MS = 800;
var DEFAULT_PAUSE_CONFIG = {
  pause_k: DEFAULT_PAUSE_K,
  pause_reaction_ms: DEFAULT_PAUSE_REACTION_MS,
  min_pause_ms: 1e3,
  max_pause_ms: 15e3
};
var FALLBACK_TARGET_MS = 2500;
function computePauseDuration(target1Ms, target2Ms, cfg) {
  const known1 = target1Ms > 0 ? target1Ms : 0;
  const known2 = target2Ms > 0 ? target2Ms : 0;
  const answer = known1 > 0 || known2 > 0 ? Math.max(0, (known1 + known2) / 2) : FALLBACK_TARGET_MS;
  const k = cfg.pause_k ?? DEFAULT_PAUSE_K;
  const reaction = cfg.pause_reaction_ms ?? DEFAULT_PAUSE_REACTION_MS;
  const calc = k * answer + reaction;
  return Math.max(cfg.min_pause_ms, Math.min(cfg.max_pause_ms, Math.round(calc)));
}

// src/script/voicePace.ts
var MIN_SPEED = 0.7;
var MAX_SPEED = 1;
var HUMAN_VOICED_LANGUAGES = Object.freeze(["cym_n", "cym_s"]);
function isHumanVoicedCourse(courseCode) {
  const target = String(courseCode ?? "").toLowerCase().split("_for_")[0];
  return !!target && HUMAN_VOICED_LANGUAGES.includes(target);
}
var ROLE_ALIASES = Object.freeze({
  target: "target",
  target1: "target",
  target2: "target",
  known: "known",
  presentation: "known",
  source: "known",
  listening: "listening",
  listen: "listening"
});
function toNumber(v) {
  if (v === null || v === void 0 || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function targetPace(role, mode) {
  const r = ROLE_ALIASES[String(role ?? "").toLowerCase()] ?? null;
  const m = String(mode ?? "").toLowerCase() === "fast" ? "fast" : "easy";
  if (r === "listening") {
    return {
      targetPace: 1,
      correctByVoice: false,
      role: r,
      mode: m,
      reason: "listening exercise \u2014 full speed in any language, because listening trains them for everyday life"
    };
  }
  if (r === "known") {
    return {
      targetPace: 1,
      correctByVoice: false,
      role: r,
      mode: m,
      reason: "known language \u2014 instruction they already understand, played exactly as rendered"
    };
  }
  if (r === "target") {
    const t = m === "fast" ? 0.9 : 0.8;
    return {
      targetPace: t,
      correctByVoice: false,
      role: r,
      mode: m,
      reason: `target language on ${m} \u2014 ${t} times the recording`
    };
  }
  return {
    targetPace: 1,
    correctByVoice: false,
    role: null,
    mode: m,
    reason: `unrecognised role "${role}" \u2014 played as rendered rather than guessed at`
  };
}
function effectivePaceRatio(voice) {
  if (!voice) return null;
  const pre = toNumber(voice.effectivePaceRatio);
  if (pre !== null) return pre > 0 ? pre : null;
  const measured = toNumber(voice.natural_pace_ratio ?? voice.naturalPaceRatio);
  if (measured === null || measured <= 0) return null;
  const nudge = toNumber(voice.natural_pace_nudge ?? voice.naturalPaceNudge);
  if (nudge === null || nudge <= 0) return measured;
  return measured * nudge;
}
function paceMultiplier(args) {
  const target = toNumber(args.targetPace);
  if (target === null || target <= 0) {
    return { speed: 1, corrected: false, clamped: false, reason: "no target pace given" };
  }
  return {
    speed: target,
    corrected: false,
    clamped: false,
    reason: "flat recording multiplier \u2014 Tom 2026-10-18"
  };
}
function playbackSpeedForVoice(_voice, role, mode, courseCode) {
  const policy = targetPace(role, mode);
  if (isHumanVoicedCourse(courseCode)) {
    return {
      ...policy,
      targetPace: 1,
      speed: 1,
      corrected: false,
      clamped: false,
      reason: "human-voiced language \u2014 every clip plays at 1.0, never slowed"
    };
  }
  return { ...policy, speed: policy.targetPace, corrected: false, clamped: false };
}
function voicePaceForSlot(pace, slot) {
  if (!pace || pace.unavailable) return null;
  const direct = pace.roles?.[slot];
  if (direct?.primary) return direct.primary;
  if (slot === "target1" || slot === "target2") {
    const other = slot === "target1" ? pace.roles?.target2 : pace.roles?.target1;
    if (other?.primary) return other.primary;
  }
  return null;
}

// src/script/phraseSelection.ts
function countTargetSyllables(targetText) {
  if (!targetText) return 0;
  const cjkRegex = /[一-鿿㐀-䶿぀-ゟ゠-ヿ가-힯]/g;
  const cjkChars = targetText.match(cjkRegex);
  if (cjkChars && cjkChars.length > 0) return cjkChars.length;
  const vowelClusters = targetText.toLowerCase().match(/[aeiouyáéíóúàèìòùâêîôûäëïöü]+/gi);
  return vowelClusters ? vowelClusters.length : 1;
}
function phraseTextLength(text) {
  return (text || "").length;
}
function courseMaxPhraseLength(phraseLists, lengthOf) {
  let max = 0;
  for (const list of phraseLists) {
    if (!list) continue;
    for (const p of list) {
      const n = lengthOf(p);
      if (n > max) max = n;
    }
  }
  return max;
}
var MIN_BUILD_PHRASES_AFTER_CAP = 4;
var MIN_USE_PHRASES_AFTER_CAP = 5;
function capPhrasesByLength(phrases, syllablesOf, lengthOf, limit, minKeep) {
  const sorted = phrases.length < 2 ? [...phrases] : phrases.map((phrase) => ({ phrase, syllables: syllablesOf(phrase) })).sort((a, b) => a.syllables - b.syllables).map(({ phrase }) => phrase);
  if (!Number.isFinite(limit) || limit <= 0 || sorted.length === 0) return sorted;
  if (minKeep >= sorted.length) return sorted;
  const capped = sorted.filter((phrase) => lengthOf(phrase) <= limit);
  return capped.length >= Math.min(minKeep, sorted.length) ? capped : sorted.slice(0, minKeep);
}
function filterReviewPool(pool, roundNumber, filter) {
  if (!filter || !Number.isFinite(filter.limit) || filter.limit <= 0) return pool;
  if (roundNumber > filter.maxRound) return pool;
  if (pool.length === 0) return pool;
  const kept = [];
  let shortest = pool[0];
  let shortestN = Infinity;
  for (const phrase of pool) {
    const n = filter.syllablesOf(phrase);
    if (typeof n !== "number" || !Number.isFinite(n)) {
      kept.push(phrase);
      continue;
    }
    if (n <= filter.limit) kept.push(phrase);
    if (n < shortestN) {
      shortestN = n;
      shortest = phrase;
    }
  }
  return kept.length > 0 ? kept : [shortest];
}
function reviewCursorStart(offsetIndex, poolLength, n1PhraseCount) {
  if (offsetIndex <= 0 || poolLength <= 0) return 0;
  return Math.min(n1PhraseCount, poolLength) + (offsetIndex - 1);
}
function orderLegoPools(build, use, opts) {
  const { eligible, syllablesOf, lengthOf, limit } = opts;
  return {
    build: capPhrasesByLength(
      build.filter(eligible),
      syllablesOf,
      lengthOf,
      limit,
      MIN_BUILD_PHRASES_AFTER_CAP
    ),
    use: capPhrasesByLength(
      use.filter(eligible),
      syllablesOf,
      lengthOf,
      limit,
      MIN_USE_PHRASES_AFTER_CAP
    )
  };
}
function selectDebutPhrases(pools, opts) {
  const build = [];
  for (const pool of [pools.build, pools.use]) {
    for (let i = 0; i < pool.length && build.length < opts.maxBuildPhrases; i++) {
      const phrase = pool[i];
      if (!opts.claim(phrase)) continue;
      build.push(phrase);
    }
  }
  const selectConsolidate = () => {
    const pool = opts.consolidatePool ?? pools.use;
    const consolidate = [];
    for (const phrase of pool) {
      if (consolidate.length >= opts.useConsolidationCount) break;
      if (!opts.claim(phrase)) continue;
      consolidate.push(phrase);
    }
    if (consolidate.length < opts.useConsolidationCount) {
      for (const phrase of pool) {
        if (consolidate.length >= opts.useConsolidationCount) break;
        if (opts.isBareLego(phrase)) continue;
        consolidate.push(phrase);
      }
    }
    return consolidate;
  };
  return { build, selectConsolidate };
}
function drawReviewPhrases(pool, offsetIndex, take, n1PhraseCount, claim) {
  if (pool.length === 0 || take <= 0) return [];
  let cursor = reviewCursorStart(offsetIndex, pool.length, n1PhraseCount);
  const drawn = [];
  for (let i = 0; i < take; i++) {
    const phrase = pool[cursor % pool.length];
    cursor++;
    drawn.push(claim(phrase) ? phrase : null);
  }
  return drawn;
}

// src/script/generateScript.ts
var GENERATOR_VERSION = 2;
var DEFAULT_ROUND_LIMIT = 15;
var DEFAULT_SPACED_REP_OFFSETS = [1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144];
var REVIEW_OFFSET_CEILING = 233;
var SEED_REVIEW_START_OFFSET = 89;
var reviewOffsets = (offsets) => offsets.filter((o) => o < REVIEW_OFFSET_CEILING);
var DEFAULT_SCRIPT_SHAPE = {
  spacedRepOffsets: DEFAULT_SPACED_REP_OFFSETS,
  maxBuildPhrases: 7,
  useConsolidationCount: 2,
  maxSpacedRepPhrases: 12,
  n1PhraseCount: 3
};
function seedReviewAnchors(roundMap) {
  const lastBySeed = /* @__PURE__ */ new Map();
  for (const entry of roundMap) lastBySeed.set(entry.seedNumber, entry.legoId);
  return new Set(lastBySeed.values());
}
function resolveShape(bundle, override) {
  const fromBundle = bundle.scriptShape;
  const merged = {
    ...DEFAULT_SCRIPT_SHAPE,
    ...fromBundle ?? {},
    ...override ?? {}
  };
  return { ...merged, spacedRepOffsets: reviewOffsets(merged.spacedRepOffsets) };
}
function normalizeForKey(text) {
  if (!text) return "";
  return text.toLowerCase().trim().replace(
    /[.,!?;:\u00a1\u00bf'"\u3000-\u303f\uff00-\uff0f\uff1a-\uff20\uff3b-\uff40\uff5b-\uff65]+/g,
    ""
  );
}
function phraseKey(knownText, targetText) {
  return `${normalizeForKey(knownText)}|${normalizeForKey(targetText)}`;
}
function phraseIsPlayable(p) {
  return !!(p.audio.known && p.audio.target1 && p.audio.target2);
}
function phraseSyllables(p) {
  return p.targetSyllableCount || countTargetSyllables(p.targetTextNative ?? p.targetText);
}
function phraseLength(p) {
  return phraseTextLength(p.targetTextNative ?? p.targetText);
}
function makePoolOrderer(phraseIndex) {
  const memo = /* @__PURE__ */ new Map();
  return (legoId) => {
    const hit = memo.get(legoId);
    if (hit) return hit;
    const phrases = phraseIndex.get(legoId);
    const ordered = orderLegoPools(phrases?.build ?? [], phrases?.use ?? [], {
      eligible: phraseIsPlayable,
      syllablesOf: phraseSyllables,
      lengthOf: phraseLength,
      limit: Infinity
    });
    memo.set(legoId, ordered);
    return ordered;
  };
}
var INTRO_LINGER_MS = 2e3;
var TARGET_CYCLES_PER_ROUND = 22;
var MIN_RANDOM_USE_PER_ROUND = 6;
var MAX_RANDOM_USE_PER_ROUND = TARGET_CYCLES_PER_ROUND;
function generateScript(opts) {
  const roundLimit = opts.roundLimit ?? DEFAULT_ROUND_LIMIT;
  const audioUrl = opts.audioUrl ?? defaultAudioUrl;
  const random = opts.random ?? Math.random;
  const pauseConfig = opts.pauseConfig ?? DEFAULT_PAUSE_CONFIG;
  const shape = resolveShape(opts.bundle, opts.shape);
  if (opts.position.mode === "main") {
    return generateMain(opts.bundle, opts.position, roundLimit, audioUrl, pauseConfig, shape);
  }
  return generateInfPlay(opts.bundle, opts.position, roundLimit, audioUrl, random, pauseConfig, shape);
}
function generateMain(bundle, position, roundLimit, audioUrl, pauseConfig, shape) {
  const roundMap = bundle.roundMap;
  const startIdx = roundMap.findIndex((e) => e.legoId === position.fromLegoId);
  if (startIdx < 0) {
    throw new Error(`legoId not in round map: ${position.fromLegoId}`);
  }
  const legoIndex = legosById(bundle);
  const phraseIndex = phrasesByLegoAndRole(bundle);
  const seedIndex = seedsBySeedId(bundle);
  const anchors = seedReviewAnchors(bundle.roundMap);
  const orderedPoolsFor = makePoolOrderer(phraseIndex);
  const rounds = [];
  let lastEmittedIdx = startIdx - 1;
  for (let i = 0; i < roundLimit; i++) {
    const mapIdx = startIdx + i;
    if (mapIdx >= roundMap.length) break;
    const entry = roundMap[mapIdx];
    const lego = legoIndex.get(entry.legoId);
    if (!lego) {
      lastEmittedIdx = mapIdx;
      continue;
    }
    const cycles = [];
    const introCycle = buildIntroCycle(lego, audioUrl);
    if (introCycle) cycles.push(introCycle);
    const debutCycle = buildDebutCycle(lego, audioUrl, pauseConfig);
    if (debutCycle) cycles.push(debutCycle);
    const claimed = /* @__PURE__ */ new Set([phraseKey(lego.knownText, lego.targetText)]);
    const claim = (p) => {
      const key = phraseKey(p.knownText, p.targetText);
      if (claimed.has(key)) return false;
      claimed.add(key);
      return true;
    };
    const pools = orderedPoolsFor(entry.legoId);
    const debut = selectDebutPhrases(pools, {
      maxBuildPhrases: shape.maxBuildPhrases,
      useConsolidationCount: shape.useConsolidationCount,
      claim,
      isBareLego: (p) => phraseKey(p.knownText, p.targetText) === phraseKey(lego.knownText, lego.targetText)
    });
    let buildOrdinal = 0;
    for (const phrase of debut.build) {
      buildOrdinal++;
      const cyc = buildPhraseCycle(phrase, lego, "build", buildOrdinal, audioUrl, pauseConfig);
      if (cyc) cycles.push(cyc);
    }
    const spacedRepCycles = buildSpacedRepCycles(
      entry.roundIndex,
      bundle,
      legoIndex,
      orderedPoolsFor,
      seedIndex,
      anchors,
      audioUrl,
      pauseConfig,
      shape,
      entry.legoId,
      claim
    );
    cycles.push(...spacedRepCycles);
    let useOrdinal = 0;
    for (const phrase of debut.selectConsolidate()) {
      useOrdinal++;
      const cyc = buildPhraseCycle(phrase, lego, "use", useOrdinal, audioUrl, pauseConfig);
      if (cyc) cycles.push(cyc);
    }
    if (cycles.length === 0) {
      lastEmittedIdx = mapIdx;
      continue;
    }
    rounds.push({
      roundNumber: entry.roundIndex,
      legoId: entry.legoId,
      seedId: lego.seedId,
      legoTargetText: lego.targetText,
      ...lego.targetTextNative ? { legoTargetTextNative: lego.targetTextNative } : {},
      legoKnownText: lego.knownText,
      revival: false,
      cycles
    });
    lastEmittedIdx = mapIdx;
  }
  const nextIdx = lastEmittedIdx + 1;
  const nextLegoId = nextIdx < roundMap.length ? roundMap[nextIdx].legoId : null;
  return {
    rounds,
    next: { mode: "main", legoId: nextLegoId }
  };
}
function buildSpacedRepCycles(currentRoundIndex, bundle, legoIndex, orderedPoolsFor, seedIndex, anchors, audioUrl, pauseConfig, shape, currentLegoId, claim) {
  const cycles = [];
  const seenLegos = /* @__PURE__ */ new Set();
  let repCount = 0;
  for (let offsetIndex = 0; offsetIndex < shape.spacedRepOffsets.length; offsetIndex++) {
    const offset = shape.spacedRepOffsets[offsetIndex];
    const isSeedRung = offset >= SEED_REVIEW_START_OFFSET;
    if (!isSeedRung && repCount >= shape.maxSpacedRepPhrases) break;
    const reviewRoundIndex = currentRoundIndex - offset;
    if (reviewRoundIndex < 1) break;
    const mapEntry = bundle.roundMap[reviewRoundIndex - 1];
    if (!mapEntry) continue;
    if (mapEntry.legoId === currentLegoId) continue;
    if (seenLegos.has(mapEntry.legoId)) continue;
    seenLegos.add(mapEntry.legoId);
    const lego = legoIndex.get(mapEntry.legoId);
    if (!lego) continue;
    if (isSeedRung) {
      if (!anchors.has(lego.legoId)) continue;
      const seed = seedIndex.get(lego.seedId);
      const seedCycle = seed ? buildSeedReviewCycle(seed, lego, `${lego.legoId}_seedrep_${offset}`, audioUrl, pauseConfig) : null;
      if (seedCycle) {
        repCount++;
        cycles.push(seedCycle);
        continue;
      }
      if (repCount >= shape.maxSpacedRepPhrases) continue;
    }
    const pool = orderedPoolsFor(mapEntry.legoId).use;
    if (pool.length === 0) continue;
    const want = offsetIndex === 0 ? shape.n1PhraseCount : 1;
    const take = Math.min(want, shape.maxSpacedRepPhrases - repCount, pool.length);
    for (const phrase of drawReviewPhrases(pool, offsetIndex, take, shape.n1PhraseCount, claim)) {
      if (!phrase) continue;
      repCount++;
      const cyc = buildPhraseCycle(phrase, lego, "review", repCount, audioUrl, pauseConfig);
      if (cyc) cycles.push(cyc);
    }
  }
  return cycles;
}
function generateInfPlay(bundle, position, roundLimit, audioUrl, random, pauseConfig, shape) {
  const mainLoopCount = bundle.mainLoopCount;
  const roundMap = bundle.roundMap;
  const legoIndex = legosById(bundle);
  const orderedPoolsFor = makePoolOrderer(phrasesByLegoAndRole(bundle));
  const seedIndex = seedsBySeedId(bundle);
  const anchors = seedReviewAnchors(roundMap);
  const rounds = [];
  for (let r = 0; r < roundLimit; r++) {
    const infRound = position.fromInfRound + r;
    const absoluteRound = mainLoopCount + infRound;
    const usedLegosThisRound = /* @__PURE__ */ new Set();
    const spacedRepEntries = [];
    for (let offsetIndex = 0; offsetIndex < shape.spacedRepOffsets.length; offsetIndex++) {
      const offset = shape.spacedRepOffsets[offsetIndex];
      const reviewRound = absoluteRound - offset;
      if (reviewRound < 1) continue;
      if (reviewRound > mainLoopCount) continue;
      const mapEntry = roundMap[reviewRound - 1];
      if (!mapEntry) continue;
      if (usedLegosThisRound.has(mapEntry.legoId)) continue;
      const lego = legoIndex.get(mapEntry.legoId);
      if (!lego) continue;
      usedLegosThisRound.add(mapEntry.legoId);
      spacedRepEntries.push({
        lego,
        offset,
        // The LEGO's position in ITS OWN review schedule, which is what the urn
        // cursor is a function of. `offset` here IS `absoluteRound - debutRound`
        // because the review round is looked up by that offset, so the index
        // into the Fibonacci array is the count of reviews this LEGO has had —
        // exactly as in the main loop.
        offsetIndex,
        phraseCount: offset === 1 ? shape.n1PhraseCount : 1
      });
    }
    const cycles = [];
    let cycleSeq = 0;
    for (const { lego, offset, offsetIndex, phraseCount } of spacedRepEntries) {
      if (offset >= SEED_REVIEW_START_OFFSET) {
        if (!anchors.has(lego.legoId)) continue;
        const seed = seedIndex.get(lego.seedId);
        const seedCycle = seed ? buildSeedReviewCycle(
          seed,
          lego,
          `${lego.legoId}_infseedrep_R${infRound}_${cycleSeq + 1}`,
          audioUrl,
          pauseConfig
        ) : null;
        if (seedCycle) {
          cycleSeq++;
          cycles.push(seedCycle);
          continue;
        }
      }
      const pool = orderedPoolsFor(lego.legoId).use;
      if (pool.length === 0) continue;
      const take = Math.min(phraseCount, pool.length);
      for (const phrase of drawReviewPhrases(pool, offsetIndex, take, shape.n1PhraseCount, () => true)) {
        if (!phrase) continue;
        cycleSeq++;
        const cyc = buildInfPlayCycle(phrase, lego, "review", infRound, cycleSeq, audioUrl, pauseConfig);
        if (cyc) cycles.push(cyc);
      }
    }
    const targetRandomUse = Math.max(
      MIN_RANDOM_USE_PER_ROUND,
      Math.min(MAX_RANDOM_USE_PER_ROUND, TARGET_CYCLES_PER_ROUND - cycles.length)
    );
    const availableLegos = [];
    for (let i = 0; i < roundMap.length; i++) {
      const entry = roundMap[i];
      if (usedLegosThisRound.has(entry.legoId)) continue;
      const lego = legoIndex.get(entry.legoId);
      if (!lego) continue;
      availableLegos.push(lego);
    }
    const randomUseLegos = sampleN(availableLegos, targetRandomUse, random);
    for (const l of randomUseLegos) usedLegosThisRound.add(l.legoId);
    for (const lego of randomUseLegos) {
      const pool = orderedPoolsFor(lego.legoId).use;
      if (pool.length === 0) continue;
      const phrase = pool[Math.floor(random() * pool.length)];
      cycleSeq++;
      const cyc = buildInfPlayCycle(phrase, lego, "use", infRound, cycleSeq, audioUrl, pauseConfig);
      if (cyc) cycles.push(cyc);
    }
    if (cycles.length === 0) continue;
    const firstCycle = cycles[0];
    rounds.push({
      roundNumber: mainLoopCount + infRound,
      legoId: firstCycle.legoId ?? "",
      seedId: firstCycle.legoId ? legoIndex.get(firstCycle.legoId)?.seedId ?? "" : "",
      legoTargetText: "",
      legoKnownText: "",
      revival: true,
      cycles
    });
  }
  return {
    rounds,
    next: { mode: "infplay", infRound: position.fromInfRound + rounds.length }
  };
}
function buildIntroCycle(lego, audioUrl) {
  const target1 = lego.ephemeralAudio.target1;
  const target2 = lego.ephemeralAudio.target2;
  if (!target1 || !target2) return null;
  const presentation = lego.ephemeralAudio.presentation;
  const promptId = presentation?.id ?? lego.ephemeralAudio.known?.id;
  const promptUrl = promptId ? audioUrl(promptId) : "";
  const knownId = lego.ephemeralAudio.known?.id;
  const fallbackUrl = knownId ? audioUrl(knownId) : void 0;
  return baseCycle({
    id: `${lego.legoId}_intro`,
    type: "intro",
    legoId: lego.legoId,
    seedId: lego.seedId,
    knownText: lego.knownText,
    knownAudioUrl: promptUrl,
    knownFallbackUrl: fallbackUrl,
    targetText: lego.targetText,
    targetTextNative: lego.targetTextNative,
    target1,
    target2,
    audioUrl,
    pauseDuration: 0,
    lingerMs: INTRO_LINGER_MS,
    components: lego.components,
    glossSegments: lego.glossSegments
  });
}
function buildDebutCycle(lego, audioUrl, pauseConfig) {
  const known = lego.ephemeralAudio.known;
  const target1 = lego.ephemeralAudio.target1;
  const target2 = lego.ephemeralAudio.target2;
  if (!known || !target1 || !target2) return null;
  return baseCycle({
    id: `${lego.legoId}_debut`,
    type: "debut",
    legoId: lego.legoId,
    seedId: lego.seedId,
    knownText: lego.knownText,
    knownAudioUrl: audioUrl(known.id),
    targetText: lego.targetText,
    targetTextNative: lego.targetTextNative,
    target1,
    target2,
    audioUrl,
    pauseDuration: computePauseDuration(
      target1.durationMs ?? 0,
      target2.durationMs ?? 0,
      pauseConfig
    ),
    components: lego.components,
    glossSegments: lego.glossSegments
  });
}
function buildPhraseCycle(phrase, lego, cycleType, ordinal, audioUrl, pauseConfig) {
  const known = phrase.audio.known;
  const target1 = phrase.audio.target1;
  const target2 = phrase.audio.target2;
  if (!known || !target1 || !target2) return null;
  return baseCycle({
    id: `${phrase.phraseId}_${cycleType}_${ordinal}`,
    type: cycleType,
    legoId: lego.legoId,
    seedId: lego.seedId,
    knownText: phrase.knownText,
    knownAudioUrl: audioUrl(known.id),
    targetText: phrase.targetText,
    targetTextNative: phrase.targetTextNative,
    target1,
    target2,
    audioUrl,
    pauseDuration: computePauseDuration(
      target1.durationMs ?? 0,
      target2.durationMs ?? 0,
      pauseConfig
    ),
    displayTiling: phrase.displayTiling,
    decomposition: phrase.decomposition
  });
}
function buildSeedReviewCycle(seed, lego, id, audioUrl, pauseConfig) {
  const known = seed.audio?.known;
  const target1 = seed.audio?.target1;
  const target2 = seed.audio?.target2;
  if (!known || !target1 || !target2) return null;
  return baseCycle({
    id,
    type: "review",
    legoId: lego.legoId,
    seedId: seed.seedId,
    knownText: seed.knownText ?? "",
    knownAudioUrl: audioUrl(known.id),
    targetText: seed.targetText ?? "",
    targetTextNative: seed.targetTextNative,
    target1,
    target2,
    audioUrl,
    pauseDuration: computePauseDuration(
      target1.durationMs ?? 0,
      target2.durationMs ?? 0,
      pauseConfig
    )
  });
}
function buildInfPlayCycle(phrase, lego, cycleType, infRound, seq, audioUrl, pauseConfig) {
  const known = phrase.audio.known;
  const target1 = phrase.audio.target1;
  const target2 = phrase.audio.target2;
  if (!known || !target1 || !target2) return null;
  const idPrefix = cycleType === "review" ? "infsr" : "infuse";
  return baseCycle({
    id: `${lego.legoId}_${idPrefix}_R${infRound}_${seq}`,
    type: cycleType,
    legoId: lego.legoId,
    seedId: lego.seedId,
    knownText: phrase.knownText,
    knownAudioUrl: audioUrl(known.id),
    targetText: phrase.targetText,
    targetTextNative: phrase.targetTextNative,
    target1,
    target2,
    audioUrl,
    pauseDuration: computePauseDuration(
      target1.durationMs ?? 0,
      target2.durationMs ?? 0,
      pauseConfig
    ),
    displayTiling: phrase.displayTiling,
    decomposition: phrase.decomposition
  });
}
function baseCycle(o) {
  const cycle = {
    id: o.id,
    type: o.type,
    legoId: o.legoId,
    known: {
      text: o.knownText,
      audioUrl: o.knownAudioUrl,
      ...o.knownFallbackUrl ? { fallbackUrl: o.knownFallbackUrl } : {}
    },
    target: {
      text: o.targetText,
      ...o.targetTextNative ? { textNative: o.targetTextNative } : {},
      voice1Url: o.audioUrl(o.target1.id),
      voice2Url: o.audioUrl(o.target2.id)
    },
    pauseDuration: o.pauseDuration,
    ...o.lingerMs !== void 0 ? { lingerMs: o.lingerMs } : {},
    ...o.target1.durationMs ? { target1DurationMs: o.target1.durationMs } : {},
    ...o.target2.durationMs ? { target2DurationMs: o.target2.durationMs } : {},
    ...o.components && o.components.length > 0 ? { components: o.components } : {},
    ...o.glossSegments && o.glossSegments.length > 0 ? { glossSegments: o.glossSegments } : {},
    ...o.displayTiling && o.displayTiling.length > 0 ? { displayTiling: o.displayTiling } : {},
    ...o.decomposition && o.decomposition.length > 0 ? { decomposition: o.decomposition } : {}
  };
  return cycle;
}
function defaultAudioUrl(id) {
  return `/api/audio/${id}`;
}
function sampleN(arr, n, random) {
  if (n <= 0 || arr.length === 0) return [];
  if (n >= arr.length) return [...arr];
  const a = [...arr];
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(random() * (a.length - i));
    const tmp = a[i];
    a[i] = a[j];
    a[j] = tmp;
  }
  return a.slice(0, n);
}

// src/script/seedSentenceReview.ts
var SEED_SENTENCE_REVIEW_COURSES = /* @__PURE__ */ new Set([
  "cym_nv2_for_eng",
  "cym_sv2_for_eng"
]);
function seedSentenceReviewEnabled(courseCode) {
  return !!courseCode && SEED_SENTENCE_REVIEW_COURSES.has(courseCode);
}
var SEED_SENTENCE_OFFSETS = [1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144];
var SEED_SENTENCE_KIND = "seed_sentence";
function assignSeedSentenceHosts(walk) {
  const hosts = /* @__PURE__ */ new Map();
  const unhosted = [];
  let current = 0;
  for (const { seedNumber, lastNewRound } of walk) {
    if (lastNewRound !== null) {
      current = lastNewRound;
      continue;
    }
    if (current > 0) hosts.set(seedNumber, current);
    else unhosted.push(seedNumber);
  }
  return { hosts, unhosted };
}
function seedSentenceHostsByRound(seedOrder, roundMap, maxSeed = null) {
  const lastRoundBySeed = /* @__PURE__ */ new Map();
  for (const { roundIndex, seedNumber } of roundMap) {
    lastRoundBySeed.set(seedNumber, Math.max(lastRoundBySeed.get(seedNumber) ?? 0, roundIndex));
  }
  const order = seedOrder ?? [...lastRoundBySeed.keys()].sort((x, y) => x - y);
  const walk = order.filter((s) => maxSeed === null || s <= maxSeed).map((seedNumber) => ({ seedNumber, lastNewRound: lastRoundBySeed.get(seedNumber) ?? null }));
  return seedsByHostRound(assignSeedSentenceHosts(walk).hosts);
}
function seedsByHostRound(hosts) {
  const byRound = /* @__PURE__ */ new Map();
  for (const [seed, round] of hosts) {
    const list = byRound.get(round);
    if (list) list.push(seed);
    else byRound.set(round, [seed]);
  }
  return byRound;
}
function seedSentenceReviewsDue(round, byHostRound) {
  const due = [];
  for (let k = 0; k < SEED_SENTENCE_OFFSETS.length; k++) {
    const hostRound = round - SEED_SENTENCE_OFFSETS[k];
    if (hostRound < 1) break;
    for (const seedNumber of byHostRound.get(hostRound) ?? []) {
      due.push({ seedNumber, offset: SEED_SENTENCE_OFFSETS[k], offsetIndex: k, hostRound });
    }
  }
  return due;
}
function seedSentenceCycleId(seedNumber, offset) {
  return `S${String(seedNumber).padStart(4, "0")}_seedsent_${offset}`;
}
function seedHasAllClips(seed) {
  return !!(seed && seed.known_audio_id && seed.target1_audio_id && seed.target2_audio_id);
}

// src/text/syllables.ts
function countSyllablesHrv(text) {
  const words = (text || "").toLowerCase().replace(/[.,!?;:„"“”'’()\-–—…]/g, " ").split(/\s+/).filter(Boolean);
  let total = 0;
  for (const w of words) {
    const vowels = (w.match(/[aeiou]/g) || []).length;
    let syllabicR = 0;
    for (let i = 0; i < w.length; i++) {
      if (w[i] !== "r") continue;
      const prev = w[i - 1], next = w[i + 1];
      const prevVowel = prev && "aeiou".includes(prev);
      const nextVowel = next && "aeiou".includes(next);
      if (!prevVowel && !nextVowel) syllabicR++;
    }
    let count = vowels + syllabicR;
    if (count === 0) count = 1;
    total += count;
  }
  return total;
}
function countSyllablesEng(text) {
  const words = (text || "").toLowerCase().replace(/’/g, "'").replace(/[^a-z'\s]/g, " ").split(/\s+/).filter(Boolean);
  let total = 0;
  for (let w of words) {
    let extra = 0;
    if (w.endsWith("n't")) {
      const base = w.slice(0, -3);
      if (base && !"aeiou".includes(base[base.length - 1])) extra = 1;
      w = base + "nt";
    }
    w = w.replace(/'(s|ll|d|re|ve|m|em)$/, "").replace(/'/g, "");
    let count = 0, inRun = false;
    for (let i = 0; i < w.length; i++) {
      const isVowel = "aeiou".includes(w[i]) || w[i] === "y" && i > 0;
      if (isVowel) {
        if (!inRun) count++;
        inRun = true;
      } else inRun = false;
    }
    if (count > 1) {
      if (/[^aeiouy]e$/.test(w) && !/[^aeiouy]le$/.test(w)) count--;
      else if (/es$/.test(w) && !/[sxzcgh]es$/.test(w) && !/[^aeiouy]les$/.test(w)) count--;
      else if (/ed$/.test(w) && !/[td]ed$/.test(w)) count--;
    }
    total += Math.max(count, 1) + extra;
  }
  return total;
}
var DIGRAPH_SLOT = "\uE000";
function vowelGroupWordCount(word, vowelSet, digraphs, hiatusSet) {
  if (!word) return 0;
  let marked = word;
  for (const d of [...digraphs].sort((a, b) => b.length - a.length)) {
    marked = marked.split(d).join(DIGRAPH_SLOT);
  }
  let count = 0, inRun = false, prevHiatus = false;
  for (const ch of marked) {
    const isVowel = ch === DIGRAPH_SLOT || vowelSet.has(ch);
    const isHiatus = hiatusSet.has(ch);
    if (isVowel) {
      if (!inRun || isHiatus || prevHiatus) count++;
      inRun = true;
      prevHiatus = isHiatus;
    } else {
      inRun = false;
      prevHiatus = false;
    }
  }
  return count || 1;
}
function wordsOf(text) {
  return (text || "").toLowerCase().replace(/[.,!?;:„"“”'’()\-–—…¿¡]/g, " ").split(/\s+/).filter(Boolean);
}
function makeVowelGroupCounter(vowels, { digraphs = [], hiatusVowels = "", frenchSilentE = false } = {}) {
  const vowelSet = new Set(vowels);
  const hiatusSet = new Set(hiatusVowels);
  return (text) => {
    let total = 0;
    for (const w of wordsOf(text)) {
      let n = vowelGroupWordCount(w, vowelSet, digraphs, hiatusSet);
      if (frenchSilentE && n > 1 && /(?:^|[^aeiouyâàéèêëîïôùûüœæ])e$/.test(w)) n--;
      total += n;
    }
    return total;
  };
}
var SYLLABLE_REGISTRY = {
  hrv: countSyllablesHrv,
  eng: countSyllablesEng,
  spa: makeVowelGroupCounter("aeiou\xE1\xE9\xED\xF3\xFA\xFC", { hiatusVowels: "\xED\xFA" }),
  ita: makeVowelGroupCounter("aeiou\xE0\xE8\xE9\xEC\xED\xEE\xF2\xF3\xF9", { hiatusVowels: "\xEC\xF9" }),
  por: makeVowelGroupCounter("aeiou\xE1\xE0\xE2\xE3\xE9\xEA\xED\xF3\xF4\xF5\xFA\xFC", { hiatusVowels: "\xED\xFA" }),
  deu: makeVowelGroupCounter("aeiouy\xE4\xF6\xFC"),
  fra: makeVowelGroupCounter("aeiouy\xE2\xE0\xE9\xE8\xEA\xEB\xEE\xEF\xF4\xF9\xFB\xFC\u0153\xE6", { frenchSilentE: true }),
  nld: makeVowelGroupCounter("aeiouy", { digraphs: ["ij"] }),
  cym: makeVowelGroupCounter("aeiouwy")
};
function registerCounter(langCode, fn) {
  SYLLABLE_REGISTRY[langCode] = fn;
}
function hasSyllableCounter(langCode) {
  return Object.prototype.hasOwnProperty.call(SYLLABLE_REGISTRY, langCode) && typeof SYLLABLE_REGISTRY[langCode] === "function";
}
function countSyllables(text, langCode) {
  const fn = hasSyllableCounter(langCode) ? SYLLABLE_REGISTRY[langCode] : void 0;
  if (!fn) {
    throw new Error(`no syllable counter registered for language '${langCode}' \u2014 add one to packages/core/src/text/syllables.ts (SYLLABLE_REGISTRY), and mirror it into its twin ssi-dashboard-v7-clean/tools/lib/syllable-counters.cjs, before running this course`);
  }
  return fn(text);
}
function syllableLangOf(targetLang) {
  return String(targetLang || "").split(/[-_]/)[0].toLowerCase();
}

// src/text/direction.ts
var RTL_SCRIPT = /[\p{Script=Arabic}\p{Script=Hebrew}\p{Script=Syriac}\p{Script=Thaana}\p{Script=Nko}\p{Script=Samaritan}\p{Script=Mandaic}\p{Script=Adlam}\p{Script=Mende_Kikakui}\p{Script=Hanifi_Rohingya}]/u;
var LETTER = /\p{L}/u;
function countStrongLetters(text) {
  const counts = { rtl: 0, ltr: 0 };
  for (const char of text) {
    if (!LETTER.test(char)) continue;
    if (RTL_SCRIPT.test(char)) counts.rtl++;
    else counts.ltr++;
  }
  return counts;
}
function isRtlText(text) {
  if (!text) return false;
  if (!RTL_SCRIPT.test(text)) return false;
  const { rtl, ltr } = countStrongLetters(text);
  return rtl > 0 && rtl >= ltr;
}
function dirFor(text) {
  return isRtlText(text) ? "rtl" : "ltr";
}

// src/identity/supportId.ts
var ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
var HEX_DIGITS = 10;
var CODE_LENGTH = 8;
var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function supportIdForLearnerId(learnerId) {
  if (!learnerId || !UUID_RE.test(learnerId)) return null;
  const hex = learnerId.replace(/-/g, "").slice(0, HEX_DIGITS);
  let bits = BigInt("0x" + hex);
  const out = [];
  for (let i = 0; i < CODE_LENGTH; i++) {
    out.unshift(ALPHABET[Number(bits & 31n)]);
    bits >>= 5n;
  }
  return out.join("").slice(0, 4) + "-" + out.join("").slice(4);
}
function learnerIdPrefixFromSupportId(code) {
  if (!code) return null;
  const cleaned = code.toUpperCase().replace(/[\s-]/g, "").replace(/O/g, "0").replace(/[IL]/g, "1");
  if (cleaned.length !== CODE_LENGTH) return null;
  let bits = 0n;
  for (const ch of cleaned) {
    const value = ALPHABET.indexOf(ch);
    if (value < 0) return null;
    bits = bits << 5n | BigInt(value);
  }
  return bits.toString(16).padStart(HEX_DIGITS, "0");
}
function learnerIdRangeFromSupportId(code) {
  const prefix = learnerIdPrefixFromSupportId(code);
  if (!prefix) return null;
  const build = (fill) => {
    const hex = prefix + fill.repeat(32 - HEX_DIGITS);
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };
  return { low: build("0"), high: build("f") };
}

// src/email/marketingEmail.ts
var OWN_HOSTS = ["saysomethingin.app", "saysomethingin.com"];
var POSTAL_PLACEHOLDER = "Postal address not set yet";
var escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
function isSafeUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "mailto:";
  } catch {
    return false;
  }
}
function isOwnHost(host) {
  const h = host.toLowerCase();
  return OWN_HOSTS.some((own) => h === own || h.endsWith(`.${own}`));
}
function tagLink(url, utm) {
  if (!utm) return url;
  let u;
  try {
    u = new URL(url);
  } catch {
    return url;
  }
  if (u.protocol !== "https:" || !isOwnHost(u.hostname)) return url;
  if (!u.searchParams.has("utm_source")) u.searchParams.set("utm_source", "email");
  if (!u.searchParams.has("utm_medium")) u.searchParams.set("utm_medium", "email");
  u.searchParams.set("utm_campaign", utm.campaign);
  u.searchParams.set("utm_content", utm.content);
  return u.toString();
}
var INLINE = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)|(https:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g;
function parseLine(line) {
  const runs = [];
  let last = 0;
  for (const m of line.matchAll(INLINE)) {
    const at = m.index ?? 0;
    if (at > last) runs.push({ text: line.slice(last, at) });
    if (m[1] !== void 0) runs.push({ text: m[1], bold: true });
    else if (m[2] !== void 0) {
      if (isSafeUrl(m[3])) runs.push({ text: m[2], href: m[3] });
      else runs.push({ text: m[0] });
    } else if (m[4] !== void 0) runs.push({ text: m[4], href: m[4] });
    last = at + m[0].length;
  }
  if (last < line.length) runs.push({ text: line.slice(last) });
  return runs;
}
function parseMarketingBody(body) {
  return body.replace(/\r\n?/g, "\n").split(/\n[ \t]*\n+/).map((p) => p.replace(/^\n+|\n+$/g, "")).filter((p) => p.trim().length > 0).map((p) => p.split("\n").map(parseLine));
}
var FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";
var INK = "#2b2622";
var MUTED = "#6f675f";
var CANVAS = "#e8e3dd";
var ACCENT = "#c23a3a";
function runsHtml(runs, utm) {
  return runs.map((r) => {
    if (r.href) return `<a href="${escapeHtml(tagLink(r.href, utm))}" style="color:${ACCENT};text-decoration:underline;">${escapeHtml(r.text)}</a>`;
    if (r.bold) return `<strong>${escapeHtml(r.text)}</strong>`;
    return escapeHtml(r.text);
  }).join("");
}
function runsText(runs, utm) {
  return runs.map((r) => r.href ? r.text === r.href ? tagLink(r.href, utm) : `${r.text}: ${tagLink(r.href, utm)}` : r.text).join("");
}
function renderMarketingEmail(copy, ctx) {
  const paragraphs = parseMarketingBody(copy.body);
  const postal = ctx.postalAddress.trim() || POSTAL_PLACEHOLDER;
  const buttonUrl = copy.buttonUrl && isSafeUrl(copy.buttonUrl) ? tagLink(copy.buttonUrl, ctx.utm) : null;
  const buttonLabel = copy.buttonLabel?.trim() || null;
  const button = buttonUrl && buttonLabel ? { url: buttonUrl, label: buttonLabel } : null;
  const bodyHtml = paragraphs.map((p) => `<p style="margin:0 0 16px;font-size:17px;line-height:1.55;color:${INK};">${p.map((line) => runsHtml(line, ctx.utm)).join("<br>")}</p>`).join("\n");
  const buttonHtml = button ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 20px;"><tr><td style="border-radius:999px;background:${ACCENT};"><a href="${escapeHtml(button.url)}" style="display:inline-block;padding:14px 28px;font-family:${FONT};font-size:17px;font-weight:600;color:#ffffff;text-decoration:none;border-radius:999px;">${escapeHtml(button.label)}</a></td></tr></table>` : "";
  const html = `<!doctype html>
<html lang="en-GB">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="light">
<title>${escapeHtml(copy.subject)}</title>
</head>
<body style="margin:0;padding:0;background:${CANVAS};">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;">${escapeHtml(copy.preheader)}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${CANVAS};">
<tr><td align="center" style="padding:24px 12px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:560px;">
<tr><td style="padding:0 8px 14px;font-family:Georgia, 'Times New Roman', serif;font-size:20px;color:${INK};">SaySomethingin</td></tr>
<tr><td style="background:#ffffff;border-radius:16px;padding:28px 24px 12px;font-family:${FONT};">
${bodyHtml}
${buttonHtml}
</td></tr>
<tr><td style="padding:18px 8px 0;font-family:${FONT};font-size:13px;line-height:1.5;color:${MUTED};">
<p style="margin:0 0 8px;">Sent by ${escapeHtml(ctx.senderName)}. You are getting this because you learnt with SaySomethingin and said we could email you.</p>
<p style="margin:0 0 8px;"><a href="${escapeHtml(ctx.unsubscribeUrl)}" style="color:${MUTED};text-decoration:underline;">Unsubscribe</a> in one tap, any time.</p>
<p style="margin:0;">${escapeHtml(postal)}</p>
</td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
  const text = [
    ...paragraphs.map((p) => p.map((line) => runsText(line, ctx.utm)).join("\n")),
    ...button ? [`${button.label}: ${button.url}`] : [],
    "--",
    `Sent by ${ctx.senderName}. You are getting this because you learnt with SaySomethingin and said we could email you.`,
    `Unsubscribe in one tap: ${ctx.unsubscribeUrl}`,
    postal
  ].join("\n\n");
  return { subject: copy.subject, html, text };
}

// src/words/buildWordRenames.json
var buildWordRenames_default = {
  "awr=hour": "awr=an hour",
  "baned=cuppa": "paned=cuppa",
  "beth=thing": "peth=thing",
  "bob=every": "pob=every",
  "ca=may have": "ga i=can i have",
  "chacen=cake": "cacen=cake",
  "chaws=cheese": "caws=cheese",
  "dda=good": "da=good",
  "de=tea": "te=tea",
  "fananas=bananas": "bananas=bananas",
  "fawr=big": "mawr=big",
  "flasus=tasty": "blasus=tasty",
  "fwyd=food": "bwyd=food",
  "fwyta=eat": "bwyta=eat",
  "ga i'r=may i have the": "ga i'r=can i have the",
  "ga i=may i have": "ga i=can i have",
  "ga=may have": "ga i=can i have",
  "gacen=cake": "cacen=cake",
  "gael=have": "cael=have",
  "garedig=kind": "caredig=kind",
  "ginio=lunch": "cinio=lunch",
  "gloch=clock": "cloch=clock",
  "gogydd=cook": "cogydd=cook",
  "gwbl=all": "cwbl=all",
  "gwrs=course": "cwrs=course",
  "gyd=all": "i gyd=all",
  "gymrwch=will have": "cymrwch=will have",
  "gynnar=early": "cynnar=early",
  "gyntaf=first": "cyntaf=first",
  "i'r=i the": "i'r=to the",
  "i'w=to its": "i'w=to his/her/its",
  "laeth=milk": "llaeth=milk",
  "thatws=potatoes": "tatws=potatoes",
  "well=better": "gwell=better",
  "wener=friday": "gwener=friday",
  "\xF4l=trace": "\xF4l=behind"
};

// src/words/buildWords.ts
var BUILD_WORD_RENAMES = buildWordRenames_default;
function currentBuildWord(key) {
  return Object.prototype.hasOwnProperty.call(BUILD_WORD_RENAMES, key) ? BUILD_WORD_RENAMES[key] : key;
}
function canonicalBuildExtra(e) {
  if (typeof e !== "string" || !e) return null;
  const [k, pos] = e.split("|");
  const to = currentBuildWord(k);
  return to === k ? e : pos ? `${to}|${pos}` : to;
}

// src/words/topicList.ts
var AREAS = [
  { id: "food", name: "Food and eating", icon: "\u{1F372}" },
  { id: "family", name: "Family and friends", icon: "\u{1F46A}" },
  { id: "home", name: "Home", icon: "\u{1F3E1}" },
  { id: "work", name: "Work", icon: "\u{1F4BC}" },
  { id: "weather", name: "Weather", icon: "\u{1F326}\uFE0F" },
  { id: "travel", name: "Getting about", icon: "\u{1F68C}" },
  { id: "shopping", name: "Shopping", icon: "\u{1F6CD}\uFE0F" },
  { id: "health", name: "Health", icon: "\u{1FA7A}" },
  { id: "sport", name: "Sport", icon: "\u26BD" },
  { id: "music", name: "Music", icon: "\u{1F3B6}" },
  { id: "school", name: "School", icon: "\u{1F3EB}" },
  { id: "hobbies", name: "Hobbies", icon: "\u{1F3A8}" },
  { id: "animals", name: "Animals", icon: "\u{1F411}" },
  { id: "outdoors", name: "The outdoors", icon: "\u26F0\uFE0F" },
  { id: "town", name: "Town and village", icon: "\u{1F3D8}\uFE0F" },
  { id: "time", name: "Time and dates", icon: "\u{1F4C5}" },
  { id: "feelings", name: "Feelings", icon: "\u{1F4AC}" },
  { id: "clothes", name: "Clothes", icon: "\u{1F9E5}" },
  { id: "money", name: "Money", icon: "\u{1F4B7}" },
  { id: "festivals", name: "Festivals", icon: "\u{1F389}" }
];
var BUILT_AREA_IDS = { cy: ["food", "family"] };

export { AREAS, AUDIO_BASE_URL, AudioRecorder, B1_CONVERSATIONAL_HOURS, BUILD_WORD_RENAMES, BUILT_AREA_IDS, CONTOUR_MAX_POINTS, ConfigResolver, CyclePhase, DEFAULT_CALIBRATION_CONFIG, DEFAULT_CONFIG, DEFAULT_CONTINUOUS_ADAPTATION_CONFIG, DEFAULT_CONTINUOUS_VAD_CONFIG, DEFAULT_PAUSE_CONFIG, DEFAULT_PAUSE_K, DEFAULT_PAUSE_REACTION_MS, DEFAULT_RATE_POLICY_BOUNDS, DEFAULT_RATE_POLICY_CONFIG, DEFAULT_SCRIPT_SHAPE, DEFAULT_TIMING_THRESHOLDS, DEFAULT_VAD_CONFIG, ENTRY_CONVERSATIONAL_HOURS, ENVELOPE_EXTRACTOR_CONSTANTS, EVIDENCE_SERIES_RING_CAP, FALLBACK_TARGET_MS, FREE_OFFLINE_WINDOW_DAYS, FREE_OFFLINE_WINDOW_MS, GENERATOR_VERSION, HUMAN_VOICED_LANGUAGES, INDIA_COURSES, INDIA_COURSE_ANNUAL_PAISE, INDIA_COURSE_CODES, INDIA_COURSE_MONTHLY_PAISE, INDIA_COURSE_QUARTERLY_PAISE, INDIA_CURRENCY, INDIA_FAMILY, INDIA_FAMILY_ANNUAL_PAISE, INDIA_FAMILY_MONTHLY_PAISE, INDIA_PLACEHOLDER_PREFIX, INDIA_PREMIUM, INDIA_PREMIUM_ANNUAL_PAISE, INDIA_PREMIUM_LEGACY, INDIA_PREMIUM_MONTHLY_PAISE, INDIA_PRICES, INDIA_STORE_COURSES, LANGUAGE_META, LANGUAGE_NAMES, LANGUAGE_VARIANT_NAMES, LegoMetricsStore, MAX_SPEED, MIN_BUILD_PHRASES_AFTER_CAP, MIN_SPEED, MIN_USE_PHRASES_AFTER_CAP, MasteryStateMachine, MetricsTracker, OFFLINE_TEACHER_ROLES, OFFLINE_UNLOCKED_BY, OWN_HOSTS, PLATFORM_TRIAL_GRACE_MS, POSTAL_PLACEHOLDER, PREMIUM_PREVIEW_MAX_SEED, PodStateStore, ProgressStore, REVIEW_OFFSET_CEILING, ROLE_ALIASES, RatePolicyEngine, ResumeResolutionError, SCHOOL_PUPIL_FREE_MAX_SEED, SEED_REVIEW_START_OFFSET, SEED_SENTENCE_KIND, SEED_SENTENCE_OFFSETS, SEED_SENTENCE_REVIEW_COURSES, STORE_COURSE_BASE_PLANS, SYLLABLE_REGISTRY, SessionStore, SpacedRepetitionQueue, SpeechTimingAnalyzer, SpikeDetector, SyncService, TEXT_VISIBILITY, TRIAL_DAYS_COMMERCIAL, TRIAL_DAYS_HERITAGE, TripleHelixEngine, VoiceActivityDetector, accelerationAlarm, assessLocalDifficulty, assignSeedSentenceHosts, beyondSliceLanding, canAccessCourse, canAccessSeed, canonicalBuildExtra, capPhrasesByLength, checkCourseAccess, compareProsody, competenceBand, computeCurvature, computeLegoCentrality, computePauseDuration, countSyllables, countSyllablesEng, countSyllablesHrv, countTargetSyllables, courseDisplayName, courseMaxPhraseLength, courseOfStoreProduct, courseShortName, createAudioRef, createConfigResolver, createDefaultLegoProgress, createEmptySpeechTimingResult, createEvidenceAggregator, createMasteryStateMachine, createMetricsTracker, createProgressStore, createRatePolicyEngine, createRoundState, createSessionStore, createSpacedRepetitionQueue, createSpeechTimingAnalyzer, createSpikeDetector, createSyncService, createTripleHelixEngine, createVoiceActivityDetector, currentBuildWord, curvatureSeries, dirFor, drawReviewPhrases, effectivePaceRatio, extractEnvelopeMetadata, extractPitchContour, filterReviewPool, formatRupees, freeOfflineWindowEndsAt, generateScript, getAudioUrl, getDebutPhraseCount, getLanguageMeta, getNativePitchContour, getNextRoundItem, hasSyllableCounter, holdsOfflineCapability, indiaPremiumChangeTarget, inferPricingTier, iosStoreCoursePeriod, isBig10Language, isCommercialCourse, isFreeTierCourse, isHumanVoicedCourse, isIndiaCourse, isOfflineTeacherRole, isPlaceholderPriceId, isPlatformActive, isRtlText, isSafeUrl, isUnstampedTrialLapsed, isWithinTrialGrace, languageName, learnerIdPrefixFromSupportId, learnerIdRangeFromSupportId, legosById, makeLatencySeries, makeVowelGroupCounter, mapEnrollmentRow, median, needsRound, offeredIndiaPriceId, orderLegoPools, paceMultiplier, parseMarketingBody, phraseTextLength, phrasesByLego, phrasesByLegoAndRole, playbackSpeedForVoice, rankLocalDifficulty, registerCounter, renderMarketingEmail, reportCursorMove, resolveResumeAnchor, resolveResumeStart, reviewCursorStart, reviewOffsets, seedHasAllClips, seedOfLegoId, seedReviewAnchors, seedSentenceCycleId, seedSentenceHostsByRound, seedSentenceReviewEnabled, seedSentenceReviewsDue, seedsByHostRound, seedsBySeedId, selectDebutPhrase, selectDebutPhrases, selectEternalPhrase, setCursorTelemetrySink, summariseVad, supportIdForLearnerId, syllableLangOf, tagLink, targetLangFromCourseCode, targetPace, tokenizeTarget, trialDaysForCourse, voicePaceForSlot };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map